import { create } from "zustand";
import { readBootAccountId } from "./window-account.js";
import { persist, type PersistStorage, type StorageValue } from "zustand/middleware";
import { createStore as createIDBStore, del as idbDel, get as idbGet, set as idbSet } from "idb-keyval";
import type {
  ChatSearchScope,
  ClientDepositRecord,
  ClientHotLeadRecord,
  ClientDepositStatusId,
  ClientStatusSettings,
  ClientSocketMessage,
  CommandEnvelope,
  DialogListRef,
  DialogSummary,
  GlobalMessageSearchHit,
  InlineKeyboard,
  ReplyKeyboard,
  MessageContent,
  MessageReaction,
  MessageReplyPreview,
  MessageTextEntity,
  PushJobSummary,
  ServerEvent,
  ServerSocketMessage
} from "@tg/shared";
import { hasMarkdownSyntax, parseMarkdown } from "@tg/shared";
import { compareMessageIds, isMessageIdAfter } from "./message-id.js";
import {
  getNotificationEnabled,
  getNotificationPermission,
  setNotificationEnabled,
  showIncomingMessageNotification,
  showSystemNotification
} from "./browser-notifications.js";
import { httpBaseFromWsUrl } from "./auth-api.js";
import { internalApi, type FolderLinesResponse, type FrequentChatEntry } from "./internal-api.js";
import { pushApi } from "./push-api.js";
import { useUiPrefs } from "./ui-prefs.js";

type ConnectionStatus = "disconnected" | "connecting" | "connected";

interface DialogListState {
  isLoading: boolean;
  isLoaded: boolean;
}

/**
 * Отложенное сообщение: живёт отдельно от ленты (TDLib хранит их в своём
 * списке), поэтому и модель отдельная — в ленте таких id нет.
 */
export interface ScheduledMessageViewModel {
  id: string;
  content: MessageContent;
  text: string;
  outgoing: boolean;
  senderName?: string;
  /** Когда Telegram отправит (ISO). Нет — значит «когда адресат будет в сети». */
  sendAt?: string;
  sendWhenOnline?: boolean;
  /** Оптимистичная запись SPA: TDLib ещё не подтвердил планирование. */
  isPending?: boolean;
}

export interface MessageViewModel {
  id: string;
  content: MessageContent;
  text: string;
  senderId: string;
  senderName?: string;
  internalAuthorName?: string;
  replyTo?: MessageReplyPreview;
  isEdited?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  outgoing: boolean;
  occurredAt: string;
  isPending?: boolean;
  localId?: string;
  reactions?: MessageReaction[];
  replyMarkup?: InlineKeyboard;
  messageThreadId?: string;
  // forward_info из TDLib (по-человечески — «Переслано от …»). Берётся
  // из chat.messageUpserted.forwardedFrom; рисуется в шапке пузыря.
  forwardedFrom?: {
    kind: "user" | "chat" | "channel" | "hidden" | "imported";
    name: string;
    authorSignature?: string;
    // Telegram id исходного отправителя. По нему SPA умеет открыть чат
    // или, если резолв не удался, скопировать id в буфер. Для hidden/
    // imported отсутствует.
    senderId?: string;
  };
  // Альбом (медиагруппа) TDLib: подряд идущие сообщения с одним id лента
  // рисует одним пузырём.
  mediaAlbumId?: string;
}

export interface ChatViewModel {
  chatId: string;
  title: string;
  avatarUrl?: string | null;
  lastMessageText: string;
  /** Воркер проверил: в чате действительно нет сообщений. Только при этом
      флаге список диалогов пишет «Нет сообщений» — пустой lastMessageText
      сам по себе означает лишь «превью ещё не доехало». */
  hasNoMessages?: boolean;
  lastMessageSenderName?: string;
  /* Метаданные последнего сообщения — клиентская деривация из live-событий
     (снапшот диалогов этого не отдаёт). Нужны DialogList'у: галочки sent/read
     у исходящих (Outgoing/Id/OccurredAt) и иконка типа медиа (Kind). */
  lastMessageOutgoing?: boolean;
  lastMessageId?: string;
  lastMessageOccurredAt?: string;
  lastMessageKind?: MessageContent["kind"];
  lastActivityAt: string;
  unreadCount: number;
  isMarkedAsUnread?: boolean;
  isForum?: boolean;
  // «Избранное» (Saved Messages): рендерим с закладкой и названием
  // «Избранное», а не как чат с самим собой.
  isSavedMessages?: boolean;
  telegramUserId?: number;
  listIds: string[];
  // TDLib per-list position.order (decimal string). Authoritative chat-list
  // order from Telegram; we sort by this, not by lastActivityAt.
  orderByList?: Record<string, string>;
  internalTags: string[];
  isGroupChat: boolean;
  isPinned?: boolean;
  // Уведомления чата выключены (с учётом дефолта области TDLib). Бейдж
  // непрочитанного у такого чата приглушён.
  isMuted?: boolean;
  // Список list-id, где чат закреплён. Без этого UI в кастомной папке всегда
  // видел один общий isPinned (true если pinned хоть где-то), и кнопка
  // «Открепить» в папке упиралась в команду, которая снимала не тот пин.
  pinnedListIds?: string[];
  peerStatusLabel?: string;
  // ISO-момент «был(а) в сети». Человекочитаемую строку собирает UI в
  // часовом поясе саппорта. `| undefined` явно: при переходе в online мы
  // ЗАТИРАЕМ прошлое значение, иначе рядом с «в сети» осталась бы дата.
  peerLastSeenAt?: string | undefined;
  isPeerOnline?: boolean;
  // «Собеседник заблокировал наш аккаунт». Только для приватных чатов.
  // Воркер выставляет по ошибке отправки USER_IS_BLOCKED* и снимает на
  // любом входящем от этого юзера. UI показывает «Заблокировал» в шапке
  // и красную точку на аватарке вместо online-индикатора.
  isBlockedByUser?: boolean;
  isTyping: boolean;
  typingActorId?: string;
  lastReadInboxMessageId?: string;
  lastReadOutboxMessageId?: string;
  lastReadOutboxAt?: string;
  // Активный таймер автоудаления чата, сек. Отсутствует = выключен.
  autoDeleteTime?: number;
}

export interface DialogListViewModel extends DialogListRef {}

export interface ChatProfileInfo {
  telegramUserId?: number;
  username?: string;
  phoneNumber?: string;
  // ISO-код страны номера («Страна телефона» из оригинального TG).
  phoneNumberCountry?: string;
  // Месяц регистрации аккаунта, "YYYY-MM".
  registrationMonth?: string;
  bio?: string;
  description?: string;
  inviteLink?: string;
  // Таймер автоудаления сообщений чата в секундах (0 = выключен).
  autoDeleteTime?: number;
}

export interface ForumTopicViewModel {
  messageThreadId: string;
  name: string;
  iconEmoji?: string;
  unreadCount: number;
  isClosed?: boolean;
  isPinned?: boolean;
}

export interface BotCallbackToast {
  id: string;
  chatId: string;
  messageId: string;
  text: string;
  url?: string;
  showAlert: boolean;
  occurredAt: string;
}

export interface PendingCall {
  chatId: string;
  // Снимаем title в момент клика — чтобы модалка показывала имя даже
  // если чат успел переключиться на другой за время ringing.
  chatTitle: string;
  startedAt: string;
  // true после chat.callRinging — TDLib подтвердил, что телефон абонента
  // зазвонил. Меняет текст модалки со «Идёт вызов…» на «Гудки идут…».
  ringing?: boolean;
  // undefined = ещё звоним; иначе worker уже прислал chat.callPlaced.
  outcome?: "picked_up" | "discarded" | "timeout" | "failed";
  failureMessage?: string;
  finishedAt?: string;
}

export interface SelfIdentity {
  telegramUserId: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  bio?: string;
  avatarUrl?: string | null;
}

interface ChatSearchState {
  query: string;
  scope: ChatSearchScope;
  isLoading: boolean;
  results: MessageViewModel[];
  // Курсор следующей страницы (next_from_message_id TDLib); нет — всё загружено.
  cursor?: string;
  hasMore: boolean;
}

// Вкладки «Что в чате» в панели профиля живут на отдельном ключе
// searchStateByChat — иначе поиск в чате и медиа-сетка затирали друг друга.
const MEDIA_SEARCH_SUFFIX = ":media";
export const mediaSearchKey = (chatId: string): string => `${chatId}${MEDIA_SEARCH_SUFFIX}`;
const searchStateKeysForChat = (
  searchStateByChat: Record<string, ChatSearchState>,
  chatId: string
): string[] => {
  const keys: string[] = [];
  if (searchStateByChat[chatId]) keys.push(chatId);
  const mediaKey = mediaSearchKey(chatId);
  if (searchStateByChat[mediaKey]) keys.push(mediaKey);
  return keys;
};

interface RealtimeState {
  connectionStatus: ConnectionStatus;
  accountId: string;
  connectionId: string;
  wsUrl: string;
  notificationPermission: NotificationPermission | "unsupported";
  notificationsEnabled: boolean;
  shouldReconnect: boolean;
  heartbeatIntervalId: number | null;
  reconnectTimeoutId: number | null;
  dialogLists: Record<string, DialogListViewModel>;
  dialogListOrder: string[];
  dialogListStateById: Record<string, DialogListState>;
  dialogRequestedLimitById: Record<string, number>;
  dialogHasMoreById: Record<string, boolean>;
  chatIdsByList: Record<string, string[]>;
  activeDialogListId: string;
  selectedChatId: string | null;
  // Оператор реально смотрит на страницу: вкладка видима И окно в фокусе.
  // Открытый (selected) чат считается прочитанным только при pageActive —
  // иначе чат, оставленный открытым в фоновой вкладке/втором окне, молча
  // гасил входящие и саппорт их не замечал. Не персистится.
  pageActive: boolean;
  pendingScrollMessageId: string | null;
  chats: Record<string, ChatViewModel>;
  pinnedMessageByChat: Record<string, MessageViewModel>;
  // Производные суммы непрочитанных: total — бейдж вкладки (без чисто
  // архивных чатов), byList — бейджи табов папок. Пересчитываются одним
  // O(n)-проходом в троттл-подписке (см. scheduleUnreadTotalsRecalc), а не
  // селекторами в компонентах — те выполнялись на КАЖДЫЙ set() стора и при
  // 10k чатов заметно грели main thread.
  // chatsByList — клиентский счёт непрочитанных ЧАТОВ (fallback для local:*
  // папок, у которых нет серверного счётчика).
  // unmutedChatsByList — то же, но только чаты со звуком: бейдж папки
  // приглушается, когда все её непрочитанные чаты без звука (как в TG).
  unreadTotals: {
    total: number;
    byList: Record<string, number>;
    chatsByList: Record<string, number>;
    unmutedChatsByList: Record<string, number>;
  };
  // Серверные счётчики непрочитанного per chat list (chat.listUnreadUpdated,
  // TDLib updateUnreadMessageCount/updateUnreadChatCount). Авторитетны для
  // main/archive/folder:* — клиентская сумма недосчитывает чаты папок,
  // которые клиент не загружал (бейдж папки показывал 0 при реальных
  // непрочитанных). messages — сообщений, chats — непрочитанных чатов
  // (именно chats рисуем на бейджах папок, как оригинальный TG).
  // unmutedChats — то же без чатов со снятым звуком: официальные клиенты
  // рисуют бейдж папки именно по нему, а от total-варианта у нас вылезали
  // счётчики там, где в оригинальном Telegram пусто. Переключается настройкой
  // countMutedInFolderBadges.
  serverUnreadByList: Record<
    string,
    { messages: number; chats?: number; unmutedChats?: number }
  >;
  // Быстрые ответы Telegram Business (вызов через «/» в композере).
  quickReplies: Array<{
    shortcutId: string;
    name: string;
    messageCount: number;
    firstMessageText?: string;
  }>;
  draftsByChat: Record<string, string>;
  // Отложенные сообщения по чатам (TDLib держит их отдельным списком).
  scheduledByChat: Record<string, ScheduledMessageViewModel[]>;
  scheduledLoadingByChat: Record<string, boolean>;
  messagesByChat: Record<string, MessageViewModel[]>;
  searchStateByChat: Record<string, ChatSearchState>;
  historyLoadingByChat: Record<string, boolean>;
  historyLoadedByChat: Record<string, boolean>;
  historyReachedStartByChat: Record<string, boolean>;
  translationsByMessage: Record<string, { text: string; entities?: MessageTextEntity[]; targetLanguage: string }>;
  translatedChats: Record<string, boolean>;
  // Целевой язык авто-перевода per chat. Пусто = дефолт ("ru").
  // Используется и кнопкой «🌐 Перевести весь чат», и глобусом перевода
  // черновика — раньше «целевой язык» жил только в локальном стейте
  // компонента и кнопка хардкодила "ru", из-за чего выбор языка в UI
  // ни на что не влиял.
  translationLanguageByChat: Record<string, string>;
  // Язык, НА который переводится исходящий черновик (кнопка «Перевести» в
  // композере). Отдельный от translationLanguageByChat: тот — язык ЧТЕНИЯ
  // входящих (обычно RU), этот — язык клиента (eg_m, ma_w, ar-shami…).
  // Пусто = вычисляем из привязки аккаунта (см. resolveOutgoingLanguage).
  outgoingLanguageByChat: Record<string, string>;
  // Последний выбранный язык клиента по аккаунту — дефолт для чатов, где
  // саппорт язык ещё не выбирал (иначе выбирать пришлось бы в каждом чате).
  outgoingLanguageByAccount: Record<string, string>;
  profilesByChat: Record<string, ChatProfileInfo>;
  forumTopicsByChat: Record<string, ForumTopicViewModel[]>;
  stickerSets: Array<{
    id: string;
    title: string;
    kind: "installed" | "favorite" | "recent";
    stickers: Array<{
      fileId: string;
      emoji?: string;
      thumbUrl?: string;
      width?: number;
      height?: number;
    }>;
  }>;
  stickerSetsLoading: boolean;
  loadStickerSets(): void;
  activeForumTopicByChat: Record<string, string | null>;
  // Бот прислал клавиатуру под композером (replyMarkupShowKeyboard).
  // null или отсутствие = клавиатуры нет; сбрасывается на removeKeyboard
  // или при oneTime после первого нажатия.
  botReplyKeyboardByChat: Record<string, ReplyKeyboard>;
  botCallbackToasts: BotCallbackToast[];
  // Состояние текущего пропуш-звонка для UI-модалки. Заполняется при
  // вызове placeCall и обновляется приходящим chat.callPlaced.
  pendingCall: PendingCall | null;
  selfIdentity: SelfIdentity | null;
  selfProfileOpen: boolean;
  appRole: "admin" | "geo_admin" | "user" | "support" | null;
  openSelfProfile(): void;
  closeSelfProfile(): void;
  requestSelfIdentity(): void;
  clearBotReplyKeyboard(chatId: string): void;
  setAppRole(role: "admin" | "geo_admin" | "user" | "support" | null): void;
  // userId сайтового пользователя: «свои» пуши в очереди (их можно убрать).
  appUserId: string | null;
  setAppUserId(userId: string | null): void;
  pressInlineButton(chatId: string, messageId: string, data: string): void;
  placeCall(chatId: string): void;
  dismissPendingCall(): void;
  listForumTopics(chatId: string): void;
  setActiveForumTopic(chatId: string, threadId: string | null): void;
  dismissBotCallbackToast(id: string): void;
  errors: AppErrorNotice[];
  dismissError(id: string): void;
  dismissAllErrors(): void;
  socket: WebSocket | null;
  connect(accountId: string, wsUrl?: string): void;
  disconnect(): void;
  setActiveDialogList(listId: string): void;
  selectChat(chatId: string | null): void;
  setPageActive(active: boolean): void;
  requestScrollToMessage(chatId: string, messageId: string): void;
  consumePendingScrollMessage(): void;
  setDraft(chatId: string, text: string): void;
  clearDraft(chatId: string): void;
  // chatId — ВСЕГДА чат, в котором оператор нажал «Отправить» (снят в
  // момент действия), а не selectedChatId на момент вызова: между кликом и
  // вызовом бывает await (загрузка файла), и за это время саппорт успевает
  // открыть другой диалог — сообщение уходило чужому пользователю.
  sendText(chatId: string, text: string): void;
  // options.localId — плейсхолдер уже создан beginOptimisticSend (медиа:
  // пузырь появляется до загрузки файла на сервер); options.whenOnline —
  // отложить «когда адресат появится в сети» (личные чаты).
  sendMessage(
    chatId: string,
    content: MessageContent,
    replyToMessageId?: string,
    scheduledAt?: string,
    trustAssetId?: string,
    options?: { localId?: string; whenOnline?: boolean }
  ): void;
  // Альбом (медиагруппа). Плейсхолдер — через beginOptimisticSend с
  // options.localId (станет первым сообщением альбома по messageAccepted);
  // остальные приедут через chat.messageUpserted.
  sendAlbum(
    chatId: string,
    contents: MessageContent[],
    trustAssetId?: string,
    options?: { localId?: string; scheduledAt?: string; whenOnline?: boolean }
  ): void;
  // Плейсхолдер исходящего медиа ДО загрузки файла: возвращает localId,
  // который затем передаётся в sendMessage/sendAlbum. content.url —
  // локальный objectURL для превью. cancelOptimisticSend — снять при
  // ошибке загрузки.
  beginOptimisticSend(chatId: string, content: MessageContent): string;
  cancelOptimisticSend(chatId: string, localId: string): void;
  // Быстрые ответы Telegram Business: запрос списка и отправка шортката в
  // выбранный чат.
  listQuickReplies(): void;
  sendQuickReply(chatId: string, shortcutId: string): void;
  // options.content — замена медиа (photo/video/document) при правке,
  // text тогда подпись; options.allowEmpty — можно отправить пустой text
  // (снять подпись у медиа).
  editMessage(
    chatId: string,
    messageId: string,
    text: string,
    // scheduled — правка из окна «Отложенные»: запись на полке помечается
    // «сохраняем…» до свежего снимка, а при ошибке полка перечитывается.
    options?: { content?: MessageContent; allowEmpty?: boolean; scheduled?: boolean }
  ): void;
  deleteMessage(chatId: string, messageId: string, revoke?: boolean): void;
  forwardMessage(
    fromChatId: string,
    messageId: string,
    toChatId: string,
    options?: { hideSenderName?: boolean; hideCaption?: boolean }
  ): void;
  togglePinned(chatId: string, pinned: boolean, listId?: string): void;
  movePinned(chatId: string, direction: "up" | "down", listId?: string): void;
  reorderPinned(draggedId: string, insertBeforeId: string | null, listId?: string): void;
  toggleArchived(chatId: string, archived: boolean): void;
  toggleMarkedAsUnread(chatId: string, markedAsUnread: boolean): void;
  // Уведомления чата: выключить/включить (TDLib setChatNotificationSettings).
  setChatMuted(chatId: string, muted: boolean): void;
  setListMembership(chatId: string, listIds: string[]): void;
  requestProfile(chatId: string): void;
  /** Таймер автоудаления сообщений чата, секунды; 0 = выключить. */
  setChatAutoDeleteTime(chatId: string, seconds: number): void;
  toggleMessagePinned(chatId: string, messageId: string, pinned: boolean, onlyForSelf?: boolean): void;
  setChatInternalTags(chatId: string, tags: string[]): void;
  setTyping(chatId: string, active: boolean): void;
  markRead(chatId: string, messageId: string): void;
  listDialogs(limit?: number, listId?: string): void;
  loadMoreDialogs(listId?: string): void;
  loadHistory(chatId: string, limit?: number, messageThreadId?: string | null): void;
  loadOlderHistory(chatId: string, limit?: number): void;
  searchMessages(
    chatId: string,
    query: string,
    scope: ChatSearchScope,
    limit?: number,
    options?: { cursor?: string; requestKey?: string; force?: boolean }
  ): void;
  // clearSearch(chatId) чистит и поиск, и медиа-ключ; clearSearch(key) — один.
  clearSearch(chatId: string): void;
  translateMessage(chatId: string, messageId: string, targetLanguage?: string): void;
  transcribeVoice(chatId: string, messageId: string): void;
  loadScheduledMessages(chatId: string): void;
  sendScheduledNow(chatId: string, messageId: string): void;
  deleteScheduledMessage(chatId: string, messageId: string): void;
  rescheduleMessage(
    chatId: string,
    messageId: string,
    schedule: { sendAt?: string; whenOnline?: boolean }
  ): void;
  clearMessageTranslation(chatId: string, messageId: string): void;
  setChatTranslation(chatId: string, enabled: boolean): void;
  setChatTranslationLanguage(chatId: string, language: string): void;
  // Добор перевода: сбрасывает счётчик неудачных попыток по чату и ставит в
  // очередь всё, что осталось без перевода (кнопка «Добрать» + автоматически
  // при открытии чата с включённым переводом).
  retranslateMissing(chatId: string): void;
  setOutgoingLanguage(chatId: string, language: string): void;
  // Запоминает пару «перевод, который уходит в чат» → «русский оригинал».
  // Когда сообщение вернётся из TDLib, подпись «Перевод (ru)» под ним
  // подставится из этой пары — без обращения к LLM и слово в слово так, как
  // саппорт написал.
  rememberOutgoingOriginal(chatId: string, sentText: string, originalText: string): void;
  // Активная пересылка: «прилипает» к композеру и едет за юзером при смене
  // чата (как в TG desktop). Send в любом чате форвардит источник сюда;
  // hideSenderName/hideCaption — флаги send_copy/remove_caption из TDLib.
  forwardComposition: {
    sourceChatId: string;
    // Первое из пересылаемых (для превью) и полный список в порядке ленты —
    // пересылка выделенных сообщений уходит пачкой.
    sourceMessageId: string;
    sourceMessageIds: string[];
    hideSenderName: boolean;
    hideCaption: boolean;
  } | null;
  openForwardComposition(sourceChatId: string, sourceMessageIds: string | string[]): void;
  closeForwardComposition(): void;
  setForwardHideSenderName(value: boolean): void;
  setForwardHideCaption(value: boolean): void;
  // Все три actions ниже работают только с локальными папками (id вида
  // "local:<folderId>") — TDLib-папки через UI больше не создаются.
  // Существующие TDLib-папки продолжают отображаться (worker присылает их
  // в dialogListsUpdated), но новые создаются только локально.
  createChatFolder(title: string, includedChatIds: string[], chatLimit?: number | null): Promise<void>;
  deleteChatFolder(listId: string): Promise<void>;
  // Состав папки правится ДЕЛЬТОЙ (addChatIds/removeChatIds): сервер
  // применяет её к актуальной строке. Полный includedChatIds оставлен для
  // совместимости, но он затирает чужие правки, если снимок вкладки устарел.
  // Возвращает актуальную запись папки из ответа gateway (null при ошибке).
  updateChatFolder(
    listId: string,
    patch: {
      title?: string;
      includedChatIds?: string[];
      addChatIds?: string[];
      removeChatIds?: string[];
      chatLimit?: number | null;
    }
  ): Promise<LocalFolderEntry | null>;
  // Все локальные папки текущего аккаунта (raw из /api/internal/local-folders).
  // Хранит chatLimit и includedChatIds. SPA автоматически синхронизирует
  // dialogLists/chatIdsByList с этим источником.
  localFolders: Array<{
    folderId: string;
    title: string;
    chatLimit: number | null;
    includedChatIds: string[];
    pinnedChatIds: string[];
    position: number | null;
  }>;
  refreshLocalFolders(): Promise<void>;
  // Перестановка вкладок папок: nextOrder — полный порядок id листов
  // (main / folder:* / local:* / archive) после drag-and-drop.
  reorderDialogLists(nextOrder: string[]): void;
  // Автонабор чатов в папку: результат последнего chatFolder.pickChats.
  // Живёт в сторе (а не в модалке), потому что ответ приходит асинхронным
  // событием chatFolder.chatsPicked, а не ответом на HTTP-запрос.
  folderPick: {
    requestId: string;
    status: "pending" | "done" | "error";
    sourceListId: string;
    requestedLimit: number;
    chatIds: string[];
    scannedCount: number;
    skippedInOtherFoldersCount: number;
    exhausted: boolean;
    message?: string;
  } | null;
  pickFolderChats(input: {
    sourceListId: string;
    limit: number;
    // Уже отмеченные в модалке чаты: они ещё не сохранены в папку, поэтому
    // сервер о них не знает и повторный подбор вернул бы их снова.
    excludeChatIds?: string[];
    // ISO-границы по дате последнего сообщения.
    from?: string;
    to?: string;
    // Окно по времени суток (минуты от полуночи) в поясе саппорта.
    dayFromMinutes?: number;
    dayToMinutes?: number;
    timeZoneOffsetMinutes?: number;
    // Только непрочитанные (по умолчанию true).
    onlyUnread?: boolean;
  }): void;
  clearFolderPick(): void;
  // Пуши ИИ текущего аккаунта по jobId (раунд 46; с 2026-10-09 их может
  // быть несколько: в работе, в очереди и завершённые за эту сессию).
  // Обновляются push.jobUpdated, незавершённые подтягиваются при подключении.
  pushJobs: Record<string, PushJobSummary>;
  upsertPushJob(job: PushJobSummary): void;
  // Экспорт чата текстом (раунд 46): «скопировать / сохранить .txt весь
  // чат или N сообщений». Страницы приходят событием chat.historyExported;
  // стор сам переизлучает команду по cursor, пока не наберёт limit / done.
  chatExport: {
    requestId: string;
    chatId: string;
    status: "pending" | "done" | "error";
    limit: number;
    lines: Array<{
      messageId: string;
      outgoing: boolean;
      senderName?: string;
      text: string;
      occurredAt: string;
    }>;
    message?: string;
  } | null;
  startChatExport(chatId: string, limit: number): void;
  cancelChatExport(): void;
  // Добор чатов в существующую локальную папку: дописывает id к текущему
  // составу с учётом лимита папки. Возвращает, сколько реально добавилось.
  appendChatsToLocalFolder(listId: string, chatIds: string[]): Promise<number>;
  // «Частые группы» каждого аккаунта (accountId → список чатов), источник
  // истины — сервер (/api/internal/frequent-chats), а НЕ localStorage: для
  // саппорта это whitelist адресатов быстрой пересылки. Назначать может
  // только менеджер (ПКМ по чату → «В Частые группы»).
  frequentChatsByAccount: Record<string, FrequentChatEntry[]>;
  refreshFrequentChats(): Promise<void>;
  // Папки по линиям (раунд 46, опционально): пока null — фильтр не
  // применяется (все папки видны). Сервер отдаёт линию текущего юзера,
  // список линий и привязки папок текущего аккаунта.
  folderLines: FolderLinesResponse | null;
  refreshFolderLines(): Promise<void>;
  // Статусы клиентов по депозитам (запрос №22). Карта глобальная (ключ —
  // Telegram user id), не сбрасывается при смене аккаунта. null у настроек =
  // ещё не загружены → бейджи/фильтры не показываем.
  clientDeposits: Record<number, ClientDepositRecord>;
  // «🔥 Горячий» без депозита (запрос №24): кандидаты из ботов (заявка на
  // вывод / баланс). Статус считает shared-резолвер вместе с clientDeposits.
  clientHotLeads: Record<number, ClientHotLeadRecord>;
  clientStatusSettings: ClientStatusSettings | null;
  clientStatusFilter: ClientDepositStatusId | null;
  refreshClientDeposits(): Promise<void>;
  setClientStatusFilter(status: ClientDepositStatusId | null): void;
  setFolderLineBinding(listId: string, lineIds: string[]): Promise<void>;
  setAccountFrequentChat(
    accountId: string,
    chatId: string,
    title: string,
    action: "add" | "remove"
  ): Promise<void>;
  // Языки переводчика и привязки к аккаунтам — с сервера (/api/translator/
  // config). Определяют список в селекторе композера и язык по умолчанию.
  translatorLanguages: Array<{ code: string; label: string }>;
  translatorBindings: Record<string, { languages?: string[]; defaultLanguage?: string }>;
  refreshTranslatorConfig(): Promise<void>;
  // Резолв чатов которых нет в state.chats (например, чат в local-папке
  // не попал в prefetch main). Worker getChat-ит каждый и возвращает
  // chat.dialogsResolved.
  resolveKnownChats(chatIds: string[]): void;
  // Серверный поиск чатов (раунд 47): локальный фильтр списка видит только
  // догруженные чаты, поэтому строка поиска дополнительно спрашивает
  // worker (TDLib searchChats/контакты/@username/Telegram ID). Слайс один
  // на все поля ввода: потребитель сверяет query со своим, чтобы не
  // показать чужие результаты. requestId отсекает устаревшие ответы.
  dialogSearch: {
    query: string;
    requestId: string | null;
    chatIds: string[];
    loading: boolean;
  };
  searchDialogs(query: string): void;
  clearDialogSearch(): void;
  // Общий поиск по сообщениям во всех чатах (запрос №29): worker спрашивает
  // Telegram (TDLib searchMessages), hits — сообщения с chatId; сводки их
  // чатов подмешиваются в chats. cursor/hasMore — постраничная догрузка.
  messageSearch: {
    query: string;
    requestId: string | null;
    hits: GlobalMessageSearchHit[];
    cursor: string | null;
    hasMore: boolean;
    loading: boolean;
    error: string | null;
  };
  searchGlobalMessages(query: string): void;
  loadMoreGlobalMessages(): void;
  clearMessageSearch(): void;
  toggleReaction(chatId: string, messageId: string, emoji: string): void;
  refreshNotificationPermission(): void;
  setNotificationsEnabled(enabled: boolean): void;
  applySocketMessage(message: ServerSocketMessage): void;
}

const DEFAULT_DIALOG_LIST_ID = "main";
const ARCHIVE_DIALOG_LIST_ID = "archive";
// При первом открытии папки тянем только видимую часть; остальное докатываем
// при скролле (loadMoreDialogs срабатывает за 320px до низа списка). До этого
// тут было 1000 — для аккаунтов с парой сотен чатов это значило стянуть всё
// сразу одним JSON-сообщением, UI лагал/«крутился» при логине.
const INITIAL_DIALOG_LIMIT = 50;
const DIALOG_PAGE_STEP = 60;
const DEFAULT_DIALOG_LIST: DialogListViewModel = {
  id: DEFAULT_DIALOG_LIST_ID,
  title: "Общие",
  kind: "main"
};

const EMPTY_DIALOG_SEARCH = {
  query: "",
  requestId: null,
  chatIds: [] as string[],
  loading: false
};

const EMPTY_MESSAGE_SEARCH = {
  query: "",
  requestId: null,
  hits: [] as GlobalMessageSearchHit[],
  cursor: null,
  hasMore: false,
  loading: false,
  error: null
};

const generateId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 12);

const defaultWsUrl = (): string => {
  if (typeof window === "undefined") {
    return "ws://127.0.0.1:8081/ws";
  }

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const host = window.location.host || "127.0.0.1:8081";
  return `${protocol}//${host}/ws`;
};

const draftsStorageKey = (accountId: string): string => `tg-client:drafts:${accountId}`;

const readDraftsByChat = (accountId: string): Record<string, string> => {
  if (typeof window === "undefined" || accountId.trim().length === 0) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(draftsStorageKey(accountId));
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") {
      return {};
    }

    return Object.entries(parsed).reduce<Record<string, string>>((acc, [chatId, text]) => {
      if (typeof text === "string" && text.trim().length > 0) {
        acc[chatId] = text;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const writeDraftsByChat = (
  accountId: string,
  draftsByChat: Record<string, string>
): void => {
  if (typeof window === "undefined" || accountId.trim().length === 0) {
    return;
  }

  try {
    const nextEntries = Object.entries(draftsByChat).filter(
      ([chatId, text]) => chatId.trim().length > 0 && text.trim().length > 0
    );

    if (nextEntries.length === 0) {
      window.localStorage.removeItem(draftsStorageKey(accountId));
      return;
    }

    window.localStorage.setItem(
      draftsStorageKey(accountId),
      JSON.stringify(Object.fromEntries(nextEntries))
    );
  } catch {
    return;
  }
};

// setDraft дёргается на КАЖДЫЙ символ, а writeDraftsByChat — это синхронный
// JSON.stringify всех черновиков + localStorage.setItem прямо в критическом
// пути набора текста (main-thread I/O = рывки при печати). Пишем с хвостовым
// дебаунсом; потеря последних ~400мс при жёсткой перезагрузке не страшна —
// серверный draft-push (scheduleDraftPush) всё равно догоняет TDLib.
const DRAFT_PERSIST_DELAY_MS = 400;
let draftPersistTimer: number | null = null;
let draftPersistPayload: {
  accountId: string;
  draftsByChat: Record<string, string>;
} | null = null;

const cancelScheduledDraftPersist = (): void => {
  if (draftPersistTimer !== null && typeof window !== "undefined") {
    window.clearTimeout(draftPersistTimer);
  }
  draftPersistTimer = null;
  draftPersistPayload = null;
};

const scheduleDraftPersist = (
  accountId: string,
  draftsByChat: Record<string, string>
): void => {
  if (typeof window === "undefined") {
    return;
  }
  draftPersistPayload = { accountId, draftsByChat };
  if (draftPersistTimer !== null) {
    return;
  }
  draftPersistTimer = window.setTimeout(() => {
    draftPersistTimer = null;
    const payload = draftPersistPayload;
    draftPersistPayload = null;
    if (payload) {
      writeDraftsByChat(payload.accountId, payload.draftsByChat);
    }
  }, DRAFT_PERSIST_DELAY_MS);
};

// Момент последней ЛОКАЛЬНОЙ правки состава папок (setListMembership).
// dialogListsUpdated, прилетевший эхом нашей же правки, не должен дёргать
// перезагрузку активной папки: оптимистичный апдейт + dialogUpdated уже всё
// применили, а каждый лишний listDialogs стоит воркеру секунд TDLib-prime —
// при массовом добавлении чатов эта лавина давила саму команду добавления
// (WORKER_COMMAND_TIMEOUT, prime до 39с в прод-логах 2026-07-31).
let lastOwnFolderEditAt = 0;
const OWN_FOLDER_EDIT_WINDOW_MS = 8_000;

// Дебаунс фоновой пересинхронизации активной папки после ВНЕШНИХ правок
// (телефон/десктоп TG): серия updateChatFolders схлопывается в один listDialogs.
let folderResyncTimer: number | null = null;
let folderResyncListId: string | null = null;

// Мягкий рефреш папки/архива при переключении на неё: живые dialogUpdated
// не покрывают чаты, вытесненные из кэша воркера (гейт knownToClient), и
// пропущенные за время reconnect'а события — без периодического снапшота
// папка саппорта «застывала» и новые диалоги в ней не появлялись. Интервал
// и размер страницы щадящие: listDialogs по папке = секунды TDLib-prime на
// большом аккаунте, а частая перечитка на 400 диалогов давила воркер
// (жалоба «папки открываются долго», Caroline 2026-08-20). Новые чаты
// всегда в верхней части списка — страницы в 200 хватает.
const FOLDER_REFRESH_MIN_INTERVAL_MS = 60_000;
const FOLDER_REFRESH_MAX_LIMIT = 200;
const folderLastRefreshAt = new Map<string, number>();

// Чаты, для которых профиль уже автозапрашивался при выборе (страна номера
// в шапке). Один запрос на чат за сессию вкладки.
const profileAutoRequested = new Set<string>();

const DRAFT_PUSH_DELAY_MS = 700;
const draftPushTimers = new Map<string, number>();
// Гонка draft'а: при отправке мы явно чистим draftsByChat[chatId], но TDLib
// может ещё некоторое время шлёшь stale `chat.dialogUpdated` со старым
// draftText (пока server-side clear не докрутится). Помечаем момент очистки,
// чтобы reducer игнорировал такие echo-обновления в течение окна.
// 5 секунд не хватало: под нагрузкой (10 аккаунтов × тысячи чатов) воркер
// throttl'ит dialogUpdated, и stale-эхо приезжало сильно позже — текст
// «воскресал» в поле ввода и в превью диалога как фантомный черновик.
// Поэтому окно длинное И дополнительно помним ТЕКСТ, который мы очистили:
// эхо именно с этим текстом отбрасываем в любой момент, пока не придёт
// подтверждение (пустой draft) или юзер не наберёт новый.
const DRAFT_CLEAR_ECHO_WINDOW_MS = 15_000;
const DRAFT_CLEAR_STAMP_LIMIT = 500;
const draftClearStamps = new Map<string, { at: number; text: string }>();
const stampDraftCleared = (chatId: string, text: string): void => {
  if (draftClearStamps.size >= DRAFT_CLEAR_STAMP_LIMIT) {
    const oldestKey = draftClearStamps.keys().next().value;
    if (oldestKey !== undefined) {
      draftClearStamps.delete(oldestKey);
    }
  }
  draftClearStamps.set(chatId, { at: Date.now(), text });
};
// Сервер прислал пустой draft — значит нашу очистку он принял, защиту снимаем.
const forgetDraftClearStamp = (chatId: string): void => {
  draftClearStamps.delete(chatId);
};
const isDraftEchoBlocked = (chatId: string, incomingDraft: string): boolean => {
  const stamp = draftClearStamps.get(chatId);
  if (stamp === undefined) {
    return false;
  }
  // Эхо РОВНО с очищенным текстом отбрасываем без ограничения по времени:
  // сервер уже получил от нас пустой draft, значит это устаревший апдейт,
  // сколько бы он ни ехал (воркер throttl'ит dialogUpdated под нагрузкой).
  if (stamp.text.length > 0 && incomingDraft === stamp.text) {
    return true;
  }
  // Прочие непустые draft'ы гасим только короткое окно — иначе черновик,
  // реально набранный на телефоне, не доехал бы до саппорта.
  return Date.now() - stamp.at <= DRAFT_CLEAR_ECHO_WINDOW_MS;
};

const sendDraftToServer = (chatId: string, text: string, snapshot: RealtimeState): void => {
  if (!snapshot.accountId || !snapshot.socket) {
    return;
  }
  const parsed = hasMarkdownSyntax(text)
    ? parseMarkdown(text)
    : { text, entities: [] };
  socketSend(snapshot.socket, {
    type: "command",
    payload: buildCommandEnvelope(snapshot.accountId, snapshot.connectionId, {
      type: "chat.setDraft",
      chatId,
      text: parsed.text,
      ...(parsed.entities.length > 0 ? { entities: parsed.entities } : {})
    })
  });
};

const scheduleDraftPush = (
  chatId: string,
  text: string,
  getState: () => RealtimeState
): void => {
  if (typeof window === "undefined") {
    return;
  }
  const previous = draftPushTimers.get(chatId);
  if (previous !== undefined) {
    window.clearTimeout(previous);
  }
  const timerId = window.setTimeout(() => {
    draftPushTimers.delete(chatId);
    sendDraftToServer(chatId, text, getState());
  }, DRAFT_PUSH_DELAY_MS);
  draftPushTimers.set(chatId, timerId);
};

// Сторожевой таймер для chat.loadHistory: если worker не ответил за
// HISTORY_LOAD_TIMEOUT_MS — снимаем флаг loading, чтобы пользователь мог
// повторить попытку. Без этого зависший запрос блокировал любые
// последующие открытия чата (loadHistory ранний return по флагу).
// 2026-10-01: 20с не хватало для больших групп с холодным кэшем — Cash и
// подобные показывали «Сообщений пока нет» до фактического ответа TDLib
// (30-60с при 10k+ сообщений + резолвы sender'ов). Поднимаем до 60с;
// параллельно chat-view.tsx показывает пусто-стейт только по флагу
// historyLoadedByChat, а не по факту сброса loading watchdog'ом.
const HISTORY_LOAD_TIMEOUT_MS = 60_000;
const historyLoadingWatchdogs = new Map<string, number>();

const scheduleHistoryLoadingWatchdog = (
  getState: () => RealtimeState,
  chatId: string
): void => {
  if (typeof window === "undefined") {
    return;
  }
  const previous = historyLoadingWatchdogs.get(chatId);
  if (previous !== undefined) {
    window.clearTimeout(previous);
  }
  const timerId = window.setTimeout(() => {
    historyLoadingWatchdogs.delete(chatId);
    const state = getState();
    if (!state.historyLoadingByChat[chatId]) {
      return;
    }
    useRealtimeStore.setState((current) => ({
      historyLoadingByChat: {
        ...current.historyLoadingByChat,
        [chatId]: false
      }
    }));
  }, HISTORY_LOAD_TIMEOUT_MS);
  historyLoadingWatchdogs.set(chatId, timerId);
};

// Тот же приём для панели отложенных: если worker не ответил, «Загружаем…»
// не должно висеть вечно — снимаем флаг и даём нажать «Обновить».
const SCHEDULED_LOAD_TIMEOUT_MS = 20_000;
const scheduledLoadingWatchdogs = new Map<string, number>();

const scheduleScheduledLoadingWatchdog = (chatId: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  const previous = scheduledLoadingWatchdogs.get(chatId);
  if (previous !== undefined) {
    window.clearTimeout(previous);
  }
  const timerId = window.setTimeout(() => {
    scheduledLoadingWatchdogs.delete(chatId);
    useRealtimeStore.setState((current) =>
      current.scheduledLoadingByChat[chatId]
        ? {
            scheduledLoadingByChat: {
              ...current.scheduledLoadingByChat,
              [chatId]: false
            }
          }
        : current
    );
  }, SCHEDULED_LOAD_TIMEOUT_MS);
  scheduledLoadingWatchdogs.set(chatId, timerId);
};

const cancelScheduledLoadingWatchdog = (chatId: string): void => {
  const timerId = scheduledLoadingWatchdogs.get(chatId);
  if (timerId === undefined) {
    return;
  }
  scheduledLoadingWatchdogs.delete(chatId);
  if (typeof window !== "undefined") {
    window.clearTimeout(timerId);
  }
};

const cancelHistoryLoadingWatchdog = (chatId: string): void => {
  const timerId = historyLoadingWatchdogs.get(chatId);
  if (timerId === undefined) {
    return;
  }
  historyLoadingWatchdogs.delete(chatId);
  if (typeof window !== "undefined") {
    window.clearTimeout(timerId);
  }
};

// Аналогичный watchdog для chat.searchMessages — медиа-таб в профиле
// иначе крутит «Загружаем…» бесконечно, если worker не отвечает.
// Экспорт чата: одна страница = одна команда воркера (бюджет ~8 с),
// продолжение — по cursor из ответа. Таймаут страховочный: воркер молчит
// (перезапуск/сокет) — снимаем спиннер, иначе модалка висит навсегда.
const CHAT_EXPORT_PAGE_TIMEOUT_MS = 45_000;
const sendChatExportPage = (
  getState: () => RealtimeState,
  requestId: string,
  cursor: string | undefined
): void => {
  const state = getState();
  const current = state.chatExport;
  if (!current || current.requestId !== requestId || !state.accountId || !state.socket) {
    return;
  }
  const remaining = Math.max(1, current.limit - current.lines.length);
  socketSend(state.socket, {
    type: "command",
    payload: buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.exportHistory",
      requestId,
      chatId: current.chatId,
      ...(cursor ? { cursor } : {}),
      limit: remaining
    })
  });
  if (typeof window !== "undefined") {
    const linesBefore = current.lines.length;
    window.setTimeout(() => {
      const latest = getState().chatExport;
      if (
        latest?.requestId === requestId &&
        latest.status === "pending" &&
        latest.lines.length === linesBefore
      ) {
        useRealtimeStore.setState({
          chatExport: {
            ...latest,
            status: "error",
            message: "Сервер не ответил вовремя. Попробуйте ещё раз."
          }
        });
      }
    }, CHAT_EXPORT_PAGE_TIMEOUT_MS);
  }
};

const SEARCH_LOAD_TIMEOUT_MS = 20_000;
const searchLoadingWatchdogs = new Map<string, number>();

const scheduleSearchLoadingWatchdog = (
  getState: () => RealtimeState,
  chatId: string
): void => {
  if (typeof window === "undefined") {
    return;
  }
  const previous = searchLoadingWatchdogs.get(chatId);
  if (previous !== undefined) {
    window.clearTimeout(previous);
  }
  const timerId = window.setTimeout(() => {
    searchLoadingWatchdogs.delete(chatId);
    const state = getState();
    const searchState = state.searchStateByChat[chatId];
    if (!searchState?.isLoading) {
      return;
    }
    useRealtimeStore.setState((current) => {
      const existing = current.searchStateByChat[chatId];
      if (!existing?.isLoading) {
        return current;
      }
      return {
        searchStateByChat: {
          ...current.searchStateByChat,
          [chatId]: { ...existing, isLoading: false }
        }
      };
    });
  }, SEARCH_LOAD_TIMEOUT_MS);
  searchLoadingWatchdogs.set(chatId, timerId);
};

const cancelSearchLoadingWatchdog = (chatId: string): void => {
  const timerId = searchLoadingWatchdogs.get(chatId);
  if (timerId === undefined) {
    return;
  }
  searchLoadingWatchdogs.delete(chatId);
  if (typeof window !== "undefined") {
    window.clearTimeout(timerId);
  }
};

// Watchdog для chat.listDialogs. Если ответ не дошёл (таймаут worker'а,
// обрыв ws) — спиннер «Подгружаем…» в списке диалогов крутился бы вечно.
// 12s — нормальный потолок: worker primeDialogList даёт себе ≤2.5s + ещё
// нужно время на resolveDialogsByIds; если уложиться не успели — лучше
// разморозить UI и показать «Это все чаты» (юзер всегда может скроллнуть
// и дёрнуть loadMore ещё раз).
const DIALOG_LIST_LOAD_TIMEOUT_MS = 12_000;
const dialogListLoadingWatchdogs = new Map<string, number>();

// commandId отправки → чат и localId её оптимистичного плейсхолдера. Если
// gateway ответил на команду ошибкой (таймаут воркера, отказ), плейсхолдер
// снимаем — иначе «отправляется…» висело до перезагрузки вкладки (жалоба
// 2026-09-23: воркер шарда падал по OOM, отправка «зависала»).
const pendingSendByCommandId = new Map<string, { chatId: string; localId: string }>();
const PENDING_SEND_MAP_LIMIT = 64;
const rememberPendingSend = (commandId: string, chatId: string, localId: string): void => {
  if (pendingSendByCommandId.size >= PENDING_SEND_MAP_LIMIT) {
    const oldest = pendingSendByCommandId.keys().next().value;
    if (oldest !== undefined) {
      pendingSendByCommandId.delete(oldest);
    }
  }
  pendingSendByCommandId.set(commandId, { chatId, localId });
};
// Мутации полки отложенных (перенос/отправить сейчас/удалить): при ошибке
// команды оптимистичное изменение откатываем перезагрузкой полки.
const pendingScheduledMutationByCommandId = new Map<string, string>();
const rememberScheduledMutation = (commandId: string, chatId: string): void => {
  if (pendingScheduledMutationByCommandId.size >= PENDING_SEND_MAP_LIMIT) {
    const oldest = pendingScheduledMutationByCommandId.keys().next().value;
    if (oldest !== undefined) {
      pendingScheduledMutationByCommandId.delete(oldest);
    }
  }
  pendingScheduledMutationByCommandId.set(commandId, chatId);
};

const forgetPendingSendByLocalId = (localId: string | undefined): void => {
  if (!localId) {
    return;
  }
  for (const [commandId, entry] of pendingSendByCommandId) {
    if (entry.localId === localId) {
      pendingSendByCommandId.delete(commandId);
      resolveTextSendCommand(commandId);
    }
  }
};

// Повтор текстовой отправки. Тот же commandId (→ дедупер воркера не даст
// дубля в TG) берём ТОЛЬКО пока исход прошлой такой же отправки неизвестен:
// она ещё в полёте или упала по таймауту. Раньше id был детерминирован по
// тексту всегда — второе «ок» в тот же чат за 2 минуты падало ошибкой
// COMMAND_ALREADY_PROCESSED (жалоба 2026-10-09).
const TEXT_SEND_RETRY_WINDOW_MS = 120_000;
const unresolvedTextSendByKey = new Map<string, { commandId: string; at: number }>();
const textSendCommandId = (key: string, freshCommandId: string): string => {
  const previous = unresolvedTextSendByKey.get(key);
  if (previous && Date.now() - previous.at < TEXT_SEND_RETRY_WINDOW_MS) {
    return previous.commandId;
  }
  if (unresolvedTextSendByKey.size >= PENDING_SEND_MAP_LIMIT) {
    const oldest = unresolvedTextSendByKey.keys().next().value;
    if (oldest !== undefined) {
      unresolvedTextSendByKey.delete(oldest);
    }
  }
  unresolvedTextSendByKey.set(key, { commandId: freshCommandId, at: Date.now() });
  return freshCommandId;
};
const resolveTextSendCommand = (commandId: string): void => {
  for (const [key, entry] of unresolvedTextSendByKey) {
    if (entry.commandId === commandId) {
      unresolvedTextSendByKey.delete(key);
    }
  }
};

const scheduleDialogListLoadingWatchdog = (
  getState: () => RealtimeState,
  listId: string
): void => {
  if (typeof window === "undefined") {
    return;
  }
  const previous = dialogListLoadingWatchdogs.get(listId);
  if (previous !== undefined) {
    window.clearTimeout(previous);
  }
  const timerId = window.setTimeout(() => {
    dialogListLoadingWatchdogs.delete(listId);
    const state = getState();
    const dialogState = state.dialogListStateById[listId];
    if (!dialogState?.isLoading) {
      return;
    }
    useRealtimeStore.setState((current) => {
      const existing = current.dialogListStateById[listId];
      if (!existing?.isLoading) {
        return current;
      }
      return {
        dialogListStateById: {
          ...current.dialogListStateById,
          [listId]: { ...existing, isLoading: false }
        }
      };
    });
  }, DIALOG_LIST_LOAD_TIMEOUT_MS);
  dialogListLoadingWatchdogs.set(listId, timerId);
};

const cancelDialogListLoadingWatchdog = (listId: string): void => {
  const timerId = dialogListLoadingWatchdogs.get(listId);
  if (timerId === undefined) {
    return;
  }
  dialogListLoadingWatchdogs.delete(listId);
  if (typeof window !== "undefined") {
    window.clearTimeout(timerId);
  }
};

const flushDraftPush = (
  chatId: string,
  text: string,
  getState: () => RealtimeState
): void => {
  const previous = draftPushTimers.get(chatId);
  if (previous !== undefined) {
    if (typeof window !== "undefined") {
      window.clearTimeout(previous);
    }
    draftPushTimers.delete(chatId);
  }
  sendDraftToServer(chatId, text, getState());
};

// Лейблы медиа без подписи. Единственный источник: по ним же превью диалога
// восстанавливает тип медиа для чатов из снапшота (kindForPreviewLabel).
const MEDIA_PREVIEW_LABELS = {
  photo: "Фотография",
  video: "Видео",
  document: "Файл",
  voice: "Голосовое сообщение"
} as const;

const LABEL_TO_KIND = new Map<string, MessageContent["kind"]>(
  (Object.entries(MEDIA_PREVIEW_LABELS) as Array<[MessageContent["kind"], string]>).map(
    ([kind, label]) => [label, kind]
  )
);

/** Тип медиа по точному лейблу превью (для диалогов, где kind неизвестен). */
export const kindForPreviewLabel = (text: string): MessageContent["kind"] | undefined =>
  LABEL_TO_KIND.get(text);

export const contentToText = (content: MessageContent): string => {
  switch (content.kind) {
    case "text":
      return content.text ?? "";
    case "photo":
      return content.caption ?? MEDIA_PREVIEW_LABELS.photo;
    case "video":
      return content.caption ?? MEDIA_PREVIEW_LABELS.video;
    case "document":
      return content.caption ?? content.fileName ?? MEDIA_PREVIEW_LABELS.document;
    case "voice":
      return MEDIA_PREVIEW_LABELS.voice;
    case "sticker":
      return content.emoji ? `${content.emoji} Стикер` : "Стикер";
    default:
      // Бэкенд может прислать kind, которого ещё нет в локальном протоколе
      // (анимация и т.п.) — не даём undefined утечь в превью.
      return "Сообщение";
  }
};

/**
 * Текст, который имеет смысл переводить. Это НЕ то же самое, что
 * `message.text`: для медиа там лежит лейбл превью («Голосовое сообщение»),
 * и раньше перевод голосового упирался именно в это — переводить было нечего,
 * а расшифровка (transcript) игнорировалась.
 */
export const translationSourceText = (content: MessageContent): string => {
  switch (content.kind) {
    case "text":
      return content.text?.trim() ?? "";
    case "voice":
    case "videoNote":
      // Расшифровка приезжает асинхронно после «Распознать»; до этого
      // переводить нечего (лейбл «Голосовое сообщение» — не текст).
      return content.transcript?.trim() ?? "";
    case "photo":
    case "video":
    case "document":
      return content.caption?.trim() ?? "";
    default:
      return "";
  }
};

const messagePreviewText = (message: MessageViewModel | undefined): string =>
  message?.isDeleted ? "Сообщение удалено" : message?.text ?? "";

// Поля «последнего сообщения» для строки диалога (галочки/иконка медиа).
const lastMessageMeta = (
  message: MessageViewModel | undefined
): Pick<
  ChatViewModel,
  "lastMessageOutgoing" | "lastMessageId" | "lastMessageOccurredAt" | "lastMessageKind"
> =>
  message
    ? {
        lastMessageOutgoing: message.outgoing,
        lastMessageId: message.id,
        lastMessageOccurredAt: message.occurredAt,
        lastMessageKind: message.content.kind
      }
    : {};

// Одна плашка ошибки в шапке. Раньше это была просто строка, а список
// рендерился по индексу — из-за этого повторяющиеся ошибки давали одинаковые
// React-ключи (плашка «дёргалась» вместо закрытия), а срез последних трёх
// подставлял на место закрытой следующую скрытую ошибку, и выглядело это как
// «крестик не работает». Теперь у каждой плашки стабильный id, а повторы
// схлопываются в счётчик.
export interface AppErrorNotice {
  id: string;
  text: string;
  /** Технические детали (код, requestId) — мелким шрифтом под текстом. */
  detail?: string;
  count: number;
  occurredAt: string;
}

let errorSeq = 0;

/** Хелпер для set(): errors: pushError(current.errors, "текст"). */
const pushError = (
  current: AppErrorNotice[],
  text: string,
  detail?: string
): AppErrorNotice[] => {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return current;
  }
  const existingIndex = current.findIndex(
    (entry) => entry.text === trimmed && entry.detail === detail
  );
  const occurredAt = new Date().toISOString();
  if (existingIndex >= 0) {
    const existing = current[existingIndex]!;
    const next = current.slice();
    next[existingIndex] = { ...existing, count: existing.count + 1, occurredAt };
    return next;
  }
  errorSeq += 1;
  const notice: AppErrorNotice = {
    id: `err_${errorSeq}`,
    text: trimmed,
    ...(detail ? { detail } : {}),
    count: 1,
    occurredAt
  };
  // Больше 20 плашек копить незачем — старые всё равно неактуальны.
  return [...current, notice].slice(-20);
};

// Технические коды/сообщения TDLib и транспорта → человеческий текст.
// Правило: пользователь должен понять, что произошло и что делать; сырой
// код уходит в detail (мелкая строка), а не в основной текст.
const RAW_ERROR_RULES: Array<{ match: RegExp; text: string }> = [
  {
    match: /worker response timeout|response timeout for req_/i,
    text: "Сервер не ответил вовремя. Проверьте соединение и повторите действие через несколько секунд."
  },
  {
    match: /maximum number of pinned chats exceeded/i,
    text: "Telegram не позволяет закрепить больше чатов. Сначала открепите один из уже закреплённых."
  },
  {
    match: /flood_wait_(\d+)/i,
    text: "Telegram временно ограничил частоту запросов. Подождите немного и повторите."
  },
  {
    match: /message_id_invalid|message not found|MESSAGE_ID_INVALID/i,
    text: "Сообщение больше недоступно — возможно, оно удалено."
  },
  {
    match: /chat not found|CHAT_ID_INVALID|PEER_ID_INVALID/i,
    text: "Чат недоступен. Обновите список чатов и попробуйте снова."
  },
  {
    match: /message is not modified|MESSAGE_NOT_MODIFIED/i,
    text: "Текст не изменился — редактировать нечего."
  },
  {
    match: /USER_PRIVACY_RESTRICTED|USER_IS_BLOCKED|user restricted/i,
    text: "Пользователь ограничил приём сообщений."
  },
  {
    match: /CHAT_WRITE_FORBIDDEN|not enough rights|have no write access/i,
    text: "Нет прав на отправку сообщений в этот чат."
  },
  {
    match: /premium/i,
    text: "Функция доступна только аккаунтам с Telegram Premium."
  },
  {
    match: /session (is )?(closed|expired)|Unauthorized|authorization/i,
    text: "Сессия аккаунта неактивна. Переподключите аккаунт в настройках."
  },
  {
    match: /network|ECONNREFUSED|fetch failed|socket hang up/i,
    text: "Нет связи с сервером. Проверьте интернет-соединение."
  }
];

/**
 * Приводит серверную ошибку к паре «понятный текст» + «технические детали».
 */
const describeRealtimeError = (
  code: string,
  message: string
): { text: string; detail?: string } => {
  const detail = [code, message].filter((part) => part && part.length > 0).join(": ");

  if (code === "PIN_LIMIT_REACHED") {
    return {
      text: "Telegram не позволяет закрепить больше чатов. Сначала открепите один из уже закреплённых."
    };
  }

  if (code === "WORKER_COMMAND_TIMEOUT") {
    return {
      text: "Сервер не ответил вовремя. Проверьте соединение и повторите действие через несколько секунд.",
      detail
    };
  }

  if (code === "SUPPORT_CHAT_LIMIT") {
    return { text: message || "Дневной лимит новых клиентов исчерпан." };
  }

  if (code === "SUPPORT_BURST_BLOCKED") {
    return { text: message || "Подозрение на парсинг базы. Аккаунт заблокирован." };
  }

  if (code === "ACCESS_DENIED") {
    return { text: "Недостаточно прав для этого действия.", detail };
  }

  for (const rule of RAW_ERROR_RULES) {
    if (rule.match.test(message) || rule.match.test(code)) {
      return { text: rule.text, detail };
    }
  }

  // Кириллица в сообщении = его уже писали для пользователя (worker/gateway
  // переупаковывают часть ошибок TDLib в человеческий текст) — показываем как
  // есть, без «технической» приписки.
  if (/[а-яё]/i.test(message)) {
    return { text: message };
  }

  // Неизвестная ошибка: не вываливаем сырой код в основной текст —
  // он всё равно ничего не объясняет. Даём общий понятный текст и
  // оставляем техническую строку для скриншота в поддержку.
  return {
    text: "Не удалось выполнить действие. Попробуйте ещё раз.",
    detail
  };
};

const pushRealtimeError = (
  current: AppErrorNotice[],
  code: string,
  message: string
): AppErrorNotice[] => {
  const described = describeRealtimeError(code, message);
  return pushError(current, described.text, described.detail);
};

const buildCommandEnvelope = (
  accountId: string,
  connectionId: string,
  command: CommandEnvelope["command"]
): CommandEnvelope => ({
  commandId: generateId() as CommandEnvelope["commandId"],
  accountId: accountId as CommandEnvelope["accountId"],
  connectionId: connectionId as CommandEnvelope["connectionId"],
  issuedAt: new Date().toISOString(),
  command
});

// Команды перестановки пинов, ответ на которые ещё не пришёл: по commandId
// находим список, чтобы при ошибке перечитать его с воркера — иначе
// оптимистичный порядок висит в UI, пока сам список не перезагрузится.
const pendingPinnedOrderByCommandId = new Map<string, string>();
const rememberPinnedOrderCommand = (commandId: string, listId: string): void => {
  pendingPinnedOrderByCommandId.set(commandId, listId);
  if (pendingPinnedOrderByCommandId.size > 50) {
    const oldest = pendingPinnedOrderByCommandId.keys().next().value;
    if (oldest !== undefined) {
      pendingPinnedOrderByCommandId.delete(oldest);
    }
  }
};

const socketSend = (socket: WebSocket | null, message: ClientSocketMessage): void => {
  if (!socket || socket.readyState !== WebSocket.OPEN) {
    return;
  }
  socket.send(JSON.stringify(message));
};

// Outbox отправки: WS может быть в CONNECTING/CLOSED в момент клика «Отправить»
// (реконнект, серверный slow-consumer close, кратковременная потеря сети).
// Раньше socketSend в этих состояниях молча возвращался и envelope пропадал —
// саппорт видел «отправляется…», а команда никуда не уходила. Держим очередь
// таких конвертов в памяти, шлём при onopen (после session.attach). Старые
// (>5 мин) выкидываем — если WS столько не пришёл, дело не в отправке.
type OutboxEntry = { envelope: CommandEnvelope; enqueuedAt: number };
const sendOutbox: OutboxEntry[] = [];
const OUTBOX_MAX_AGE_MS = 5 * 60_000;

const enqueueCommand = (socket: WebSocket | null, envelope: CommandEnvelope): void => {
  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ type: "command", payload: envelope }));
    return;
  }
  sendOutbox.push({ envelope, enqueuedAt: Date.now() });
};

const flushSendOutbox = (socket: WebSocket | null): void => {
  if (!socket || socket.readyState !== WebSocket.OPEN || sendOutbox.length === 0) {
    return;
  }
  const now = Date.now();
  const drained = sendOutbox.splice(0, sendOutbox.length);
  for (const entry of drained) {
    if (now - entry.enqueuedAt > OUTBOX_MAX_AGE_MS) {
      continue;
    }
    socket.send(JSON.stringify({ type: "command", payload: entry.envelope }));
  }
};

// Стабильный commandId для текстовых отправок: тот же ввод (аккаунт+чат+
// текст+reply+scheduledAt) даёт тот же id. При повторном клике саппорта после
// таймаута CommandDeduplicator (TTL 120с, packages/config/src/env.ts) режет
// дубль — воркер не отправит второе сообщение в TG. Медиа мы не хэшируем
// (payload с File/URL меняется), для них защиту даёт outbox + не-удаление
// пузыря по таймауту (case "error", WORKER_COMMAND_TIMEOUT ниже).
const stableCommandId = (parts: readonly string[]): string => {
  const src = parts.join(" ");
  let h1 = 0x811c9dc5 >>> 0;
  let h2 = 0xdeadbeef >>> 0;
  for (let i = 0; i < src.length; i += 1) {
    const c = src.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 ^ (c + i), 2246822519) >>> 0;
  }
  const hex = (n: number) => n.toString(16).padStart(8, "0");
  const a = hex(h1);
  const b = hex(h2);
  return `${a}-${b.slice(0, 4)}-${b.slice(4, 8)}-${a.slice(0, 4)}-${b}${a.slice(4, 8)}`;
};

const emptyChat = (chatId: string): ChatViewModel => ({
  chatId,
  title: "Диалог",
  lastMessageText: "",
  listIds: [],
  internalTags: [],
  lastActivityAt: new Date(0).toISOString(),
  unreadCount: 0,
  isGroupChat: false,
  isTyping: false
});

const isUsableAvatarUrl = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && !value.startsWith("data:");

const mergeDialogSummary = (
  existing: ChatViewModel | undefined,
  dialog: DialogSummary
): ChatViewModel => {
  const incomingAvatarUrl = isUsableAvatarUrl(dialog.avatarUrl) ? dialog.avatarUrl : null;
  const existingAvatarUrl = isUsableAvatarUrl(existing?.avatarUrl)
    ? existing!.avatarUrl ?? null
    : null;
  const resolvedAvatarUrl = incomingAvatarUrl ?? existingAvatarUrl;

  // Снапшот не отдаёт метаданные последнего сообщения; наша клиентская
  // деривация валидна, только если активность чата не изменилась.
  const sourceChat = existing ?? emptyChat(dialog.chatId);
  const staleMeta = existing && existing.lastActivityAt !== dialog.lastActivityAt;
  const {
    lastMessageOutgoing: _lastMessageOutgoing,
    lastMessageId: _lastMessageId,
    lastMessageKind: _lastMessageKind,
    ...sourceChatSansMeta
  } = sourceChat;

  const base = {
    ...(staleMeta ? sourceChatSansMeta : sourceChat),
    chatId: dialog.chatId,
    title: dialog.title,
    avatarUrl: resolvedAvatarUrl,
    // Пустой lastMessageText в снапшоте ≠ «в чате пусто»: воркер отдаёт "",
    // пока TDLib не подтянул chat.last_message (типично после сборки папки).
    // Раньше такой снапшот ЗАТИРАЛ уже показанное превью, и строка списка
    // сваливалась в «Нет сообщений». Затираем только когда воркер подтвердил
    // пустоту чата (hasNoMessages).
    lastMessageText:
      dialog.lastMessageText && dialog.lastMessageText.length > 0
        ? dialog.lastMessageText
        : dialog.hasNoMessages
          ? ""
          : existing?.lastMessageText ?? "",
    hasNoMessages: dialog.hasNoMessages === true,
    ...(dialog.lastMessageSenderName
      ? { lastMessageSenderName: dialog.lastMessageSenderName }
      : {}),
    // Серверный summary полный, поэтому если поле пришло — берём его,
    // иначе оставляем то, что было в existing (без сброса в undefined,
    // т.к. exactOptionalPropertyTypes запрещает явный undefined).
    ...(dialog.lastMessageOutgoing
      ? { lastMessageOutgoing: true }
      : {}),
    ...(dialog.lastMessageId
      ? { lastMessageId: dialog.lastMessageId }
      : {}),
    ...(dialog.lastMessageOccurredAt
      ? { lastMessageOccurredAt: dialog.lastMessageOccurredAt }
      : {}),
    lastActivityAt: dialog.lastActivityAt,
    unreadCount: dialog.unreadCount,
    ...(dialog.isMarkedAsUnread !== undefined
      ? { isMarkedAsUnread: dialog.isMarkedAsUnread }
      : existing?.isMarkedAsUnread !== undefined
        ? { isMarkedAsUnread: existing.isMarkedAsUnread }
        : {}),
    ...(dialog.isForum !== undefined
      ? { isForum: dialog.isForum }
      : existing?.isForum !== undefined
        ? { isForum: existing.isForum }
        : {}),
    ...(dialog.telegramUserId !== undefined
      ? { telegramUserId: dialog.telegramUserId }
      : existing?.telegramUserId !== undefined
        ? { telegramUserId: existing.telegramUserId }
        : {}),
    ...(dialog.isSavedMessages !== undefined
      ? { isSavedMessages: dialog.isSavedMessages }
      : existing?.isSavedMessages !== undefined
        ? { isSavedMessages: existing.isSavedMessages }
        : {}),
    listIds: dialog.lists.map((list) => list.id),
    ...(dialog.orderByList
      ? { orderByList: dialog.orderByList }
      : existing?.orderByList
        ? { orderByList: existing.orderByList }
        : {}),
    internalTags: dialog.internalTags ?? existing?.internalTags ?? [],
    isGroupChat: dialog.isGroupChat,
    ...(dialog.isPinned !== undefined ? { isPinned: dialog.isPinned } : {}),
    ...(dialog.isMuted !== undefined
      ? { isMuted: dialog.isMuted }
      : existing?.isMuted !== undefined
        ? { isMuted: existing.isMuted }
        : {}),
    // Серверный summary всегда полный, поэтому сбрасываем existing если
    // worker больше не присылает поле (чат был откреплён везде).
    ...(dialog.pinnedListIds !== undefined ? { pinnedListIds: dialog.pinnedListIds } : {}),
    ...(dialog.peerStatusLabel !== undefined ? { peerStatusLabel: dialog.peerStatusLabel } : {}),
    ...(dialog.peerLastSeenAt !== undefined ? { peerLastSeenAt: dialog.peerLastSeenAt } : {}),
    ...(dialog.isPeerOnline !== undefined ? { isPeerOnline: dialog.isPeerOnline } : {}),
    // Флаг живёт только у DialogSummary когда воркер его проставил.
    // Undefined в summary означает «неизвестно» — тогда оставляем прежнее
    // значение (не сбрасываем при обычном dialogUpdated без флага).
    ...(dialog.isBlockedByUser !== undefined
      ? { isBlockedByUser: dialog.isBlockedByUser }
      : existing?.isBlockedByUser !== undefined
        ? { isBlockedByUser: existing.isBlockedByUser }
        : {}),
    ...(dialog.autoDeleteTime !== undefined ? { autoDeleteTime: dialog.autoDeleteTime } : {})
  };

  const merged: ChatViewModel = {
    ...base,
    ...(dialog.lastReadInboxMessageId || existing?.lastReadInboxMessageId
      ? {
          lastReadInboxMessageId:
            dialog.lastReadInboxMessageId ?? existing?.lastReadInboxMessageId!
        }
      : {}),
    ...(dialog.lastReadOutboxMessageId || existing?.lastReadOutboxMessageId
      ? {
          lastReadOutboxMessageId:
            dialog.lastReadOutboxMessageId ?? existing?.lastReadOutboxMessageId!
        }
      : {})
  };
  // Серверный summary всегда полный для last_message. Если в нём нет
  // outgoing/id/occurredAt — значит, последний сообщение не «моё»; нужно
  // явно стереть из existing, иначе галочки sent/read будут висеть на чужих
  // сообщениях.
  if (!dialog.lastMessageOutgoing) {
    delete merged.lastMessageOutgoing;
  }
  if (!dialog.lastMessageId) {
    delete merged.lastMessageId;
  }
  if (!dialog.lastMessageOccurredAt) {
    delete merged.lastMessageOccurredAt;
  }
  // Summary без autoDeleteTime = таймер выключен (в т.ч. только что) —
  // сбрасываем, иначе бейдж ⏱ висел бы после отключения.
  if (dialog.autoDeleteTime === undefined) {
    delete merged.autoDeleteTime;
  }
  return merged;
};

// Видимость+фокус страницы (см. RealtimeState.pageActive). hasFocus — потому
// что видимая, но неактивная вкладка второго окна тоже «не на глазах».
const readPageActive = (): boolean =>
  typeof document === "undefined" ||
  (document.visibilityState === "visible" && document.hasFocus());

// Открытый чат, который оператор сейчас действительно видит: только для него
// держим unreadCount=0 оптимистично и шлём markRead.
const isViewingChat = (state: RealtimeState, chatId: string): boolean =>
  state.selectedChatId === chatId && state.pageActive;

const withChat = (state: RealtimeState, chatId: string): ChatViewModel =>
  state.chats[chatId] ?? emptyChat(chatId);

const readTimestampForMessage = (
  messages: MessageViewModel[],
  messageId: string | undefined
): string | undefined =>
  messages.find((message) => message.id === messageId)?.occurredAt;

const compareMessagesByOrder = (
  left: Pick<MessageViewModel, "id" | "occurredAt">,
  right: Pick<MessageViewModel, "id" | "occurredAt">
): number => {
  const timestampComparison = left.occurredAt.localeCompare(right.occurredAt);
  if (timestampComparison !== 0) {
    return timestampComparison;
  }

  // id — int64 в десятичной строке: сравнивать строками нельзя
  // ("1048576" > "999424" лексикографически), иначе сообщения одной секунды
  // встают в ленте в обратном порядке.
  return compareMessageIds(left.id, right.id);
};

const findSortedMessageInsertIndex = (
  messages: MessageViewModel[],
  target: MessageViewModel
): number => {
  let low = 0;
  let high = messages.length;

  while (low < high) {
    const middle = (low + high) >> 1;
    if (compareMessagesByOrder(messages[middle]!, target) <= 0) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  return low;
};

// Полка отложенных: сортировка по времени отправки («когда в сети» — в конец).
const sortScheduledEntries = (
  entries: ScheduledMessageViewModel[]
): ScheduledMessageViewModel[] =>
  [...entries].sort((left, right) => {
    const l = left.sendWhenOnline ? "~" : left.sendAt ?? "";
    const r = right.sendWhenOnline ? "~" : right.sendAt ?? "";
    return l.localeCompare(r);
  });

const appendScheduledEntry = (
  entries: ScheduledMessageViewModel[],
  entry: ScheduledMessageViewModel
): ScheduledMessageViewModel[] =>
  sortScheduledEntries([...entries.filter((existing) => existing.id !== entry.id), entry]);

const upsertSortedMessageList = (
  messages: MessageViewModel[],
  nextMessage: MessageViewModel
): { existed: boolean; messages: MessageViewModel[] } => {
  const existingIndex = messages.findIndex((message) => message.id === nextMessage.id);
  const existed = existingIndex >= 0;
  const sourceMessages =
    existingIndex >= 0
      ? [...messages.slice(0, existingIndex), ...messages.slice(existingIndex + 1)]
      : messages;
  const insertIndex = findSortedMessageInsertIndex(sourceMessages, nextMessage);

  return {
    existed,
    messages: [
      ...sourceMessages.slice(0, insertIndex),
      nextMessage,
      ...sourceMessages.slice(insertIndex)
    ]
  };
};

// Готовый URL медиа не должен «откатываться»: воркер обрабатывает апдейты
// TDLib параллельно, и снимок сообщения, собранный ДО докачки файла, иногда
// приходит ПОСЛЕ события «файл скачан» — фото снова становилось «Загружаем…»,
// а повторного события уже не будет (лечилось только F5). Переносим URL с
// известной версии того же файла, если новая его не знает. blob: — локальное
// превью оптимистичной отправки, его не тащим (objectURL отзывается).
const carryMediaUrls = (
  next: MessageContent,
  known: MessageContent | undefined
): MessageContent => {
  if (!known || known.kind !== next.kind || !("fileId" in known) || !("fileId" in next)) {
    return next;
  }
  if (known.fileId !== next.fileId) {
    return next;
  }
  const knownUrl = "url" in known && typeof known.url === "string" && !known.url.startsWith("blob:")
    ? known.url
    : undefined;
  const knownThumb =
    "thumbnailUrl" in known &&
    typeof known.thumbnailUrl === "string" &&
    !known.thumbnailUrl.startsWith("blob:")
      ? known.thumbnailUrl
      : undefined;
  const needUrl = knownUrl && !("url" in next && next.url);
  const needThumb = knownThumb && !("thumbnailUrl" in next && next.thumbnailUrl);
  if (!needUrl && !needThumb) {
    return next;
  }
  return {
    ...next,
    ...(needUrl ? { url: knownUrl } : {}),
    ...(needThumb ? { thumbnailUrl: knownThumb } : {})
  } as MessageContent;
};

const withCarriedMediaUrls = (
  next: MessageViewModel,
  known: MessageViewModel | undefined
): MessageViewModel => {
  const content = carryMediaUrls(next.content, known?.content);
  return content === next.content ? next : { ...next, content };
};

const mergeSortedUniqueMessages = (
  left: MessageViewModel[],
  right: MessageViewModel[]
): MessageViewModel[] => {
  if (left.length === 0) {
    return right;
  }

  if (right.length === 0) {
    return left;
  }

  const merged: MessageViewModel[] = [];
  let leftIndex = 0;
  let rightIndex = 0;

  while (leftIndex < left.length || rightIndex < right.length) {
    const leftMessage = left[leftIndex];
    const rightMessage = right[rightIndex];

    if (!leftMessage) {
      merged.push(rightMessage!);
      rightIndex += 1;
      continue;
    }

    if (!rightMessage) {
      merged.push(leftMessage);
      leftIndex += 1;
      continue;
    }

    if (leftMessage.id === rightMessage.id) {
      // При совпадении id побеждает правая (уже лежащая в сторе) версия —
      // она богаче live-полями. Но реакции из истории (левая) авторитетны:
      // воркер кладёт их в historyLoaded всегда (пустой массив = реакций
      // нет), а сторовая версия могла быть создана до того, как реакцию
      // поставили с телефона.
      // Inline-кнопки из истории тоже подхватываем, если сторовая версия их
      // растеряла (гонка апдейтов у бота, который правит текст и клавиатуру
      // разными update'ами): страница истории собрана из свежего getMessage,
      // так что её клавиатура не хуже. Обратное не делаем — историческая
      // страница может быть старше живого «кнопки сняты».
      // URL медиа из истории тоже подхватываем, если сторовая версия его не
      // знает (см. carryMediaUrls) — иначе свежая страница истории не могла
      // починить застрявший плейсхолдер фото.
      const storeVersion = withCarriedMediaUrls(rightMessage, leftMessage);
      const restored =
        !storeVersion.replyMarkup && leftMessage.replyMarkup
          ? { ...storeVersion, replyMarkup: leftMessage.replyMarkup }
          : storeVersion;
      if (leftMessage.reactions !== undefined) {
        if (leftMessage.reactions.length > 0) {
          merged.push({ ...restored, reactions: leftMessage.reactions });
        } else if (restored.reactions) {
          const { reactions: _stale, ...rest } = restored;
          merged.push(rest);
        } else {
          merged.push(restored);
        }
      } else {
        merged.push(restored);
      }
      leftIndex += 1;
      rightIndex += 1;
      continue;
    }

    if (compareMessagesByOrder(leftMessage, rightMessage) <= 0) {
      merged.push(leftMessage);
      leftIndex += 1;
      continue;
    }

    merged.push(rightMessage);
    rightIndex += 1;
  }

  return merged;
};

// BigInt(строка) — дорогая аллокация, а компаратор дергает её ~2×n·log n раз
// за сортировку списка из 10k чатов. Кэшируем распарсенные order'ы по строке:
// внутри одной сортировки каждый уникальный order парсится один раз, между
// событиями кэш переживает неизменившиеся чаты. Cap — защита от вечного роста
// (старые order'ы после updateChatPosition больше не встречаются).
const chatOrderCache = new Map<string, bigint | null>();
const CHAT_ORDER_CACHE_LIMIT = 50_000;

const parseChatOrder = (value: string | undefined): bigint | null => {
  if (value === undefined || value.length === 0) {
    return null;
  }
  let cached = chatOrderCache.get(value);
  if (cached === undefined) {
    try {
      cached = BigInt(value);
    } catch {
      cached = null;
    }
    if (chatOrderCache.size >= CHAT_ORDER_CACHE_LIMIT) {
      chatOrderCache.clear();
    }
    chatOrderCache.set(value, cached);
  }
  return cached;
};

type OrderableChat = {
  isPinned?: boolean;
  pinnedListIds?: string[];
  lastActivityAt: string;
  orderByList?: Record<string, string>;
};

// Закреплён ли чат именно в этом списке. `isPinned` у воркера — «закреплён
// хоть где-то»: чат, запиненный только в папке, поднимался бы в топ «Общих».
// Для local:* пины живут отдельно (localPinnedByList) — здесь всегда false.
const isPinnedInList = (listId: string, chat: OrderableChat): boolean =>
  chat.pinnedListIds
    ? chat.pinnedListIds.includes(listId)
    : Boolean(chat.isPinned);

// Canonical chat-list order, matching Telegram exactly:
//   1. pinned chats first (explicit, so a pin is never buried),
//   2. then by TDLib's per-list position.order descending — this is the
//      authoritative recency Telegram itself uses (folds in drafts/service
//      messages, not just last_message.date),
//   3. lastActivityAt descending only as a fallback/tie-break when the
//      TDLib order is missing or equal (e.g. a freshly arrived message
//      before its updateChatPosition lands).
// Returns negative when `left` should appear above `right`.
const compareChatOrder = (
  listId: string,
  left: OrderableChat,
  right: OrderableChat
): number => {
  const leftPinned = isPinnedInList(listId, left) ? 1 : 0;
  const rightPinned = isPinnedInList(listId, right) ? 1 : 0;
  if (leftPinned !== rightPinned) {
    return rightPinned - leftPinned;
  }

  const leftOrder = parseChatOrder(left.orderByList?.[listId]);
  const rightOrder = parseChatOrder(right.orderByList?.[listId]);
  if (leftOrder !== null && rightOrder !== null && leftOrder !== rightOrder) {
    return leftOrder > rightOrder ? -1 : 1;
  }

  if (left.lastActivityAt !== right.lastActivityAt) {
    return left.lastActivityAt < right.lastActivityAt ? 1 : -1;
  }
  return 0;
};

// Порядок по формуле TDLib для незакреплённого чата: (date << 32) +
// server_message_id. Ровно это TDLib вернёт в position.order, когда долетит
// updateChatPosition, — так что чат встаёт сразу на то же место, без прыжка.
const syntheticChatOrder = (occurredAt: string, messageId: string): bigint | null => {
  const seconds = Math.floor(Date.parse(occurredAt) / 1000);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  let serverId = 0n;
  try {
    const id = BigInt(messageId);
    if (id > 0n) {
      serverId = id >> 20n;
    }
  } catch {
    // optimistic:<localId> — без серверного id, хватит даты.
  }
  return (BigInt(seconds) << 32n) + serverId;
};

// Новое последнее сообщение в чате: поднимаем его orderByList во всех
// TDLib-списках, где он лежит. Раньше здесь менялся только lastActivityAt, а
// compareChatOrder сравнивал по устаревшему order от воркера — чат показывал
// свежее время, но оставался на старом месте между уже прочитанными
// («10:10 → 12:40 → 11:20», жалоба 2026-10-13). Актуальный order от воркера
// приезжает позже (throttle) или вообще не приезжает, если чат вытеснен из
// его кэша (chatIdsKnownToClient). Закреплённые не трогаем: их order у TDLib
// заведомо больше любого синтетического.
const withBumpedChatOrder = (
  chat: ChatViewModel,
  occurredAt: string,
  messageId: string
): ChatViewModel => {
  if (chat.listIds.length === 0) {
    return chat;
  }
  const synthetic = syntheticChatOrder(occurredAt, messageId);
  if (synthetic === null) {
    return chat;
  }
  let next: Record<string, string> | null = null;
  for (const listId of chat.listIds) {
    const current = parseChatOrder(chat.orderByList?.[listId]);
    if (current !== null && current >= synthetic) {
      continue;
    }
    next ??= { ...(chat.orderByList ?? {}) };
    next[listId] = synthetic.toString();
  }
  return next ? { ...chat, orderByList: next } : chat;
};

const sortChatIds = (
  listId: string,
  chatIds: string[],
  chatsRecord: Record<string, ChatViewModel>
): string[] =>
  chatIds
    .map((chatId, index) => ({ chatId, index }))
    .sort((left, right) => {
      const leftChat = chatsRecord[left.chatId];
      const rightChat = chatsRecord[right.chatId];
      if (!leftChat || !rightChat) {
        return left.index - right.index;
      }
      const compared = compareChatOrder(listId, leftChat, rightChat);
      return compared !== 0 ? compared : left.index - right.index;
    })
    .map((entry) => entry.chatId);

// Ставит ОДИН изменившийся чат на своё место в уже отсортированном списке:
// удалить + бинарная вставка по compareChatOrder — O(n) копия + O(log n)
// сравнений вместо полного sort в O(n·log n) сравнений. На событиях
// chat.dialogUpdated / messageUpserted (летят на каждый updateChatPosition /
// LastMessage) это главный hot path при 10k+ чатов в main.
// Возвращает исходную ссылку, если позиция чата не изменилась — подписчики
// (виртуализованный DialogList) не передёргиваются.
const placeChatIdInList = (
  listId: string,
  chatIds: string[],
  chatId: string,
  chatsRecord: Record<string, OrderableChat>
): string[] => {
  const chat = chatsRecord[chatId];
  if (!chat) {
    return chatIds.includes(chatId) ? chatIds : [...chatIds, chatId];
  }
  const currentIndex = chatIds.indexOf(chatId);
  // Локальная папка: закреплённые лежат блоком сверху в своём порядке и от
  // новых сообщений не двигаются; незакреплённые сортируются ниже блока.
  const localPins = isLocalDialogList(listId) ? localPinnedByList.get(listId) ?? [] : null;
  if (localPins && localPins.includes(chatId) && currentIndex >= 0) {
    return chatIds;
  }
  const without =
    currentIndex < 0 ? chatIds : chatIds.filter((id) => id !== chatId);
  let lo = 0;
  if (localPins && localPins.length > 0) {
    while (lo < without.length && localPins.includes(without[lo]!)) {
      lo += 1;
    }
  }
  let hi = without.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const otherId = without[mid];
    const other = otherId !== undefined ? chatsRecord[otherId] : undefined;
    // Чат без записи в chatsRecord упорядочить нельзя — считаем, что известный
    // чат стоит выше него: неизвестные id попадают в список через upsertChatId
    // без сортировки и живут в хвосте.
    const compared = other ? compareChatOrder(listId, chat, other) : -1;
    if (compared < 0) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  if (currentIndex >= 0 && lo === currentIndex) {
    return chatIds;
  }
  const next = without.slice();
  next.splice(lo, 0, chatId);
  return next;
};

// Оптимистичный пузырь исходящего: вставка в ленту + обновление превью
// диалога (последнее сообщение, порядок в списках). Общий код для
// sendMessage и beginOptimisticSend (плейсхолдер медиа до загрузки файла).
const applyOptimisticMessage = (
  current: RealtimeState,
  chatId: string,
  optimistic: MessageViewModel
): Partial<RealtimeState> => {
  const existing = current.messagesByChat[chatId] ?? [];
  const { messages: nextMessages } = upsertSortedMessageList(existing, optimistic);
  const chat = withChat(current, chatId);
  const { typingActorId: _typingActorId, ...chatBase } = chat;
  const nextChatBase: ChatViewModel = {
    ...chatBase,
    lastMessageText: optimistic.text || chat.lastMessageText,
    ...lastMessageMeta(optimistic),
    lastActivityAt: optimistic.occurredAt,
    // Optimistic — мой исходящий, ставим галочку «отправлено» (✓
    // gray); ✓✓ появится позже из chat.messageRead.
    lastMessageOutgoing: true,
    lastMessageId: optimistic.id,
    lastMessageOccurredAt: optimistic.occurredAt,
    isTyping: false
  };
  const nextChat = withBumpedChatOrder(nextChatBase, optimistic.occurredAt, optimistic.id);
  const nextChatsRecord = { ...current.chats, [chatId]: nextChat };
  const nextChatIdsByList = { ...current.chatIdsByList };
  for (const listId of nextChat.listIds) {
    const list = nextChatIdsByList[listId] ?? [];
    nextChatIdsByList[listId] = placeChatIdInList(listId, list, chatId, nextChatsRecord);
  }
  return {
    chats: nextChatsRecord,
    chatIdsByList: nextChatIdsByList,
    messagesByChat: {
      ...current.messagesByChat,
      [chatId]: nextMessages
    }
  };
};

const removeOptimisticMessage = (
  current: RealtimeState,
  chatId: string,
  localId: string
): Partial<RealtimeState> => {
  const list = current.messagesByChat[chatId];
  if (!list) {
    return {};
  }
  const optimisticId = `optimistic:${localId}`;
  const next = list.filter((entry) => entry.id !== optimisticId && entry.localId !== localId);
  if (next.length === list.length) {
    return {};
  }
  return { messagesByChat: { ...current.messagesByChat, [chatId]: next } };
};

const mergeChatsIntoLists = (
  current: Record<string, string[]>,
  dialogs: DialogSummary[],
  targetListId: string,
  chatsRecord: Record<string, OrderableChat>
): Record<string, string[]> => {
  const next: Record<string, string[]> = { ...current };
  const pageById = new Map<string, DialogSummary>();
  for (const dialog of dialogs) {
    if (dialog.lists.some((list) => list.id === targetListId)) {
      pageById.set(dialog.chatId, dialog);
    }
  }
  // Не заменяем список страницей целиком: в папке могли быть id, слитые из
  // снапшотов main (или прошлых, более глубоких страниц), — иначе выбор папки
  // визуально «схлопывал» её до размера первой страницы. Хвост дочистит
  // dialogUpdated (membership по positions) и последующие страницы.
  //
  // Страницу и остаток сортируем ВМЕСТЕ по каноническому порядку (пины →
  // TDLib order). Раньше страница ставилась перед остатком как есть: снапшот
  // кэша воркера приходит кусками по 500 (on-demand после «Загрузить ещё»),
  // и второй кусок — чаты 500..999 — ложился ВЫШЕ первого, вместе с его
  // закреплёнными (жалоба 2026-09-14: «Общие» у Olivia/Kevin перемешиваются
  // при скролле вниз, F5 чинит). Порядок остатка берём из chatsRecord;
  // чаты без записи упорядочить нельзя — остаются в хвосте как были.
  const previousIds = current[targetListId] ?? [];
  const leftovers = previousIds.filter((chatId) => !pageById.has(chatId));
  const lookup = (chatId: string): OrderableChat | undefined =>
    pageById.get(chatId) ?? chatsRecord[chatId];
  const known: Array<{ chatId: string; chat: OrderableChat; index: number }> = [];
  const unknown: string[] = [];
  [...pageById.keys(), ...leftovers].forEach((chatId, index) => {
    const chat = lookup(chatId);
    if (chat) {
      known.push({ chatId, chat, index });
    } else {
      unknown.push(chatId);
    }
  });
  known.sort((left, right) => {
    const compared = compareChatOrder(targetListId, left.chat, right.chat);
    return compared !== 0 ? compared : left.index - right.index;
  });
  const mergedIds = [...known.map((entry) => entry.chatId), ...unknown];
  // Reference-equality, если состав и порядок не изменились: виртуализатор
  // списка чатов подписан на массив через useShallow.
  next[targetListId] =
    mergedIds.length === previousIds.length &&
    mergedIds.every((chatId, index) => previousIds[index] === chatId)
      ? previousIds
      : mergedIds;

  // Чаты страницы, лежащие и в ДРУГИХ списках, ставим туда по порядку, а не
  // в хвост: страница папки дописывала свои чаты в конец main как есть, и
  // внизу «Общих» появлялись чаты с временем новее соседей, а бинарная
  // вставка placeChatIdInList по уже несортированному массиву разносила
  // ошибку дальше.
  const orderable: Record<string, OrderableChat> = { ...chatsRecord };
  for (const dialog of dialogs) {
    orderable[dialog.chatId] = dialog;
  }
  for (const dialog of dialogs) {
    for (const list of dialog.lists) {
      if (list.id === targetListId) {
        continue;
      }
      next[list.id] = placeChatIdInList(list.id, next[list.id] ?? [], dialog.chatId, orderable);
    }
  }

  return next;
};

const mergeSingleChatIntoLists = (
  current: Record<string, string[]>,
  dialog: DialogSummary,
  chatsRecord: Record<string, ChatViewModel>
): Record<string, string[]> => {
  // Важно: сохраняем reference-equality для массивов, содержимое которых не
  // меняется. Иначе виртуализованный список чатов пересобирается на каждый
  // chat.dialogUpdated (а он летит на каждый updateChatLastMessage/Position),
  // и добавление чата в другую папку визуально дёргает активный список и
  // сбрасывает scroll позицию. Стратегия:
  //   1) Массив папки, куда чат теперь входит — пересчитать; если после
  //      сортировки порядок совпал с прежним — вернуть старую ссылку.
  //   2) Массив папки, откуда чат уходит — отфильтровать, только если он там
  //      реально лежал. Иначе оставить как есть.
  const targetIds = new Set(dialog.lists.map((list) => list.id));
  const next: Record<string, string[]> = {};
  let changed = false;

  for (const [listId, chatIds] of Object.entries(current)) {
    if (targetIds.has(listId)) {
      // Пересчитаем ниже во втором проходе.
      next[listId] = chatIds;
      continue;
    }
    const idx = chatIds.indexOf(dialog.chatId);
    if (idx < 0) {
      next[listId] = chatIds;
      continue;
    }
    if (isLocalDialogList(listId)) {
      // Локальные папки воркер не знает (их состав — в БД gateway), в
      // dialog.lists их нет никогда. Раньше чат здесь ВЫКИДЫВАЛСЯ из
      // local:* на каждый dialogUpdated (любое новое сообщение) и
      // возвращался только после следующего listDialogs — папка «моргала»,
      // а после переноса чата между папками казалось, что перенос не
      // сработал. Состав не трогаем, только пересортировываем по активности.
      const placed = placeChatIdInList(listId, chatIds, dialog.chatId, chatsRecord);
      next[listId] = placed;
      if (placed !== chatIds) {
        changed = true;
      }
      continue;
    }
    const filtered = chatIds.slice();
    filtered.splice(idx, 1);
    next[listId] = filtered;
    changed = true;
  }

  for (const list of dialog.lists) {
    const prev = next[list.id] ?? [];
    const placed = placeChatIdInList(list.id, prev, dialog.chatId, chatsRecord);
    if (placed === prev) {
      next[list.id] = prev;
      continue;
    }
    next[list.id] = placed;
    changed = true;
  }

  return changed ? next : current;
};

// Дописывает local:* папки поверх результата синхронизации с TDLib-листами,
// так чтобы серверный chat.dialogListsUpdated не вытирал пользовательские
// локальные папки. Также обновляет chatIdsByList[local:*] из текущего
// includedChatIds. Вызывается из refreshLocalFolders и из обработчика
// chat.dialogListsUpdated.
type LocalFolderEntry = RealtimeState["localFolders"][number];

const toLocalFolderEntry = (folder: {
  folderId: string;
  title: string;
  chatLimit?: number | null;
  includedChatIds: string[];
  pinnedChatIds?: string[];
  position?: number | null;
}): LocalFolderEntry => ({
  folderId: folder.folderId,
  title: folder.title,
  chatLimit:
    typeof folder.chatLimit === "number" && folder.chatLimit > 0 ? folder.chatLimit : null,
  includedChatIds: folder.includedChatIds.slice(),
  pinnedChatIds: Array.isArray(folder.pinnedChatIds) ? folder.pinnedChatIds.slice() : [],
  position:
    typeof folder.position === "number" && Number.isFinite(folder.position)
      ? folder.position
      : null
});

// Перечитывание папок по chat.localFoldersUpdated: серия чужих правок
// (саппорт добавляет чаты один за другим) схлопывается в одну загрузку.
let localFoldersRefreshTimer: ReturnType<typeof setTimeout> | null = null;
const scheduleLocalFoldersRefresh = (run: () => Promise<void>): void => {
  if (localFoldersRefreshTimer !== null) {
    clearTimeout(localFoldersRefreshTimer);
  }
  localFoldersRefreshTimer = setTimeout(() => {
    localFoldersRefreshTimer = null;
    void run();
  }, 400);
};

// Пины локальных папок (local:*) — их знает только gateway, у чата в
// pinnedListIds их нет. Держим отдельной картой для compare/place-функций.
const localPinnedByList = new Map<string, string[]>();
export const isLocalDialogList = (listId: string): boolean => listId.startsWith("local:");
export const pinnedChatIdsForList = (state: RealtimeState, listId: string): string[] => {
  const idsInList = state.chatIdsByList[listId] ?? [];
  if (isLocalDialogList(listId)) {
    const pins = localPinnedByList.get(listId) ?? [];
    return idsInList.filter((id) => pins.includes(id));
  }
  return idsInList.filter((id) => {
    const chat = state.chats[id];
    return chat?.pinnedListIds?.includes(listId) ?? false;
  });
};

const mergeLocalFoldersIntoLists = (
  state: RealtimeState,
  localFolders: LocalFolderEntry[]
): Partial<RealtimeState> => {
  for (const key of [...localPinnedByList.keys()]) {
    if (!localFolders.some((folder) => `local:${folder.folderId}` === key)) {
      localPinnedByList.delete(key);
    }
  }
  const nextDialogLists: Record<string, DialogListViewModel> = {};
  for (const [id, list] of Object.entries(state.dialogLists)) {
    if (!id.startsWith("local:")) {
      nextDialogLists[id] = list;
    }
  }
  const orderWithoutLocal = state.dialogListOrder.filter(
    (id) => !id.startsWith("local:")
  );
  const nextChatIdsByList: Record<string, string[]> = {};
  for (const [id, ids] of Object.entries(state.chatIdsByList)) {
    if (!id.startsWith("local:")) {
      nextChatIdsByList[id] = ids;
    }
  }

  // Локальные папки не имеют серверной страничной подгрузки. Чтобы UI
  // не показывал «Загрузить ещё» и сразу рисовал «Это все чаты», явно
  // выставляем dialogListStateById и dialogHasMoreById для каждой
  // local:*. Сначала очищаем stale local: записи.
  const nextDialogListStateById: Record<string, DialogListState> = {};
  for (const [id, state2] of Object.entries(state.dialogListStateById)) {
    if (!id.startsWith("local:")) {
      nextDialogListStateById[id] = state2;
    }
  }
  const nextDialogHasMoreById: Record<string, boolean> = {};
  for (const [id, value] of Object.entries(state.dialogHasMoreById)) {
    if (!id.startsWith("local:")) {
      nextDialogHasMoreById[id] = value;
    }
  }

  const localIds = new Set<string>();
  // Единый ряд вкладок: локальная папка с известной позицией встаёт на этот
  // индекс среди TDLib-папок и «Все чаты» (архив всегда последний), без
  // позиции — в конец. Позиция пишется gateway'ем при DnD вкладок.
  const positioned = localFolders
    .map((folder, index) => ({ folder, index }))
    .sort((left, right) => {
      const lp = left.folder.position ?? Number.MAX_SAFE_INTEGER;
      const rp = right.folder.position ?? Number.MAX_SAFE_INTEGER;
      return lp !== rp ? lp - rp : left.index - right.index;
    })
    .map((entry) => entry.folder);
  for (const folder of positioned) {
    const id = `local:${folder.folderId}`;
    localIds.add(id);
    const pinsInFolder = folder.pinnedChatIds.filter((chatId) =>
      folder.includedChatIds.includes(chatId)
    );
    localPinnedByList.set(id, pinsInFolder);
    // Reference-equality для неизменившихся записей: этот merge дёргается на
    // каждый dialogListsUpdated и refreshLocalFolders, и свежие объекты/массивы
    // пробивали useShallow-подписки сайдбара → перерисовка всех строк.
    const previousList = state.dialogLists[id];
    nextDialogLists[id] =
      previousList && previousList.title === folder.title && previousList.kind === "local"
        ? previousList
        : { id, title: folder.title, kind: "local" };
    const sortedIds = [
      ...pinsInFolder,
      ...sortChatIds(
        id,
        folder.includedChatIds.filter((chatId) => !pinsInFolder.includes(chatId)),
        state.chats
      )
    ];
    const previousIds = state.chatIdsByList[id];
    nextChatIdsByList[id] =
      previousIds &&
      previousIds.length === sortedIds.length &&
      previousIds.every((chatId, index) => chatId === sortedIds[index])
        ? previousIds
        : sortedIds;
    const archiveIndex = orderWithoutLocal.indexOf(ARCHIVE_DIALOG_LIST_ID);
    const tailIndex = archiveIndex >= 0 ? archiveIndex : orderWithoutLocal.length;
    const insertAt =
      folder.position === null ? tailIndex : Math.max(0, Math.min(folder.position, tailIndex));
    orderWithoutLocal.splice(insertAt, 0, id);
    const previousState = state.dialogListStateById[id];
    nextDialogListStateById[id] =
      previousState && !previousState.isLoading && previousState.isLoaded
        ? previousState
        : { isLoading: false, isLoaded: true };
    nextDialogHasMoreById[id] = false;
  }

  // Сохраняем активную local:* папку, если она ещё существует. Без этого
  // synchronizeDialogListState отбрасывает «активный» listId, которого нет
  // в серверных availableLists, и переключает на main — что наблюдается
  // как «при клике на чат из local-папки кидает в Общие».
  let activeDialogListId = state.activeDialogListId;
  if (
    state.activeDialogListId.startsWith("local:") &&
    !localIds.has(state.activeDialogListId)
  ) {
    // local папка исчезла (например, удалена) — переключаемся на main.
    activeDialogListId = DEFAULT_DIALOG_LIST_ID;
  }

  return {
    localFolders,
    dialogLists: nextDialogLists,
    dialogListOrder: orderWithoutLocal,
    chatIdsByList: nextChatIdsByList,
    dialogListStateById: nextDialogListStateById,
    dialogHasMoreById: nextDialogHasMoreById,
    activeDialogListId
  };
};

const synchronizeDialogListState = (
  state: Pick<
    RealtimeState,
    | "dialogLists"
    | "dialogListOrder"
    | "dialogListStateById"
    | "dialogRequestedLimitById"
    | "dialogHasMoreById"
    | "chatIdsByList"
    | "activeDialogListId"
  >,
  availableLists: DialogListRef[],
  options?: { loadedListId?: string }
): {
  dialogLists: Record<string, DialogListViewModel>;
  dialogListOrder: string[];
  dialogListStateById: Record<string, DialogListState>;
  dialogRequestedLimitById: Record<string, number>;
  dialogHasMoreById: Record<string, boolean>;
  chatIdsByList: Record<string, string[]>;
  activeDialogListId: string;
} => {
  const normalizedLists = availableLists.length > 0 ? availableLists : [DEFAULT_DIALOG_LIST];
  const nextOrder = normalizedLists.map((dialogList) => dialogList.id);
  const nextListIds = new Set(nextOrder);
  // Reference-equality всюду, где содержимое не поменялось: worker шлёт
  // dialogListsUpdated со СВЕЖИМИ объектами каждый раз, и без reuse новые
  // identity пробивали useShallow-подписки сайдбара + WeakMap-кэш чипов папок
  // → перерисовка всех видимых строк на каждое событие (визуальный «дёрг»).
  const dialogLists = normalizedLists.reduce<Record<string, DialogListViewModel>>(
    (acc, dialogList) => {
      const previous = state.dialogLists[dialogList.id];
      acc[dialogList.id] =
        previous &&
        previous.title === dialogList.title &&
        previous.kind === dialogList.kind
          ? previous
          : dialogList;
      return acc;
    },
    {}
  );
  const dialogListStateById = normalizedLists.reduce<Record<string, DialogListState>>(
    (acc, dialogList) => {
      const previous = state.dialogListStateById[dialogList.id];
      const isLoading = previous?.isLoading ?? false;
      const isLoaded =
        dialogList.id === options?.loadedListId ? true : previous?.isLoaded ?? false;
      acc[dialogList.id] =
        previous && previous.isLoading === isLoading && previous.isLoaded === isLoaded
          ? previous
          : { isLoading, isLoaded };
      return acc;
    },
    {}
  );
  const dialogRequestedLimitById = Object.fromEntries(
    Object.entries(state.dialogRequestedLimitById).filter(([listId]) => nextListIds.has(listId))
  );
  const dialogHasMoreById = Object.fromEntries(
    Object.entries(state.dialogHasMoreById).filter(([listId]) => nextListIds.has(listId))
  );
  const chatIdsByList = Object.fromEntries(
    Object.entries(state.chatIdsByList).filter(([listId]) => nextListIds.has(listId))
  );
  const fallbackActiveDialogListId = nextOrder[0] ?? DEFAULT_DIALOG_LIST_ID;
  const activeDialogListId = nextListIds.has(state.activeDialogListId)
    ? state.activeDialogListId
    : fallbackActiveDialogListId;

  return {
    dialogLists,
    dialogListOrder: nextOrder,
    dialogListStateById,
    dialogRequestedLimitById,
    dialogHasMoreById,
    chatIdsByList,
    activeDialogListId
  };
};

const toMessageViewModel = (
  message:
    | Extract<ServerEvent, { type: "chat.messageUpserted" }>
    | Extract<ServerEvent, { type: "chat.historyLoaded" }>["messages"][number]
    | Extract<ServerEvent, { type: "chat.searchResultsLoaded" }>["results"][number]
    | NonNullable<Extract<ServerEvent, { type: "chat.pinnedMessageUpdated" }>["pinnedMessage"]>
    | NonNullable<Extract<ServerEvent, { type: "chat.messageDeleted" }>["snapshot"]>
): MessageViewModel => ({
  id: message.messageId,
  content: message.content,
  text: contentToText(message.content),
  senderId: message.senderId,
  ...(message.senderName ? { senderName: message.senderName } : {}),
  ...(message.internalAuthorName ? { internalAuthorName: message.internalAuthorName } : {}),
  ...(message.replyTo ? { replyTo: message.replyTo } : {}),
  ...(message.isEdited ? { isEdited: message.isEdited } : {}),
  outgoing: message.outgoing,
  occurredAt: message.occurredAt,
  ...("reactions" in message && message.reactions ? { reactions: message.reactions } : {}),
  ...("replyMarkup" in message && message.replyMarkup ? { replyMarkup: message.replyMarkup } : {}),
  ...("messageThreadId" in message && message.messageThreadId
    ? { messageThreadId: message.messageThreadId }
    : {}),
  ...("forwardedFrom" in message && message.forwardedFrom
    ? { forwardedFrom: message.forwardedFrom }
    : {}),
  ...("mediaAlbumId" in message && message.mediaAlbumId
    ? { mediaAlbumId: message.mediaAlbumId }
    : {})
});

// temp-id исходящих, уже заменённых финальным сообщением (replacesMessageId).
// Поздний upsert с таким id (гонка воркера: getMessage/updateFile по temp
// сообщению во время аплоада медиа) иначе воскрешал бы копию в ленте —
// «шаблон задваивается у нас, юзер видит один». Кольцо на чат, не персистится.
const REPLACED_MESSAGE_IDS_PER_CHAT = 200;
const replacedMessageIdsByChat = new Map<string, Set<string>>();
const rememberReplacedMessageId = (chatId: string, messageId: string): void => {
  let bucket = replacedMessageIdsByChat.get(chatId);
  if (!bucket) {
    bucket = new Set<string>();
    replacedMessageIdsByChat.set(chatId, bucket);
  }
  if (bucket.size >= REPLACED_MESSAGE_IDS_PER_CHAT) {
    const oldest = bucket.values().next().value;
    if (oldest !== undefined) {
      bucket.delete(oldest);
    }
  }
  bucket.add(messageId);
};
const isReplacedMessageId = (chatId: string, messageId: string): boolean =>
  replacedMessageIdsByChat.get(chatId)?.has(messageId) ?? false;

const upsertMessage = (
  state: RealtimeState,
  event: Extract<ServerEvent, { type: "chat.messageUpserted" }>
): Partial<RealtimeState> => {
  if (event.replacesMessageId) {
    rememberReplacedMessageId(event.chatId, event.replacesMessageId);
  } else if (isReplacedMessageId(event.chatId, event.messageId)) {
    return {};
  }
  const chat = withChat(state, event.chatId);
  const { typingActorId: _typingActorId, ...chatWithoutTyping } = chat;
  const existing = state.messagesByChat[event.chatId] ?? [];
  // updateMessageSendSucceeded carries replacesMessageId == the temporary id
  // (our pending placeholder, re-keyed in chat.messageAccepted). Drop it so
  // the final message takes its place instead of stacking a duplicate.
  // Плейсхолдер снимаем ДВУМЯ независимыми ключами:
  //   * replacesMessageId — temp-id, в который мы его перекеили в
  //     chat.messageAccepted;
  //   * localId — id нашей команды отправки, который воркер вернул на
  //     финальном сообщении.
  // Одного replacesMessageId было мало: если chat.messageAccepted приходил
  // ПОЗЖЕ этого события, плейсхолдер оставался в ленте копией с вечным
  // «отправляется».
  const base =
    event.replacesMessageId || event.localId
      ? existing.filter(
          (entry) =>
            entry.id !== event.replacesMessageId &&
            !(event.localId !== undefined && entry.localId === event.localId)
        )
      : existing;
  const nextModel = withCarriedMediaUrls(
    toMessageViewModel(event),
    existing.find((entry) => entry.id === event.messageId)
  );
  // Воркер кладёт reactions в messageUpserted только когда они непусты, а
  // часть событий (докачка медиа, editMessage) собирается без них вовсе.
  // Отсутствие поля = «не знаю», поэтому переносим реакции с прежней версии
  // сообщения — иначе чипы исчезали при любом апдейте. Явное обнуление
  // приходит отдельным chat.messageReactionsUpdated с пустым массивом.
  if (!nextModel.reactions) {
    const previous = existing.find((entry) => entry.id === event.messageId);
    if (previous?.reactions && previous.reactions.length > 0) {
      nextModel.reactions = previous.reactions;
    }
  }
  // То же для inline-кнопок бота, но по явному признаку: null = кнопок нет
  // (воркер посмотрел и знает), отсутствие поля = не знаю — тогда держим уже
  // показанные. Иначе событие от старого воркера гасило бы клавиатуру, а
  // вернуть её было нечем: повторный вход в чат историю не перезапрашивает.
  if (event.replyMarkup === undefined) {
    const previous = existing.find((entry) => entry.id === event.messageId);
    if (previous?.replyMarkup) {
      nextModel.replyMarkup = previous.replyMarkup;
    }
  }
  const { existed, messages: nextMessages } = upsertSortedMessageList(
    base,
    nextModel
  );
  // Результаты поиска — отдельный срез стора, и до этого их обновлял только
  // chat.messageDeleted. А вкладки «Фото/Видео/Файлы» в профиле собеседника
  // живут именно на них: воркер отдаёт медиа без url, пока TDLib не докачал
  // файл, и присылает messageUpserted со ссылкой позже. В ленту ссылка
  // приезжала, в сетку фото — нет, и плитки навсегда оставались пустыми.
  let nextSearchStateByChat = state.searchStateByChat;
  for (const searchKey of searchStateKeysForChat(state.searchStateByChat, event.chatId)) {
    const searchState = state.searchStateByChat[searchKey];
    if (searchState?.results.some((entry) => entry.id === event.messageId)) {
      nextSearchStateByChat = {
        ...nextSearchStateByChat,
        [searchKey]: {
          ...searchState,
          results: searchState.results.map((entry) =>
            entry.id === event.messageId ? nextModel : entry
          )
        }
      };
    }
  }
  const isActiveChat = isViewingChat(state, event.chatId);
  // +1 к непрочитанным — только для ДЕЙСТВИТЕЛЬНО нового входящего: новее
  // отметки прочтения. Воркер переизлучает messageUpserted и по старым
  // сообщениям (правка, докачка медиа, расшифровка голосового), а лента
  // покинутого чата урезана (trimLeftChatHistory) — такое сообщение в ней
  // «не existed», и бейдж рос без новых сообщений от собеседника.
  const isNewerThanReadMark =
    !chat.lastReadInboxMessageId || isMessageIdAfter(event.messageId, chat.lastReadInboxMessageId);
  const unreadIncrement =
    !event.outgoing && !isActiveChat && !existed && isNewerThanReadMark ? 1 : 0;
  const latestMessage = nextMessages.at(-1);
  const isLatestMessage = latestMessage?.id === event.messageId;
  const nextChat: ChatViewModel = {
    ...chatWithoutTyping,
    title: chat.title || "Диалог",
    // Появилось сообщение — чат больше не «подтверждённо пустой» (иначе
    // DialogList продолжал бы прятать его как пустышку с черновиком).
    hasNoMessages: false,
    lastMessageText: latestMessage?.text ?? chat.lastMessageText,
    ...(latestMessage?.senderName ? { lastMessageSenderName: latestMessage.senderName } : {}),
    ...lastMessageMeta(latestMessage),
    lastActivityAt: latestMessage?.occurredAt ?? chat.lastActivityAt,
    isGroupChat: event.isGroupChat ?? chat.isGroupChat,
    unreadCount: isActiveChat ? 0 : chat.unreadCount + unreadIncrement,
    isTyping: false
  };
  // Метаданные последнего сообщения для галочек в DialogList: их надо
  // СБРОСИТЬ когда latestMessage уже не outgoing (кто-то ответил после
  // моего исходящего) — иначе у чужого ответа будут висеть мои ✓✓.
  if (latestMessage?.outgoing) {
    nextChat.lastMessageOutgoing = true;
    nextChat.lastMessageId = latestMessage.id;
    nextChat.lastMessageOccurredAt = latestMessage.occurredAt;
  } else {
    delete nextChat.lastMessageOutgoing;
    delete nextChat.lastMessageId;
    delete nextChat.lastMessageOccurredAt;
  }
  if (isLatestMessage && latestMessage) {
    const bumped = withBumpedChatOrder(nextChat, latestMessage.occurredAt, latestMessage.id);
    if (bumped.orderByList) {
      nextChat.orderByList = bumped.orderByList;
    }
  }
  const nextChatIdsByList = { ...state.chatIdsByList };
  const nextChatsRecord = {
    ...state.chats,
    [event.chatId]: nextChat
  };

  if (isLatestMessage && nextChat.listIds.length > 0) {
    for (const listId of nextChat.listIds) {
      const list = nextChatIdsByList[listId] ?? [];
      nextChatIdsByList[listId] = placeChatIdInList(
        listId,
        list,
        event.chatId,
        nextChatsRecord
      );
    }
  }
  if (isLatestMessage) {
    // Локальные папки в listIds не входят — поднимаем чат по активности и
    // в них (закреплённые остаются на месте, см. placeChatIdInList).
    for (const [listId, list] of Object.entries(nextChatIdsByList)) {
      if (isLocalDialogList(listId) && list.includes(event.chatId)) {
        nextChatIdsByList[listId] = placeChatIdInList(
          listId,
          list,
          event.chatId,
          nextChatsRecord
        );
      }
    }
  }

  return {
    chats: nextChatsRecord,
    chatIdsByList: nextChatIdsByList,
    messagesByChat: {
      ...state.messagesByChat,
      [event.chatId]: nextMessages
    },
    ...(nextSearchStateByChat === state.searchStateByChat
      ? {}
      : { searchStateByChat: nextSearchStateByChat })
  };
};

// Б2 «сообщения висят новыми, но не грузятся»: история чата грузится один
// раз (historyLoadedByChat) и при потере chat.messageUpserted (разрыв WS,
// рестарт шарда, эвикция чата из кэша воркера) лента отставала до F5, хотя
// карточка диалога (dialogUpdated) уже показывала новое. Лечим сверкой:
// lastMessageId из снимка диалога должен быть в ленте — иначе дотягиваем хвост
// (historyLoaded мержится через mergeSortedUniqueMessages, ничего не теряем).
const hasHistoryTailGap = (state: RealtimeState, chatId: string): boolean => {
  const chat = state.chats[chatId];
  const lastMessageId = chat?.lastMessageId;
  if (!lastMessageId || lastMessageId.startsWith("optimistic:")) {
    return false;
  }
  const messages = state.messagesByChat[chatId];
  if (!messages || messages.length === 0) {
    return false;
  }
  // Смотрим только хвост: пропавшее сообщение всегда новее загруженных.
  for (let index = messages.length - 1; index >= Math.max(0, messages.length - 12); index -= 1) {
    if (messages[index]?.id === lastMessageId) {
      return false;
    }
  }
  return true;
};
const tailReloadTimers = new Map<string, number>();
const scheduleHistoryTailReload = (
  get: () => RealtimeState,
  chatId: string,
  delayMs = 800
): void => {
  if (tailReloadTimers.has(chatId)) {
    return;
  }
  tailReloadTimers.set(
    chatId,
    window.setTimeout(() => {
      tailReloadTimers.delete(chatId);
      const state = get();
      if (
        state.historyLoadedByChat[chatId] &&
        !state.historyLoadingByChat[chatId] &&
        hasHistoryTailGap(state, chatId)
      ) {
        state.loadHistory(chatId, 80);
      }
    }, delayMs)
  );
};

// Пины локальной папки: оптимистично перестраиваем список и сохраняем на
// gateway (PATCH local-folders.pinnedChatIds). Лимитов Telegram тут нет.
const saveLocalFolderPins = async (
  get: () => RealtimeState,
  set: (partial: Partial<RealtimeState> | ((state: RealtimeState) => Partial<RealtimeState>)) => void,
  listId: string,
  pinnedChatIds: string[]
): Promise<void> => {
  const state = get();
  const folderId = listId.slice("local:".length);
  const folder = state.localFolders.find((entry) => entry.folderId === folderId);
  if (!folder || !state.accountId) {
    return;
  }
  const nextFolders = state.localFolders.map((entry) =>
    entry.folderId === folderId ? { ...entry, pinnedChatIds } : entry
  );
  set((current) => mergeLocalFoldersIntoLists(current, nextFolders));
  try {
    await internalApi.updateLocalFolder(
      { accountId: state.accountId, folderId, pinnedChatIds },
      state.wsUrl
    );
  } catch (cause) {
    set((current) => ({
      errors: pushError(
        current.errors,
        "Не удалось сохранить закреплённые чаты папки.",
        cause instanceof Error ? cause.message : undefined
      )
    }));
    void get().refreshLocalFolders();
  }
};

const PERSIST_STORAGE_KEY = "tg-client:realtime";
const PERSIST_WRITE_DELAY_MS = 600;
const PERSIST_DB_NAME = "tg-client";
const PERSIST_DB_STORE = "state";

// PersistStorage (не StateStorage + createJSONStorage): zustand-persist зовёт
// setItem на КАЖДЫЙ set() стора, и с createJSONStorage сериализация мегабайтного
// снапшота (кэш переводов) происходила синхронно на каждое WS-событие — один из
// главных источников фризов. Здесь pending хранит несериализованный снапшот
// (иммутабельный, копировать не нужно), а JSON.stringify выполняется один раз
// в троттл-таймере перед записью в IDB.
// Снапшот стора — ПО АККАУНТУ (ключ IDB с суффиксом accountId). Раньше
// ключ был один на origin, и два окна на разных аккаунтах (мультиокно)
// перетирали друг другу activeDialogListId / кэш переводов / черновики.
// Кто читает — берёт accountId живого стора, а до его создания — аккаунт,
// с которым стартует окно (?account= или последний выбранный).
let persistAccountIdReader: (() => string) | null = null;
const persistAccountSuffix = (): string => {
  const live = persistAccountIdReader?.() ?? "";
  if (live) {
    return live;
  }
  return readBootAccountId() ?? "";
};
const persistKeyFor = (name: string, accountId: string): string =>
  accountId ? `${name}:${accountId}` : name;

const createThrottledIDBStorage = (): PersistStorage<Partial<RealtimeState>> => {
  if (typeof window === "undefined") {
    return {
      getItem: () => null,
      setItem: () => undefined,
      removeItem: () => undefined
    };
  }

  const idbStore = createIDBStore(PERSIST_DB_NAME, PERSIST_DB_STORE);
  const pending = new Map<string, StorageValue<Partial<RealtimeState>>>();
  let timerId: number | null = null;
  let inflightFlush: Promise<void> | null = null;

  const serialize = (value: StorageValue<Partial<RealtimeState>>): string => {
    const snapshot = value.state as unknown as PersistedRealtimeState;
    return JSON.stringify({
      ...value,
      state: {
        ...snapshot,
        translationsByMessage: boundTranslations(snapshot.translationsByMessage ?? {})
      }
    });
  };

  const writeBatch = async (
    entries: Array<[string, StorageValue<Partial<RealtimeState>>]>
  ): Promise<void> => {
    for (const [key, value] of entries) {
      try {
        await idbSet(key, serialize(value), idbStore);
      } catch {
        // ignore quota / storage failures
      }
    }
  };

  const flush = (): Promise<void> => {
    if (timerId !== null) {
      window.clearTimeout(timerId);
      timerId = null;
    }
    if (pending.size === 0) {
      return inflightFlush ?? Promise.resolve();
    }
    const entries = Array.from(pending);
    pending.clear();
    inflightFlush = writeBatch(entries).finally(() => {
      inflightFlush = null;
    });
    return inflightFlush;
  };

  const schedule = (): void => {
    if (timerId !== null) {
      return;
    }
    timerId = window.setTimeout(() => {
      timerId = null;
      void flush();
    }, PERSIST_WRITE_DELAY_MS);
  };

  const flushSync = (): void => {
    void flush();
  };

  window.addEventListener("pagehide", flushSync);
  window.addEventListener("beforeunload", flushSync);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushSync();
    }
  });

  return {
    getItem: async (name) => {
      const accountId = persistAccountSuffix();
      const key = persistKeyFor(name, accountId);
      const queued = pending.get(key);
      if (queued) {
        return queued;
      }
      try {
        const stored = await idbGet<string>(key, idbStore);
        if (typeof stored === "string") {
          return JSON.parse(stored) as StorageValue<Partial<RealtimeState>>;
        }
        // Миграция со старого общего ключа: берём его снапшот один раз, если
        // он принадлежит этому же аккаунту.
        if (accountId) {
          const legacy = await idbGet<string>(name, idbStore);
          if (typeof legacy === "string") {
            const parsed = JSON.parse(legacy) as StorageValue<Partial<RealtimeState>>;
            if (parsed?.state?.accountId === accountId) {
              return parsed;
            }
          }
        }
        return null;
      } catch {
        return null;
      }
    },
    setItem: (name, value) => {
      // Ключ — по аккаунту из самого снапшота (partialize кладёт accountId):
      // пишем ровно туда, чей это стейт, даже во время переключения.
      const snapshotAccountId =
        typeof value?.state?.accountId === "string" && value.state.accountId
          ? value.state.accountId
          : persistAccountSuffix();
      if (!snapshotAccountId) {
        return;
      }
      pending.set(persistKeyFor(name, snapshotAccountId), value);
      schedule();
    },
    removeItem: async (name) => {
      const key = persistKeyFor(name, persistAccountSuffix());
      pending.delete(key);
      try {
        await idbDel(key, idbStore);
      } catch {
        // ignore
      }
    }
  };
};

// Сколько переводов сохранять в IDB. Кэш переводов персистится, чтобы после
// reload/переоткрытия НЕ ретранслировать всю историю заново (это была главная
// причина «долгой загрузки перевода»). Ограничиваем, чтобы снапшот не рос без
// предела — храним самые свежие записи (порядок ключей = порядок вставки).
const PERSISTED_TRANSLATIONS_LIMIT = 4000;

interface PersistedRealtimeState {
  accountId: string;
  draftsByChat: Record<string, string>;
  activeDialogListId: string;
  translatedChats: Record<string, boolean>;
  translationLanguageByChat?: Record<string, string>;
  outgoingLanguageByChat?: Record<string, string>;
  outgoingLanguageByAccount?: Record<string, string>;
  translationsByMessage?: RealtimeState["translationsByMessage"];
}

// Тот же кэш в ПАМЯТИ жил без предела — bound применялся только при
// сериализации в IDB. За смену с включённым переводом это десятки тысяч
// записей, которые уже никто не увидит: ленты давно выгружены. Срезаем с
// гистерезисом (порог вдвое выше цели), чтобы не пересобирать словарь на
// каждый батч.
const MEMORY_TRANSLATIONS_LIMIT = PERSISTED_TRANSLATIONS_LIMIT * 2;

// Эвикция идёт по порядку вставки, а он НЕ про давность просмотра: ключи
// открытого чата были вставлены, когда его впервые перевели, и за смену
// (сотни чатов) успевали стать «самыми старыми». Их вычищало прямо из-под
// глаз саппорта — переводы в открытом чате просто исчезали кусками. Записи
// текущего чата теперь иммунны к обрезке.
const boundMemoryTranslations = (
  translations: RealtimeState["translationsByMessage"],
  keepChatId?: string | null
): RealtimeState["translationsByMessage"] => {
  const keys = Object.keys(translations);
  if (keys.length <= MEMORY_TRANSLATIONS_LIMIT) {
    return translations;
  }
  const kept = new Set(keys.slice(keys.length - PERSISTED_TRANSLATIONS_LIMIT));
  const prefix = keepChatId ? `${keepChatId}:` : null;
  if (prefix) {
    for (const key of keys) {
      if (key.startsWith(prefix)) {
        kept.add(key);
      }
    }
  }
  const next: RealtimeState["translationsByMessage"] = {};
  for (const key of keys) {
    if (kept.has(key)) {
      next[key] = translations[key]!;
    }
  }
  return next;
};

const boundTranslations = (
  translations: RealtimeState["translationsByMessage"]
): RealtimeState["translationsByMessage"] => {
  const keys = Object.keys(translations);
  if (keys.length <= PERSISTED_TRANSLATIONS_LIMIT) {
    return translations;
  }
  const kept = keys.slice(keys.length - PERSISTED_TRANSLATIONS_LIMIT);
  const next: RealtimeState["translationsByMessage"] = {};
  for (const key of kept) {
    next[key] = translations[key]!;
  }
  return next;
};

// Вызывается на каждый set() стора (partialize), поэтому обязан быть O(1):
// только ссылки на иммутабельные срезы, без обходов. Ограничение переводов
// (boundTranslations) применяется позже, при отложенной сериализации в
// createThrottledIDBStorage.
const buildPersistedSnapshot = (state: RealtimeState): PersistedRealtimeState => ({
  accountId: state.accountId,
  draftsByChat: state.draftsByChat,
  activeDialogListId: state.activeDialogListId,
  translatedChats: state.translatedChats,
  translationLanguageByChat: state.translationLanguageByChat,
  outgoingLanguageByChat: state.outgoingLanguageByChat,
  outgoingLanguageByAccount: state.outgoingLanguageByAccount,
  translationsByMessage: state.translationsByMessage
});

// Ключ перевода: ОБЯЗАТЕЛЬНО chatId+messageId. TDLib message id уникален
// только внутри чата — ключ по одному messageId подставлял перевод из чужого
// чата («перевод не имеет ничего общего с оригиналом», багрепорт 2026-08-10).
export const translationKey = (chatId: string, messageId: string): string =>
  `${chatId}:${messageId}`;

// Дефолт целевого языка авто-перевода, если для чата не выбран свой и у
// аккаунта нет своего дефолта в настройках переводчика.
const TRANSLATION_TARGET_LANGUAGE = "ru";

// Язык по умолчанию для аккаунта: привязка из настроек переводчика
// (translator.accountBindings) или общий "ru".
export const resolveAccountDefaultTranslationLanguage = (state: {
  accountId: string | null;
  translatorBindings: Record<string, { defaultLanguage?: string }>;
}): string => {
  const binding = state.accountId ? state.translatorBindings[state.accountId] : undefined;
  return binding?.defaultLanguage?.trim().toLowerCase() || TRANSLATION_TARGET_LANGUAGE;
};

export const resolveChatTranslationLanguage = (state: RealtimeState, chatId: string): string =>
  state.translationLanguageByChat[chatId]?.trim().toLowerCase() ||
  resolveAccountDefaultTranslationLanguage(state);

// Язык, НА который переводится исходящий черновик. Это НЕ язык чтения:
// саппорт читает входящие по-русски (translationLanguageByChat), а клиенту
// пишет на его языке. Порядок: явный выбор по чату → первый язык привязки
// аккаунта, отличный от языка чтения → первый такой язык из общего каталога.
// Пусто = переводить некуда (кнопка в композере остаётся выключенной).
export const resolveOutgoingLanguage = (state: RealtimeState, chatId: string | null): string => {
  const explicit = chatId ? state.outgoingLanguageByChat[chatId]?.trim().toLowerCase() : "";
  if (explicit) {
    return explicit;
  }
  const reading = chatId
    ? resolveChatTranslationLanguage(state, chatId)
    : resolveAccountDefaultTranslationLanguage(state);
  const remembered = state.accountId
    ? state.outgoingLanguageByAccount[state.accountId]?.trim().toLowerCase()
    : "";
  if (remembered) {
    return remembered;
  }
  const binding = state.accountId ? state.translatorBindings[state.accountId] : undefined;
  const bound = (binding?.languages ?? []).map((code) => code.trim().toLowerCase());
  const fromBinding = bound.find((code) => code.length > 0 && code !== reading);
  if (fromBinding) {
    return fromBinding;
  }
  // Из общего каталога язык наугад НЕ берём: там лежат и служебные записи
  // («тест»), и языки чужих гео — отправить клиенту не тот язык хуже, чем
  // попросить саппорта выбрать его один раз. Исключение — когда кандидат
  // ровно один: выбирать не из чего.
  const candidates = state.translatorLanguages
    .map((language) => language.code.trim().toLowerCase())
    .filter((code) => code.length > 0 && code !== reading);
  return candidates.length === 1 ? candidates[0]! : "";
};

// Spacing between batch translate requests when translating a whole
// chat, so we don't flood the gateway / LLM provider with a burst.
// Сколько сообщений уходит в один LLM-вызов (/api/translate c texts[]).
// Совпадает с cap эндпоинта (20) — меньше запросов на тот же объём.
const CHAT_TRANSLATE_BATCH_SIZE = 20;
// Сколько батчей держим в полёте ОДНОВРЕМЕННО на чат. Раньше батчи шли строго
// по одному (fetch → пауза → следующий), и чат из 100 сообщений превращался в
// ~10 последовательных LLM-роундтрипов (десятки секунд). При 3 в полёте это
// ~2 волны — кратно быстрее. Cap защищает от 429-штормов у провайдера.
const CHAT_TRANSLATE_MAX_INFLIGHT = 3;
// Первые батчи слива — маленькие: LLM отвечает пропорционально объёму, и
// пачка из 20 длинных сообщений держала экран без перевода 10–20 с. Пока
// сначала уходят 5, потом 10 свежих (то, что видно внизу), остальная история
// добирается полными батчами параллельно.
const CHAT_TRANSLATE_WARMUP_BATCH_SIZES = [5, 10];

// Удалённые (tombstone в ленте) переводим только если включена опция
// «Переводить удалённые» в настройках оформления (ui-prefs).
const isTranslatableMessage = (message: MessageViewModel): boolean =>
  (!message.isDeleted || useUiPrefs.getState().translateDeleted) &&
  translationSourceText(message.content).length > 0;

// Русские оригиналы отправленных переводов: ключ — чат + отправленный текст.
const OUTGOING_ORIGINALS_MAX = 50;
const outgoingOriginals = new Map<string, string>();
const outgoingOriginalKey = (chatId: string, text: string): string => `${chatId}\n${text}`;
/**
 * Сколько сообщений чата ещё НЕ переведено (для кнопки «Добрать · N»).
 * Считает загруженные сообщения, которых нет в кэше переводов. Тот же
 * критерий, что и в очереди перевода (isTranslatableMessage).
 */
export const selectUntranslatedCount = (state: RealtimeState, chatId: string): number => {
  const messages = state.messagesByChat[chatId] ?? [];
  let count = 0;
  for (const message of messages) {
    if (
      isTranslatableMessage(message) &&
      !state.translationsByMessage[translationKey(chatId, message.id)]
    ) {
      count += 1;
    }
  }
  return count;
};

const chatTranslateQueues = new Map<string, string[]>();
const chatTranslateDraining = new Set<string>();

// Shadow-кэш переводов, переживающий выключение «Перевести весь чат»:
// при disable активные переводы копируются сюда, при повторном enable —
// восстанавливаются (если язык тот же), чтобы НЕ обращаться к переводчику
// заново (ТЗ). Ключ — translationKey(chatId, messageId). In-memory (на сессию).
const translationShadowCache = new Map<
  string,
  { text: string; entities?: MessageTextEntity[]; targetLanguage: string }
>();

// ---- Страховочная сверка переводов ----------------------------------------
// Сообщение может остаться без перевода не только «потому что нечего
// переводить»: запрос к провайдеру мог вернуть ошибку (её мы гасили молча),
// результат мог быть отброшен из-за смены языка чата на лету, а исходящее,
// отправленное при открытом чате, в очередь вообще не попадает. Каждый такой
// случай оставлял дыру в переводе до переоткрытия чата — «внутри чата
// выборочно». После того как очередь опустела, сверяем ленту и добираем
// пропущенное. Число попыток на сообщение ограничено, иначе сверка вечно
// крутилась бы на том, что упорно не переводится.
const TRANSLATION_MAX_ATTEMPTS = 3;
const TRANSLATION_SWEEP_DELAY_MS = 6_000;
// Пауза перед повтором пачки, не доехавшей до шлюза.
const TRANSLATION_RETRY_DELAY_MS = 1_500;
const translationAttempts = new Map<string, number>();
const translationSweepTimers = new Map<string, number>();
// Переводы, скрытые пользователем кнопкой «скрыть»: сверка не должна
// возвращать их обратно.
const translationsHiddenByUser = new Set<string>();

const canAttemptTranslation = (chatId: string, messageId: string): boolean =>
  (translationAttempts.get(translationKey(chatId, messageId)) ?? 0) < TRANSLATION_MAX_ATTEMPTS;

const noteTranslationAttempt = (chatId: string, messageId: string): void => {
  const key = translationKey(chatId, messageId);
  translationAttempts.set(key, (translationAttempts.get(key) ?? 0) + 1);
};

const forgetTranslationAttempts = (chatId: string, messageIds: string[]): void => {
  for (const messageId of messageIds) {
    translationAttempts.delete(translationKey(chatId, messageId));
  }
};

// Сброс счётчиков и меток чата (выключили перевод / сменили язык).
const resetChatTranslationBookkeeping = (chatId: string): void => {
  const prefix = `${chatId}:`;
  for (const key of [...translationAttempts.keys()]) {
    if (key.startsWith(prefix)) {
      translationAttempts.delete(key);
    }
  }
  for (const key of [...translationsHiddenByUser]) {
    if (key.startsWith(prefix)) {
      translationsHiddenByUser.delete(key);
    }
  }
  const timer = translationSweepTimers.get(chatId);
  if (timer !== undefined && typeof window !== "undefined") {
    window.clearTimeout(timer);
    translationSweepTimers.delete(chatId);
  }
};

const scheduleTranslationSweep = (
  getState: () => RealtimeState,
  chatId: string
): void => {
  if (typeof window === "undefined" || translationSweepTimers.has(chatId)) {
    return;
  }
  const timer = window.setTimeout(() => {
    translationSweepTimers.delete(chatId);
    const state = getState();
    if (!state.translatedChats[chatId]) {
      return;
    }
    // reverse: как и везде — снизу вверх, свежие сообщения раньше истории.
    const missing = (state.messagesByChat[chatId] ?? [])
      .filter(
        (message) =>
          isTranslatableMessage(message) &&
          !state.translationsByMessage[translationKey(chatId, message.id)] &&
          !translationsHiddenByUser.has(translationKey(chatId, message.id)) &&
          canAttemptTranslation(chatId, message.id)
      )
      .map((message) => message.id)
      .reverse();
    if (missing.length > 0) {
      enqueueChatTranslation(getState, chatId, missing);
    }
  }, TRANSLATION_SWEEP_DELAY_MS);
  translationSweepTimers.set(chatId, timer);
};

// Drains queued message ids for a chat one at a time, re-checking the live
// store before each send so toggling translation off cancels the rest.
// Язык НЕ кэшируется в drain-замыкании: на каждом шаге берётся актуальный
// resolveChatTranslationLanguage(state) — иначе после смены языка в UI
// очередь продолжает отправлять старый lang. Чтобы устранить race между
// set(translationLanguageByChat) и тем моментом, когда drain'у дойдёт ход,
// callsite может опционально передать `prioritizedLang` (берётся для всех
// будущих шагов этого drain'а).
const enqueueChatTranslation = (
  getState: () => RealtimeState,
  chatId: string,
  messageIds: string[],
  options?: { front?: boolean }
): void => {
  if (messageIds.length === 0) {
    return;
  }
  const queue = chatTranslateQueues.get(chatId) ?? [];
  const known = new Set(queue);
  const fresh: string[] = [];
  for (const id of messageIds) {
    if (!known.has(id)) {
      fresh.push(id);
      known.add(id);
    }
  }
  // front: новое входящее сообщение встаёт В НАЧАЛО очереди — иначе в
  // длинной переписке оно ждало, пока переведётся вся старая история.
  const next = options?.front ? [...fresh, ...queue] : [...queue, ...fresh];
  chatTranslateQueues.set(chatId, next);
  startChatTranslationDrain(getState, chatId);
};

// Запускает пул воркеров для перевода очереди чата с ограниченным
// параллелизмом. takeBatch синхронна (без await внутри) — в однопоточном JS
// это гарантирует, что два воркера не заберут одно и то же сообщение.
const startChatTranslationDrain = (
  getState: () => RealtimeState,
  chatId: string
): void => {
  if (chatTranslateDraining.has(chatId) || typeof window === "undefined") {
    return;
  }
  const pendingNow = chatTranslateQueues.get(chatId) ?? [];
  if (pendingNow.length === 0 || !getState().translatedChats[chatId]) {
    return;
  }
  chatTranslateDraining.add(chatId);
  let batchesTaken = 0;

  // Синхронно забирает следующий батч текстовых сообщений; медиа (без текста в
  // сторе) переводит по одному через translateMessage (worker/TDLib-фолбэк).
  const takeBatch = (): { batch: Array<{ id: string; text: string }>; lang: string } | null => {
    const state = getState();
    if (!state.translatedChats[chatId]) {
      return null;
    }
    const pending = chatTranslateQueues.get(chatId) ?? [];
    const lang = resolveChatTranslationLanguage(state, chatId);
    const batch: Array<{ id: string; text: string }> = [];
    const batchLimit = CHAT_TRANSLATE_WARMUP_BATCH_SIZES[batchesTaken] ?? CHAT_TRANSLATE_BATCH_SIZE;
    batchesTaken += 1;
    while (pending.length > 0 && batch.length < batchLimit) {
      const messageId = pending.shift()!;
      if (
        state.translationsByMessage[translationKey(chatId, messageId)] ||
        !canAttemptTranslation(chatId, messageId)
      ) {
        continue;
      }
      const message = (state.messagesByChat[chatId] ?? []).find(
        (entry) => entry.id === messageId
      );
      const text = message ? translationSourceText(message.content) : "";
      if (text.length === 0) {
        state.translateMessage(chatId, messageId, lang);
        continue;
      }
      noteTranslationAttempt(chatId, messageId);
      batch.push({ id: messageId, text });
    }
    chatTranslateQueues.set(chatId, pending);
    if (batch.length > 0) {
      return { batch, lang };
    }
    // Батч пуст, но в очереди могли остаться ещё сообщения (например, забрали
    // только медиа) — сигнализируем воркеру «продолжай», а не «стоп».
    return pending.length > 0 ? { batch: [], lang } : null;
  };

  const runBatch = async (batch: Array<{ id: string; text: string }>, lang: string): Promise<void> => {
    const state = getState();
    let status = 0;
    try {
      const apiBase = httpBaseFromWsUrl(state.wsUrl);
      const response = await fetch(`${apiBase}/api/translate`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          texts: batch.map((entry) => entry.text),
          targetLanguage: lang,
          // Аккаунт — для пер-админского резолва LLM-интеграции и языков.
          accountId: state.accountId
        })
      });
      status = response.status;
      const payload = (await response.json().catch(() => null)) as
        | { ok?: boolean; texts?: unknown }
        | null;
      if (
        !response.ok ||
        !payload?.ok ||
        !Array.isArray(payload.texts) ||
        payload.texts.length !== batch.length ||
        payload.texts.some((entry) => typeof entry !== "string")
      ) {
        throw new Error(`batch translate failed (${response.status})`);
      }
      const translatedTexts = payload.texts as string[];
      forgetTranslationAttempts(chatId, batch.map((entry) => entry.id));
      const fresh = getState();
      // Пока запрос летал, язык/режим могли смениться — устаревшие переводы
      // не сохраняем (иначе смена языка «не работает»).
      if (
        fresh.translatedChats[chatId] &&
        resolveChatTranslationLanguage(fresh, chatId) === lang
      ) {
        useRealtimeStore.setState((current) => ({
          translationsByMessage: boundMemoryTranslations(
            {
              ...current.translationsByMessage,
              ...Object.fromEntries(
                batch.map((entry, index) => [
                  translationKey(chatId, entry.id),
                  { text: translatedTexts[index]!, targetLanguage: lang }
                ])
              )
            },
            current.selectedChatId
          )
        }));
      }
    } catch {
      // 503 = LLM-интеграция не настроена/выключена: единственный рабочий
      // путь — встроенный перевод TDLib, идём по одному сообщению.
      if (status === 503) {
        const fresh = getState();
        for (const entry of batch) {
          fresh.translateMessage(chatId, entry.id, lang);
        }
        return;
      }
      // Любая другая осечка (сеть, 502, таймаут) — возвращаем пачку в НАЧАЛО
      // очереди. Раньше отсюда веером уходили 20 одиночных запросов разом:
      // провайдер отвечал 429, попытки сгорали, и сообщения оставались без
      // перевода до переоткрытия чата. Пауза — чтобы не молотить в упавший
      // сервис вплотную; счётчик попыток не даёт крутиться вечно.
      const retryable = batch
        .map((entry) => entry.id)
        .filter((id) => canAttemptTranslation(chatId, id));
      if (retryable.length === 0) {
        return;
      }
      await new Promise((resolve) => window.setTimeout(resolve, TRANSLATION_RETRY_DELAY_MS));
      if (!getState().translatedChats[chatId]) {
        return;
      }
      enqueueChatTranslation(getState, chatId, retryable, { front: true });
    }
  };

  const worker = async (): Promise<void> => {
    for (;;) {
      const next = takeBatch();
      if (next === null) {
        return;
      }
      if (next.batch.length > 0) {
        await runBatch(next.batch, next.lang);
      }
    }
  };

  const workers = Array.from({ length: CHAT_TRANSLATE_MAX_INFLIGHT }, () => worker());
  void Promise.allSettled(workers).finally(() => {
    chatTranslateDraining.delete(chatId);
    // Если во время слива в очередь долили новые сообщения (или их не успели
    // разобрать из-за смены языка) — перезапускаем слив.
    const pending = chatTranslateQueues.get(chatId) ?? [];
    if (pending.length === 0) {
      chatTranslateQueues.delete(chatId);
      // Очередь пуста — но это не значит, что переведено всё: сверяем ленту.
      scheduleTranslationSweep(getState, chatId);
    } else if (getState().translatedChats[chatId]) {
      startChatTranslationDrain(getState, chatId);
    }
  });
};

// Эвикция ленты покинутого чата: за смену саппорт открывает сотни чатов, и
// без обрезки messagesByChat рос без предела (каждая открытая лента + все
// докачанные страницы истории оставались в памяти навсегда). При уходе из
// чата оставляем последние MESSAGE_TRIM_KEEP сообщений; гистерезис
// (THRESHOLD > KEEP), чтобы не пересоздавать массив на каждом переключении
// туда-обратно. reachedStart сбрасываем: старые страницы снова «есть на
// сервере» и догрузятся при повторном открытии.
const MESSAGE_TRIM_KEEP = 150;
const MESSAGE_TRIM_THRESHOLD = 300;

const trimLeftChatHistory = (
  state: RealtimeState,
  nextChatId: string | null
): Partial<RealtimeState> => {
  const prevChatId = state.selectedChatId;
  if (!prevChatId || prevChatId === nextChatId) {
    return {};
  }
  const messages = state.messagesByChat[prevChatId];
  if (!messages || messages.length <= MESSAGE_TRIM_THRESHOLD) {
    return {};
  }
  return {
    messagesByChat: {
      ...state.messagesByChat,
      [prevChatId]: messages.slice(-MESSAGE_TRIM_KEEP)
    },
    historyReachedStartByChat: {
      ...state.historyReachedStartByChat,
      [prevChatId]: false
    }
  };
};

// Обрезки покинутой ленты мало: она оставляет по 150 сообщений НА КАЖДЫЙ
// чат, а при массовой подготовке пушей саппорт за раз проходит сотни чатов —
// вкладка растёт линейно и в какой-то момент падает. Держим ленты только
// последних FEED_LRU_LIMIT открытых чатов, остальные выгружаем целиком:
// история перезапросится при следующем открытии (флаг historyLoadedByChat
// снят). Черновики, чаты и папки живут отдельно и не страдают.
const FEED_LRU_LIMIT = 40;
const recentFeedChatIds: string[] = [];

const touchFeedChat = (chatId: string): void => {
  const at = recentFeedChatIds.indexOf(chatId);
  if (at >= 0) {
    recentFeedChatIds.splice(at, 1);
  }
  recentFeedChatIds.unshift(chatId);
};

const evictColdChatFeeds = (
  state: RealtimeState,
  keepChatId: string
): Partial<RealtimeState> => {
  if (recentFeedChatIds.length <= FEED_LRU_LIMIT) {
    return {};
  }
  const cold = recentFeedChatIds
    .slice(FEED_LRU_LIMIT)
    .filter(
      (chatId) =>
        chatId !== keepChatId &&
        // Догрузка в полёте: выгрузить сейчас — значит принять её ответ в
        // пустую ленту и оставить чат с куском истории посередине.
        !state.historyLoadingByChat[chatId] &&
        state.messagesByChat[chatId] !== undefined
    );
  recentFeedChatIds.length = Math.min(recentFeedChatIds.length, FEED_LRU_LIMIT);
  if (cold.length === 0) {
    return {};
  }
  const messagesByChat = { ...state.messagesByChat };
  const historyLoadedByChat = { ...state.historyLoadedByChat };
  const historyReachedStartByChat = { ...state.historyReachedStartByChat };
  const scheduledByChat = { ...state.scheduledByChat };
  const scheduledLoadingByChat = { ...state.scheduledLoadingByChat };
  const searchStateByChat = { ...state.searchStateByChat };
  for (const chatId of cold) {
    delete messagesByChat[chatId];
    delete historyLoadedByChat[chatId];
    delete historyReachedStartByChat[chatId];
    delete scheduledByChat[chatId];
    delete scheduledLoadingByChat[chatId];
    delete searchStateByChat[chatId];
    delete searchStateByChat[mediaSearchKey(chatId)];
  }
  return {
    messagesByChat,
    historyLoadedByChat,
    historyReachedStartByChat,
    scheduledByChat,
    scheduledLoadingByChat,
    searchStateByChat
  };
};

// Обрезка покинутой ленты и LRU-выгрузка холодных — вместе и в правильном
// порядке: обе правят messagesByChat, и наивный spread второй затёр бы первую.
const applyFeedRetention = (
  state: RealtimeState,
  nextChatId: string | null
): Partial<RealtimeState> => {
  const trimmed = trimLeftChatHistory(state, nextChatId);
  if (!nextChatId) {
    return trimmed;
  }
  const evicted = evictColdChatFeeds({ ...state, ...trimmed }, nextChatId);
  return { ...trimmed, ...evicted };
};

// Reconnect с экспоненциальным backoff + jitter: фиксированные 1.5 с при
// падении gateway означали, что все вкладки (до 10 аккаунтов) долбят сервер
// синхронно, а при восстановлении шлют session.attach одновременно.
let reconnectAttempt = 0;
const RECONNECT_BASE_MS = 1_500;
const RECONNECT_MAX_MS = 30_000;
const nextReconnectDelay = (): number => {
  const expo = Math.min(
    RECONNECT_MAX_MS,
    RECONNECT_BASE_MS * 2 ** Math.min(reconnectAttempt, 5)
  );
  reconnectAttempt += 1;
  return Math.round(expo * (0.8 + Math.random() * 0.4));
};

// Сводки чатов из серверного поиска (chat.dialogsSearched /
// chat.globalMessagesSearched) → в chats и на свои места в списках.
// getChat по чату вне загруженного окна списка отдаёт пустые positions —
// это «не знаю», а не «ни в одной папке»: известные listIds/orderByList
// пустотой не затираем. Чат без lists (публичный @username, юзер без
// переписки) живёт только в результатах поиска.
const mergeSearchedDialogsIntoState = (
  state: RealtimeState,
  dialogs: DialogSummary[]
): Pick<RealtimeState, "chats" | "chatIdsByList"> => {
  if (dialogs.length === 0) {
    return { chats: state.chats, chatIdsByList: state.chatIdsByList };
  }
  const nextChats = { ...state.chats };
  let nextChatIdsByList = state.chatIdsByList;
  for (const dialog of dialogs) {
    const existing = state.chats[dialog.chatId];
    const merged = mergeDialogSummary(existing, dialog);
    nextChats[dialog.chatId] =
      dialog.lists.length === 0 && existing && existing.listIds.length > 0
        ? {
            ...merged,
            listIds: existing.listIds,
            ...(existing.orderByList ? { orderByList: existing.orderByList } : {})
          }
        : merged;
  }
  for (const dialog of dialogs) {
    if (dialog.lists.length > 0) {
      nextChatIdsByList = mergeSingleChatIntoLists(nextChatIdsByList, dialog, nextChats);
    }
  }
  return { chats: nextChats, chatIdsByList: nextChatIdsByList };
};

export const useRealtimeStore = create<RealtimeState>()(persist((set, get) => ({
  connectionStatus: "disconnected",
  accountId: "",
  connectionId: generateId(),
  wsUrl: defaultWsUrl(),
  notificationPermission: getNotificationPermission(),
  notificationsEnabled: getNotificationEnabled(),
  shouldReconnect: false,
  heartbeatIntervalId: null,
  reconnectTimeoutId: null,
  dialogLists: {},
  dialogListOrder: [DEFAULT_DIALOG_LIST_ID],
  dialogListStateById: {},
  dialogRequestedLimitById: {},
  dialogHasMoreById: {},
  chatIdsByList: {},
  activeDialogListId: DEFAULT_DIALOG_LIST_ID,
  selectedChatId: null,
  pageActive: readPageActive(),
  pendingScrollMessageId: null,
  chats: {},
  pinnedMessageByChat: {},
  unreadTotals: { total: 0, byList: {}, chatsByList: {}, unmutedChatsByList: {} },
  serverUnreadByList: {},
  quickReplies: [],
  draftsByChat: {},
  scheduledByChat: {},
  scheduledLoadingByChat: {},
  messagesByChat: {},
  searchStateByChat: {},
  historyLoadingByChat: {},
  historyLoadedByChat: {},
  historyReachedStartByChat: {},
  translationsByMessage: {},
  translatedChats: {},
  translationLanguageByChat: {},
  outgoingLanguageByChat: {},
  outgoingLanguageByAccount: {},
  forwardComposition: null,
  profilesByChat: {},
  forumTopicsByChat: {},
  stickerSets: [],
  stickerSetsLoading: false,
  loadStickerSets: () => {
    const state = get();
    if (state.stickerSetsLoading) {
      return;
    }
    set({ stickerSetsLoading: true });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "stickers.loadSets"
      })
    });
  },
  activeForumTopicByChat: {},
  botReplyKeyboardByChat: {},
  botCallbackToasts: [],
  pendingCall: null,
  selfIdentity: null,
  selfProfileOpen: false,
  appRole: null,
  openSelfProfile: () => set({ selfProfileOpen: true }),
  closeSelfProfile: () => set({ selfProfileOpen: false }),
  requestSelfIdentity: () => {
    const state = get();
    if (!state.accountId) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "account.requestIdentity"
      })
    });
  },
  setAppRole: (role) => set({ appRole: role }),
  appUserId: null,
  setAppUserId: (userId) => set({ appUserId: userId }),
  clearBotReplyKeyboard: (chatId) => {
    set((state) => {
      if (!state.botReplyKeyboardByChat[chatId]) return state;
      const next = { ...state.botReplyKeyboardByChat };
      delete next[chatId];
      return { botReplyKeyboardByChat: next };
    });
  },
  pressInlineButton: (chatId, messageId, data) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.pressInlineButton",
        chatId,
        messageId,
        data
      })
    });
  },
  placeCall: (chatId) => {
    const state = get();
    if (!chatId) {
      return;
    }
    const chatTitle = state.chats[chatId]?.title ?? chatId;
    set({
      pendingCall: {
        chatId,
        chatTitle,
        startedAt: new Date().toISOString()
      }
    });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.placeCall",
        chatId
      })
    });
  },
  dismissPendingCall: () => {
    set({ pendingCall: null });
  },
  listForumTopics: (chatId) => {
    const state = get();
    if (!chatId) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.listForumTopics",
        chatId
      })
    });
  },
  setActiveForumTopic: (chatId, threadId) => {
    // У каждой темы — своя лента сообщений на стороне TDLib
    // (getForumTopicHistory). Сбрасываем уже загруженные сообщения и
    // флаг historyLoadedByChat, чтобы при следующем рендере loadHistory
    // дернулся с новым messageThreadId. Иначе при переключении тем
    // фронт просто фильтровал старый общий слой и в большинстве тем
    // выводил пустоту или сообщения из General.
    set((current) => {
      const previousThread = current.activeForumTopicByChat[chatId] ?? null;
      if (previousThread === threadId) {
        return current;
      }
      const nextMessagesByChat = { ...current.messagesByChat };
      delete nextMessagesByChat[chatId];
      const nextHistoryLoaded = { ...current.historyLoadedByChat };
      delete nextHistoryLoaded[chatId];
      const nextHistoryReached = { ...current.historyReachedStartByChat };
      delete nextHistoryReached[chatId];
      return {
        activeForumTopicByChat: {
          ...current.activeForumTopicByChat,
          [chatId]: threadId
        },
        messagesByChat: nextMessagesByChat,
        historyLoadedByChat: nextHistoryLoaded,
        historyReachedStartByChat: nextHistoryReached
      };
    });
    // Подгружаем историю темы (или общий слой, если threadId=null —
    // юзер вернулся на «Все темы»).
    const state = get();
    if (state.selectedChatId === chatId) {
      state.loadHistory(chatId, 80, threadId);
    }
  },
  dismissBotCallbackToast: (id) => {
    set((current) => ({
      botCallbackToasts: current.botCallbackToasts.filter((toast) => toast.id !== id)
    }));
  },
  dismissError: (id) => {
    set((current) => ({
      errors: current.errors.filter((entry) => entry.id !== id)
    }));
  },
  dismissAllErrors: () => {
    set({ errors: [] });
  },
  errors: [],
  socket: null,
  connect: (accountId, wsUrl) => {
    const currentState = get();
    const currentSocket = currentState.socket;
    if (currentState.heartbeatIntervalId !== null) {
      window.clearInterval(currentState.heartbeatIntervalId);
    }
    if (currentState.reconnectTimeoutId !== null) {
      window.clearTimeout(currentState.reconnectTimeoutId);
    }
    if (currentSocket) {
      currentSocket.close();
    }

    const connectionId = generateId();
    const targetUrl = wsUrl && wsUrl.trim().length > 0 ? wsUrl : defaultWsUrl();
    const socket = new WebSocket(targetUrl);
    const preserveState = currentState.accountId === accountId;
    const draftsByChat = preserveState
      ? currentState.draftsByChat
      : readDraftsByChat(accountId);

    set({
      accountId,
      connectionId,
      wsUrl: targetUrl,
      notificationPermission: getNotificationPermission(),
      connectionStatus: "connecting",
      shouldReconnect: true,
      heartbeatIntervalId: null,
      reconnectTimeoutId: null,
      dialogLists: preserveState ? currentState.dialogLists : {},
      dialogListOrder: preserveState ? currentState.dialogListOrder : [DEFAULT_DIALOG_LIST_ID],
      dialogListStateById: preserveState ? currentState.dialogListStateById : {},
      dialogRequestedLimitById: preserveState ? currentState.dialogRequestedLimitById : {},
      dialogHasMoreById: preserveState ? currentState.dialogHasMoreById : {},
      chatIdsByList: preserveState ? currentState.chatIdsByList : {},
      activeDialogListId: preserveState
        ? currentState.activeDialogListId
        : DEFAULT_DIALOG_LIST_ID,
      selectedChatId: preserveState ? currentState.selectedChatId : null,
      chats: preserveState ? currentState.chats : {},
      serverUnreadByList: preserveState ? currentState.serverUnreadByList : {},
      quickReplies: preserveState ? currentState.quickReplies : [],
      pinnedMessageByChat: preserveState ? currentState.pinnedMessageByChat : {},
      draftsByChat,
      messagesByChat: preserveState ? currentState.messagesByChat : {},
      searchStateByChat: preserveState ? currentState.searchStateByChat : {},
      historyLoadingByChat: {},
      // Флаг «история загружена» НЕ переживает переподключение: за время
      // разрыва могли потеряться messageUpserted, и следующий selectChat
      // должен дотянуть хвост (сообщения при этом сохраняем и мержим).
      historyLoadedByChat: {},
      historyReachedStartByChat: preserveState ? currentState.historyReachedStartByChat : {},
      translatedChats: preserveState ? currentState.translatedChats : {},
      translationLanguageByChat: preserveState ? currentState.translationLanguageByChat : {},
      outgoingLanguageByChat: preserveState ? currentState.outgoingLanguageByChat : {},
      outgoingLanguageByAccount: currentState.outgoingLanguageByAccount,
      forwardComposition: null,
      socket,
      localFolders: preserveState ? currentState.localFolders : [],
      // Подбор всегда относится к конкретному аккаунту и к открытой модалке —
      // переподключение/смена аккаунта его обнуляет.
      folderPick: null,
      chatExport: null,
      pushJobs: preserveState ? currentState.pushJobs : {},
      folderLines: preserveState ? currentState.folderLines : null,
      dialogSearch: EMPTY_DIALOG_SEARCH,
      messageSearch: EMPTY_MESSAGE_SEARCH,
      errors: preserveState ? currentState.errors : []
    });

    // Смена аккаунта в этом окне: подтягиваем персистнутый снапшот нового
    // аккаунта (папка, кэш переводов) — ключ IDB теперь по аккаунту.
    if (!preserveState) {
      void useRealtimeStore.persist.rehydrate();
    }
    // Подгружаем локальные папки сразу. До этого момента WS ещё не открыт,
    // но HTTP API доступен по session cookie. Если запрос упадёт (например,
    // 401), refreshLocalFolders сам положит ошибку в errors[].
    void get().refreshLocalFolders();
    // Частые группы приходят с сервера тем же способом (нужны и саппорту —
    // у него это единственные разрешённые адресаты пересылки чужих сообщений).
    void get().refreshFrequentChats();
    // Папки по линиям — привязки и линия юзера (фильтр вкладок папок).
    void get().refreshFolderLines();
    // Депозиты клиентов для бейджей статусов (дальше — дельты по WS).
    void get().refreshClientDeposits();
    // Языки переводчика + привязки аккаунтов (селектор в композере).
    void get().refreshTranslatorConfig();

    socket.onopen = () => {
      if (get().socket !== socket) {
        return;
      }

      socketSend(socket, {
        type: "session.attach",
        accountId: accountId as CommandEnvelope["accountId"],
        connectionId: connectionId as CommandEnvelope["connectionId"]
      });
      // Отправки, скопившиеся в WS-closed окне (реконнект после slow-consumer
      // close, кратковременная потеря сети) — отдаём сразу после attach, до
      // loadHistory, чтобы саппортские сообщения не оказались позже истории.
      flushSendOutbox(socket);
      // Открытый чат после реконнекта того же аккаунта: перечитываем хвост
      // истории сразу — пропущенные за разрыв сообщения иначе не появятся.
      if (preserveState) {
        const openChatId = get().selectedChatId;
        if (openChatId && !get().historyLoadingByChat[openChatId]) {
          get().loadHistory(openChatId, 80);
        }
      }
      const heartbeatIntervalId = window.setInterval(() => {
        if (get().socket === socket) {
          socketSend(socket, { type: "ping" });
        }
      }, 20_000);
      reconnectAttempt = 0;
      set({
        connectionStatus: "connected",
        heartbeatIntervalId,
        reconnectTimeoutId: null
      });
    };

    socket.onmessage = (event) => {
      if (get().socket !== socket) {
        return;
      }

      try {
        const parsed = JSON.parse(event.data as string) as ServerSocketMessage;
        get().applySocketMessage(parsed);
      } catch {
        set((state) => ({
          errors: pushError(state.errors, "Получено некорректное сообщение от сервера")
        }));
      }
    };

    socket.onclose = () => {
      if (get().socket !== socket) {
        return;
      }

      const state = get();
      if (state.heartbeatIntervalId !== null) {
        window.clearInterval(state.heartbeatIntervalId);
      }

      let reconnectTimeoutId: number | null = null;
      if (state.shouldReconnect && state.accountId === accountId) {
        reconnectTimeoutId = window.setTimeout(() => {
          const latestState = get();
          if (
            latestState.shouldReconnect &&
            latestState.accountId === accountId &&
            latestState.connectionStatus === "disconnected"
          ) {
            latestState.connect(accountId, targetUrl);
          }
        }, nextReconnectDelay());
      }

      set({
        connectionStatus: "disconnected",
        socket: null,
        heartbeatIntervalId: null,
        reconnectTimeoutId
      });
    };

    socket.onerror = () => {
      if (get().socket !== socket) {
        return;
      }

      set((state) => ({
        errors: pushError(
          state.errors,
          "Соединение с сервером прервано. Пробуем переподключиться…"
        )
      }));
    };
  },
  disconnect: () => {
    const state = get();
    if (state.heartbeatIntervalId !== null) {
      window.clearInterval(state.heartbeatIntervalId);
    }
    if (state.reconnectTimeoutId !== null) {
      window.clearTimeout(state.reconnectTimeoutId);
    }
    const socket = state.socket;
    set({
      shouldReconnect: false,
      heartbeatIntervalId: null,
      reconnectTimeoutId: null
    });
    socket?.close();
    set({ connectionStatus: "disconnected", socket: null });
  },
  setActiveDialogList: (listId) => {
    set({ activeDialogListId: listId, selectedChatId: null });
    const state = get();
    // local:* папки не серверные — listDialogs им не нужен. Зато могут
    // содержать чаты, которые не в state.chats (не попали в prefetch
    // main). Дёргаем chat.resolveKnown для них.
    if (listId.startsWith("local:")) {
      const folderId = listId.slice("local:".length);
      const folder = state.localFolders.find(
        (entry) => entry.folderId === folderId
      );
      if (folder && folder.includedChatIds.length > 0) {
        state.resolveKnownChats(folder.includedChatIds);
      }
      return;
    }
    const dialogListState = state.dialogListStateById[listId];
    if (!dialogListState?.isLoaded && !dialogListState?.isLoading) {
      folderLastRefreshAt.set(listId, Date.now());
      state.listDialogs(INITIAL_DIALOG_LIMIT, listId);
      return;
    }
    // Уже загруженную папку/архив при открытии перезапрашиваем снапшотом
    // (не чаще раза в минуту): текущий список остаётся на экране,
    // dialogsListed заменит его без прыжков (mergeChatsIntoLists). Main не
    // трогаем — у него полное живое покрытие событиями.
    if (
      dialogListState?.isLoaded &&
      !dialogListState.isLoading &&
      state.dialogLists[listId]?.kind !== "main"
    ) {
      const lastAt = folderLastRefreshAt.get(listId) ?? 0;
      if (Date.now() - lastAt >= FOLDER_REFRESH_MIN_INTERVAL_MS) {
        folderLastRefreshAt.set(listId, Date.now());
        const currentCount = state.chatIdsByList[listId]?.length ?? 0;
        state.listDialogs(
          Math.min(
            Math.max(INITIAL_DIALOG_LIMIT, currentCount),
            FOLDER_REFRESH_MAX_LIMIT
          ),
          listId
        );
      }
    }
  },
  setPageActive: (active) => {
    if (get().pageActive === active) {
      return;
    }
    set((state) => {
      const chatId = state.selectedChatId;
      const chat = active && chatId ? state.chats[chatId] : undefined;
      // Вернулся к открытому чату — он снова «на глазах»: гасим бейдж как
      // при selectChat (markRead отправит ChatView, он тоже ждёт pageActive).
      if (!chat || !chatId || chat.unreadCount === 0) {
        return { pageActive: active };
      }
      const nextChat = { ...chat, unreadCount: 0 };
      return {
        pageActive: active,
        chats: { ...state.chats, [chatId]: nextChat },
        serverUnreadByList: adjustServerUnreadForChat(state.serverUnreadByList, chat, nextChat)
      };
    });
  },
  selectChat: (chatId) => {
    // Помним прежний флаг «помечен непрочитанным», чтобы после set() решить,
    // нужно ли отправить toggleMarkedAsUnread(false) на сервер.
    const wasMarkedAsUnread = chatId
      ? Boolean(get().chats[chatId]?.isMarkedAsUnread)
      : false;

    set((state) => {
      if (!chatId) {
        return {
          selectedChatId: null,
          ...trimLeftChatHistory(state, null)
        };
      }

      touchFeedChat(chatId);
      const chat = withChat(state, chatId);
      return {
        selectedChatId: chatId,
        ...applyFeedRetention(state, chatId),
        chats: {
          ...state.chats,
          [chatId]: {
            ...chat,
            unreadCount: 0,
            isMarkedAsUnread: false,
            isTyping: false
          }
        }
      };
    });

    if (!chatId) {
      return;
    }

    const state = get();
    // Ленивая подгрузка профиля при открытии личного чата — ради «Страны
    // телефона» в шапке (как в оригинальном TG). Один запрос на чат за
    // сессию: profileLoaded смержится в profilesByChat и переживёт
    // переключения чатов.
    {
      const chat = state.chats[chatId];
      if (
        chat &&
        !chat.isGroupChat &&
        !state.profilesByChat[chatId] &&
        !profileAutoRequested.has(chatId)
      ) {
        profileAutoRequested.add(chatId);
        state.requestProfile(chatId);
      }
    }
    // Если чат был отмечен пользователем как «непрочитанный» — снимаем флаг
    // в TDLib. Отдельная команда (chat.toggleMarkedAsUnread), потому что
    // viewMessages этот флаг не трогает.
    if (wasMarkedAsUnread) {
      state.toggleMarkedAsUnread(chatId, false);
    }
    // Открытие чата — момент, когда саппорт увидит дыры в переводе. Добираем
    // пропущенное сразу: попытки могли сгореть на упавшем провайдере, а
    // сообщение — приехать в чат, пока перевод был выключен.
    if (state.translatedChats[chatId]) {
      state.retranslateMissing(chatId);
    }
    if (!state.historyLoadedByChat[chatId] && !state.historyLoadingByChat[chatId]) {
      state.loadHistory(chatId, 80);
    } else if (!state.historyLoadingByChat[chatId] && hasHistoryTailGap(state, chatId)) {
      // История уже была загружена, но карточка диалога знает более свежее
      // сообщение, чем лента — дотягиваем хвост (см. hasHistoryTailGap).
      state.loadHistory(chatId, 80);
    }
  },
  requestScrollToMessage: (chatId, messageId) => {
    const state = get();
    if (state.selectedChatId !== chatId) {
      state.selectChat(chatId);
    }
    set({ pendingScrollMessageId: messageId });
  },
  consumePendingScrollMessage: () => {
    set({ pendingScrollMessageId: null });
  },
  setDraft: (chatId, text) => {
    const state = get();
    if (!chatId) {
      return;
    }

    const normalizedText = text.replace(/\r\n/g, "\n");
    const nextDraftsByChat = { ...state.draftsByChat };
    if (normalizedText.trim().length === 0) {
      // Ручная очистка поля — та же ситуация, что и после отправки: помечаем
      // стёртый текст, чтобы его эхо от TDLib не вернуло черновик обратно.
      stampDraftCleared(chatId, state.draftsByChat[chatId] ?? "");
      delete nextDraftsByChat[chatId];
    } else {
      nextDraftsByChat[chatId] = normalizedText;
    }

    const unchanged =
      state.draftsByChat[chatId] === nextDraftsByChat[chatId] &&
      Object.keys(state.draftsByChat).length === Object.keys(nextDraftsByChat).length;
    if (unchanged) {
      return;
    }

    scheduleDraftPersist(state.accountId, nextDraftsByChat);
    set({
      draftsByChat: nextDraftsByChat
    });
    scheduleDraftPush(chatId, normalizedText, () => get());
  },
  clearDraft: (chatId) => {
    const state = get();
    if (!chatId) {
      return;
    }
    stampDraftCleared(chatId, state.draftsByChat[chatId] ?? "");
    // Пустой draft шлём на сервер ВСЕГДА, даже если локально его уже нет:
    // черновик мог остаться в TDLib (набран в другой вкладке/на телефоне,
    // либо debounce-push успел уйти перед отправкой). Иначе сервер продолжит
    // рассылать его в dialogUpdated — это и есть «фантомный черновик».
    flushDraftPush(chatId, "", () => get());
    if (!state.draftsByChat[chatId]) {
      return;
    }

    const nextDraftsByChat = { ...state.draftsByChat };
    delete nextDraftsByChat[chatId];
    // Очистку пишем сразу и гасим отложенную запись: иначе pending-таймер
    // через 400мс воскресил бы только что стёртый черновик в localStorage.
    cancelScheduledDraftPersist();
    writeDraftsByChat(state.accountId, nextDraftsByChat);
    set({
      draftsByChat: nextDraftsByChat
    });
  },
  sendText: (chatId, text) => {
    const trimmedText = text.trim();
    if (trimmedText.length === 0) {
      return;
    }

    const parsed = hasMarkdownSyntax(trimmedText)
      ? parseMarkdown(trimmedText)
      : { text: trimmedText, entities: [] };

    get().sendMessage(chatId, {
      kind: "text",
      text: parsed.text,
      ...(parsed.entities.length > 0 ? { entities: parsed.entities } : {})
    });
  },
  sendMessage: (chatId, content, replyToMessageId, scheduledAt, trustAssetId, options) => {
    const state = get();
    if (!chatId) {
      return;
    }

    const localId = options?.localId ?? generateId();
    const whenOnline = options?.whenOnline === true;
    const isScheduled =
      whenOnline ||
      Boolean(scheduledAt && new Date(scheduledAt).getTime() > Date.now() + 1000);

    const activeTopic = state.activeForumTopicByChat[chatId];
    // Снапшот названия чата — display-only, для журнала медиа-отправок
    // саппортов в админке (gateway пишет его в app_support_media_log).
    const chatTitle = state.chats[chatId]?.title;
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.sendMessage",
      chatId,
      localId,
      ...(replyToMessageId ? { replyToMessageId } : {}),
      ...(isScheduled && scheduledAt && !whenOnline ? { scheduledAt } : {}),
      ...(whenOnline ? { scheduleWhenOnline: true } : {}),
      ...(activeTopic ? { messageThreadId: activeTopic } : {}),
      ...(trustAssetId ? { trustAssetId } : {}),
      ...(chatTitle ? { chatTitle } : {}),
      content
    });
    // Для чисто текстового ввода повторный клик того же текста, пока прошлая
    // отправка в полёте или упала по таймауту, идёт с тем же commandId
    // (см. textSendCommandId) — дедупер воркера режет дубль.
    // Для медиа content содержит File/URL и стабильный хэш не построить,
    // защиту даёт in-memory outbox + не-удаление пузыря по таймауту.
    if (content.kind === "text") {
      const stableParts = [
        state.accountId,
        chatId,
        "text",
        content.text ?? "",
        JSON.stringify(content.entities ?? []),
        replyToMessageId ?? "",
        activeTopic ?? "",
        whenOnline ? "wo" : (isScheduled && scheduledAt ? `at:${scheduledAt}` : "")
      ];
      (envelope as { commandId: string }).commandId = textSendCommandId(
        stableCommandId(stableParts),
        envelope.commandId
      ) as CommandEnvelope["commandId"];
    }
    rememberPendingSend(envelope.commandId, chatId, localId);
    enqueueCommand(state.socket, envelope);

    if (isScheduled) {
      // Отложенное в ленту не попадает — сразу показываем его на «полке»
      // отложенных, пока TDLib не пришлёт подтверждённый снимок. Если
      // плейсхолдер в ленте успели создать (медиа) — снимаем.
      set((current) => ({
        ...removeOptimisticMessage(current, chatId, localId),
        scheduledByChat: {
          ...current.scheduledByChat,
          [chatId]: appendScheduledEntry(current.scheduledByChat[chatId] ?? [], {
            id: `optimistic:${localId}`,
            content,
            text: contentToText(content),
            outgoing: true,
            isPending: true,
            ...(whenOnline
              ? { sendWhenOnline: true }
              : scheduledAt
                ? { sendAt: new Date(scheduledAt).toISOString() }
                : {})
          })
        }
      }));
      return;
    }

    // Render the outgoing message immediately. TDLib's sendMessage can block
    // for tens of seconds while it reads/uploads a video before the worker
    // replies with chat.messageAccepted, so without this the chat looks
    // frozen after pressing send. Reconciled by localId on messageAccepted,
    // then by replacesMessageId when the real message arrives.
    set((current) => {
      const existing = current.messagesByChat[chatId] ?? [];
      const prior = existing.find((entry) => entry.id === `optimistic:${localId}`);
      // Плейсхолдер медиа создан до загрузки файла с локальным превью —
      // сохраняем это превью, иначе пузырь мигнёт в «Загружаем фото…».
      const displayContent =
        prior &&
        prior.content.kind === content.kind &&
        "url" in prior.content &&
        typeof prior.content.url === "string" &&
        !("url" in content && content.url)
          ? ({ ...content, url: prior.content.url } as MessageContent)
          : content;
      const optimistic: MessageViewModel = {
        id: `optimistic:${localId}`,
        content: displayContent,
        text: contentToText(content),
        senderId: "self",
        outgoing: true,
        occurredAt: prior?.occurredAt ?? new Date().toISOString(),
        isPending: true,
        localId
      };
      return applyOptimisticMessage(current, chatId, optimistic);
    });
  },
  beginOptimisticSend: (chatId, content) => {
    const localId = generateId();
    set((current) => {
      const optimistic: MessageViewModel = {
        id: `optimistic:${localId}`,
        content,
        text: contentToText(content),
        senderId: "self",
        outgoing: true,
        occurredAt: new Date().toISOString(),
        isPending: true,
        localId
      };
      return applyOptimisticMessage(current, chatId, optimistic);
    });
    return localId;
  },
  cancelOptimisticSend: (chatId, localId) => {
    set((current) => removeOptimisticMessage(current, chatId, localId));
  },
  sendAlbum: (chatId, contents, trustAssetId, options) => {
    const state = get();
    if (!chatId || contents.length === 0) {
      return;
    }
    const activeTopic = state.activeForumTopicByChat[chatId];
    const chatTitle = state.chats[chatId]?.title;
    const localId = options?.localId ?? generateId();
    const whenOnline = options?.whenOnline === true;
    const scheduledAt = options?.scheduledAt;
    const isScheduled =
      whenOnline ||
      Boolean(scheduledAt && new Date(scheduledAt).getTime() > Date.now() + 1000);
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.sendAlbum",
      chatId,
      localId,
      contents,
      ...(activeTopic ? { messageThreadId: activeTopic } : {}),
      ...(trustAssetId ? { trustAssetId } : {}),
      ...(chatTitle ? { chatTitle } : {}),
      ...(isScheduled && scheduledAt && !whenOnline ? { scheduledAt } : {}),
      ...(whenOnline ? { scheduleWhenOnline: true } : {})
    });
    rememberPendingSend(envelope.commandId, chatId, localId);
    enqueueCommand(state.socket, envelope);
    if (isScheduled) {
      const first = contents[0]!;
      set((current) => ({
        ...removeOptimisticMessage(current, chatId, localId),
        scheduledByChat: {
          ...current.scheduledByChat,
          [chatId]: appendScheduledEntry(current.scheduledByChat[chatId] ?? [], {
            id: `optimistic:${localId}`,
            content: first,
            text:
              contents.length > 1
                ? `Альбом · ${contents.length} вложений${
                    contentToText(first) ? ` · ${contentToText(first)}` : ""
                  }`
                : contentToText(first),
            outgoing: true,
            isPending: true,
            ...(whenOnline
              ? { sendWhenOnline: true }
              : scheduledAt
                ? { sendAt: new Date(scheduledAt).toISOString() }
                : {})
          })
        }
      }));
    }
  },
  listQuickReplies: () => {
    const state = get();
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "account.listQuickReplies"
      })
    });
  },
  sendQuickReply: (chatId, shortcutId) => {
    const state = get();
    if (!chatId || !shortcutId) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.sendQuickReply",
        chatId,
        shortcutId
      })
    });
  },
  editMessage: (chatId, messageId, text, options) => {
    const state = get();
    const trimmedText = text.trim();
    if (!chatId || !messageId) {
      return;
    }
    if (trimmedText.length === 0 && !options?.content && !options?.allowEmpty) {
      return;
    }

    const chatTitle = state.chats[chatId]?.title;
    // Markdown — как при отправке (жирный, ссылки [текст](url)): раньше правка
    // уходила сырым текстом и разметка превращалась в звёздочки/скобки.
    const parsed = hasMarkdownSyntax(trimmedText)
      ? parseMarkdown(trimmedText)
      : { text: trimmedText, entities: [] };
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.editMessage",
      chatId,
      messageId,
      text: parsed.text,
      ...(parsed.entities.length > 0 ? { entities: parsed.entities } : {}),
      ...(options?.content ? { content: options.content } : {}),
      ...(chatTitle ? { chatTitle } : {})
    });
    if (options?.scheduled) {
      rememberScheduledMutation(envelope.commandId, chatId);
      set((current) => {
        const entries = current.scheduledByChat[chatId];
        if (!entries) {
          return current;
        }
        return {
          scheduledByChat: {
            ...current.scheduledByChat,
            [chatId]: entries.map((entry) =>
              entry.id === messageId ? { ...entry, isPending: true } : entry
            )
          }
        };
      });
    }
    socketSend(state.socket, { type: "command", payload: envelope });
  },
  deleteMessage: (chatId, messageId, revoke = true) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.deleteMessage",
        chatId,
        messageId,
        revoke
      })
    });
  },
  forwardMessage: (fromChatId, messageId, toChatId, options) => {
    const state = get();
    if (!fromChatId || !messageId || !toChatId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.forwardMessage",
        fromChatId,
        messageId,
        toChatId,
        ...(options?.hideSenderName ? { hideSenderName: true } : {}),
        ...(options?.hideCaption ? { hideCaption: true } : {})
      })
    });
  },
  markRead: (chatId, messageId) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.markRead",
        chatId,
        messageId
      })
    });
  },
  togglePinned: (chatId, pinned, listId) => {
    const state = get();
    if (!chatId) {
      return;
    }

    const targetListId = listId ?? state.activeDialogListId ?? DEFAULT_DIALOG_LIST_ID;
    if (isLocalDialogList(targetListId)) {
      const current = pinnedChatIdsForList(state, targetListId);
      const next = pinned
        ? current.includes(chatId)
          ? current
          : [chatId, ...current]
        : current.filter((id) => id !== chatId);
      void saveLocalFolderPins(get, set, targetListId, next);
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.togglePinned",
        chatId,
        listId: targetListId,
        pinned
      })
    });
  },
  movePinned: (chatId, direction, listId) => {
    const state = get();
    if (!chatId) {
      return;
    }
    const targetListId = listId ?? state.activeDialogListId ?? DEFAULT_DIALOG_LIST_ID;
    const pinnedOrder = pinnedChatIdsForList(state, targetListId);
    const idx = pinnedOrder.indexOf(chatId);
    if (idx < 0) {
      return;
    }
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= pinnedOrder.length) {
      return;
    }
    const next = pinnedOrder.slice();
    const tmp = next[idx]!;
    next[idx] = next[swapWith]!;
    next[swapWith] = tmp;
    if (isLocalDialogList(targetListId)) {
      void saveLocalFolderPins(get, set, targetListId, next);
      return;
    }
    const moveEnvelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.setPinnedOrder",
      listId: targetListId,
      chatIds: next
    });
    rememberPinnedOrderCommand(moveEnvelope.commandId, targetListId);
    socketSend(state.socket, { type: "command", payload: moveEnvelope });
    // Optimistic UI: пересортируем chatIdsByList локально, чтобы пользователь
    // видел перемещение мгновенно. Серверный chat.dialogUpdated с новыми
    // orderByList придёт следом и закрепит порядок.
    set((current) => {
      const currentList = current.chatIdsByList[targetListId] ?? [];
      const nonPinned = currentList.filter((id) => !pinnedOrder.includes(id));
      const reordered = [...next, ...nonPinned];
      return {
        chatIdsByList: {
          ...current.chatIdsByList,
          [targetListId]: reordered
        }
      };
    });
  },
  reorderPinned: (draggedId, insertBeforeId, listId) => {
    const state = get();
    if (!draggedId) {
      return;
    }
    const targetListId = listId ?? state.activeDialogListId ?? DEFAULT_DIALOG_LIST_ID;
    const pinnedOrder = pinnedChatIdsForList(state, targetListId);
    if (pinnedOrder.indexOf(draggedId) < 0) {
      return;
    }
    const withoutDragged = pinnedOrder.filter((id) => id !== draggedId);
    let insertIdx: number;
    if (insertBeforeId === null || insertBeforeId === draggedId) {
      insertIdx = withoutDragged.length;
    } else {
      const idx = withoutDragged.indexOf(insertBeforeId);
      insertIdx = idx < 0 ? withoutDragged.length : idx;
    }
    const next = [
      ...withoutDragged.slice(0, insertIdx),
      draggedId,
      ...withoutDragged.slice(insertIdx)
    ];
    // Если порядок не изменился — не дёргаем сервер и не пишем в стор,
    // чтобы drop в исходную позицию не вызывал зря realtime-команду.
    if (next.every((id, i) => id === pinnedOrder[i])) {
      return;
    }
    if (isLocalDialogList(targetListId)) {
      void saveLocalFolderPins(get, set, targetListId, next);
      return;
    }
    const reorderEnvelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.setPinnedOrder",
      listId: targetListId,
      chatIds: next
    });
    rememberPinnedOrderCommand(reorderEnvelope.commandId, targetListId);
    socketSend(state.socket, { type: "command", payload: reorderEnvelope });
    set((current) => {
      const currentList = current.chatIdsByList[targetListId] ?? [];
      const nonPinned = currentList.filter((id) => !pinnedOrder.includes(id));
      return {
        chatIdsByList: {
          ...current.chatIdsByList,
          [targetListId]: [...next, ...nonPinned]
        }
      };
    });
  },
  reorderDialogLists: (nextOrder) => {
    const state = get();
    const known = new Set(state.dialogListOrder);
    const cleaned = nextOrder.filter((id, index) => known.has(id) && nextOrder.indexOf(id) === index);
    for (const id of state.dialogListOrder) {
      if (!cleaned.includes(id)) {
        cleaned.push(id);
      }
    }
    // Архив всегда последний.
    const withoutArchive = cleaned.filter((id) => id !== ARCHIVE_DIALOG_LIST_ID);
    const finalOrder = known.has(ARCHIVE_DIALOG_LIST_ID)
      ? [...withoutArchive, ARCHIVE_DIALOG_LIST_ID]
      : withoutArchive;
    if (finalOrder.every((id, index) => id === state.dialogListOrder[index])) {
      return;
    }
    // TDLib-часть: порядок folder:* и позиция «Все чаты» среди них.
    const tdSequence = finalOrder.filter(
      (id) => id.startsWith("folder:") || id === DEFAULT_DIALOG_LIST_ID
    );
    const previousTdSequence = state.dialogListOrder.filter(
      (id) => id.startsWith("folder:") || id === DEFAULT_DIALOG_LIST_ID
    );
    const tdChanged =
      tdSequence.length !== previousTdSequence.length ||
      tdSequence.some((id, index) => id !== previousTdSequence[index]);
    if (tdChanged) {
      const folderIds = tdSequence.filter((id) => id.startsWith("folder:"));
      const mainIndex = tdSequence.indexOf(DEFAULT_DIALOG_LIST_ID);
      socketSend(state.socket, {
        type: "command",
        payload: buildCommandEnvelope(state.accountId, state.connectionId, {
          type: "chatFolder.reorder",
          folderIds,
          mainChatListPosition: mainIndex < 0 ? folderIds.length : mainIndex
        })
      });
    }
    // Локальная часть: позиция = индекс в общем ряду.
    const positions = finalOrder
      .map((id, index) => ({ id, index }))
      .filter((entry) => isLocalDialogList(entry.id))
      .map((entry) => ({ folderId: entry.id.slice("local:".length), position: entry.index }));
    const nextLocalFolders = state.localFolders.map((folder) => {
      const entry = positions.find((item) => item.folderId === folder.folderId);
      return entry && entry.position !== folder.position
        ? { ...folder, position: entry.position }
        : folder;
    });
    set((current) => ({
      ...mergeLocalFoldersIntoLists(current, nextLocalFolders),
      dialogListOrder: finalOrder
    }));
    if (positions.length > 0 && state.accountId) {
      void internalApi
        .reorderLocalFolders({ accountId: state.accountId, positions }, state.wsUrl)
        .catch((cause: unknown) => {
          set((current) => ({
            errors: pushError(
              current.errors,
              "Не удалось сохранить порядок папок.",
              cause instanceof Error ? cause.message : undefined
            )
          }));
          void get().refreshLocalFolders();
        });
    }
  },
  toggleArchived: (chatId, archived) => {
    const state = get();
    if (!chatId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.toggleArchived",
        chatId,
        archived
      })
    });
  },
  setChatMuted: (chatId, muted) => {
    const state = get();
    if (!chatId || !state.chats[chatId]) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.setMuted",
        chatId,
        muted
      })
    });
    // Optimistic UI: серверный chat.dialogUpdated придёт следом.
    set((current) => {
      const previous = current.chats[chatId];
      if (!previous) {
        return {};
      }
      return { chats: { ...current.chats, [chatId]: { ...previous, isMuted: muted } } };
    });
  },
  toggleMarkedAsUnread: (chatId, markedAsUnread) => {
    const state = get();
    if (!chatId) {
      return;
    }
    // Пометка открытого чата непрочитанным закрывает его (как в оригинальном
    // ТГ) — иначе selectChat при следующем входе тут же снимал бы флаг, а
    // сам чат оставался «прочитанным» визуально.
    if (markedAsUnread && state.selectedChatId === chatId) {
      state.selectChat(null);
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.toggleMarkedAsUnread",
        chatId,
        markedAsUnread
      })
    });
    // Optimistic UI: серверный chat.dialogUpdated придёт следом и переоформит флаг.
    set((current) => {
      const previous = current.chats[chatId];
      const nextChat = { ...withChat(current, chatId), isMarkedAsUnread: markedAsUnread };
      return {
        chats: {
          ...current.chats,
          [chatId]: nextChat
        },
        serverUnreadByList: adjustServerUnreadForChat(
          current.serverUnreadByList,
          previous,
          nextChat
        )
      };
    });
  },
  setListMembership: (chatId, listIds) => {
    const state = get();
    if (!chatId) {
      return;
    }
    // Оптимистичное применение: чат появляется/уходит из папки МГНОВЕННО, не
    // дожидаясь round-trip'а через воркер (на нагруженном воркере это секунды,
    // и раньше UI выглядел как «не реагирует»). Порядок в списке черновой —
    // ближайший dialogUpdated от воркера расставит по местам.
    lastOwnFolderEditAt = Date.now();
    const chat = state.chats[chatId];
    if (chat) {
      const prevListIds = chat.listIds;
      const removed = prevListIds.filter((id) => !listIds.includes(id));
      const added = listIds.filter((id) => !prevListIds.includes(id));
      if (removed.length > 0 || added.length > 0) {
        const nextChatIdsByList = { ...state.chatIdsByList };
        for (const listId of removed) {
          const existing = nextChatIdsByList[listId];
          if (existing?.includes(chatId)) {
            nextChatIdsByList[listId] = existing.filter((id) => id !== chatId);
          }
        }
        for (const listId of added) {
          const existing = nextChatIdsByList[listId] ?? [];
          if (!existing.includes(chatId)) {
            nextChatIdsByList[listId] = [chatId, ...existing];
          }
        }
        set({
          chats: { ...state.chats, [chatId]: { ...chat, listIds } },
          chatIdsByList: nextChatIdsByList
        });
      }
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.setListMembership",
        chatId,
        listIds
      })
    });
  },
  localFolders: [],
  resolveKnownChats: (chatIds) => {
    const state = get();
    if (!state.accountId || !state.socket) {
      return;
    }
    // «Missing» = чата нет в state.chats ИЛИ его listIds пусты (заглушка
    // emptyChat, созданная upsertMessage для пришедшего сообщения). Без
    // второго условия messageUpserted не сможет починить invisible chat
    // в сайдбаре — см. Bug 1/10.
    const missing = chatIds.filter((id) => {
      const existing = state.chats[id];
      return !existing || existing.listIds.length === 0;
    });
    if (missing.length === 0) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.resolveKnown",
        chatIds: missing
      })
    });
  },
  dialogSearch: EMPTY_DIALOG_SEARCH,
  searchDialogs: (rawQuery) => {
    const state = get();
    const query = rawQuery.trim();
    if (!state.accountId || !state.socket || query.length === 0) {
      return;
    }
    if (state.dialogSearch.query === query && (state.dialogSearch.loading || state.dialogSearch.requestId)) {
      return;
    }
    const requestId = generateId();
    set({ dialogSearch: { query, requestId, chatIds: [], loading: true } });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.searchDialogs",
        requestId,
        query,
        limit: 30
      })
    });
    // Ответ мог потеряться (таймаут воркера, реконнект) — не держим
    // «Ищем…» бесконечно.
    window.setTimeout(() => {
      const current = get().dialogSearch;
      if (current.requestId === requestId && current.loading) {
        set({ dialogSearch: { ...current, loading: false } });
      }
    }, 25_000);
  },
  clearDialogSearch: () => {
    if (get().dialogSearch !== EMPTY_DIALOG_SEARCH) {
      set({ dialogSearch: EMPTY_DIALOG_SEARCH });
    }
  },
  messageSearch: EMPTY_MESSAGE_SEARCH,
  searchGlobalMessages: (rawQuery) => {
    const state = get();
    const query = rawQuery.trim();
    if (!state.accountId || !state.socket || query.length === 0) {
      return;
    }
    if (state.messageSearch.query === query && (state.messageSearch.loading || state.messageSearch.requestId)) {
      return;
    }
    const requestId = generateId();
    set({ messageSearch: { ...EMPTY_MESSAGE_SEARCH, query, requestId, loading: true } });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.searchGlobalMessages",
        requestId,
        query,
        limit: 30
      })
    });
    window.setTimeout(() => {
      const current = get().messageSearch;
      if (current.requestId === requestId && current.loading) {
        set({ messageSearch: { ...current, loading: false, error: "Telegram не ответил, попробуйте ещё раз." } });
      }
    }, 25_000);
  },
  loadMoreGlobalMessages: () => {
    const state = get();
    const current = state.messageSearch;
    if (!state.accountId || !state.socket || current.loading || !current.cursor || !current.hasMore) {
      return;
    }
    const requestId = generateId();
    set({ messageSearch: { ...current, requestId, loading: true, error: null } });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.searchGlobalMessages",
        requestId,
        query: current.query,
        limit: 30,
        cursor: current.cursor
      })
    });
    window.setTimeout(() => {
      const next = get().messageSearch;
      if (next.requestId === requestId && next.loading) {
        set({ messageSearch: { ...next, loading: false } });
      }
    }, 25_000);
  },
  clearMessageSearch: () => {
    if (get().messageSearch !== EMPTY_MESSAGE_SEARCH) {
      set({ messageSearch: EMPTY_MESSAGE_SEARCH });
    }
  },
  translatorLanguages: [],
  translatorBindings: {},
  refreshTranslatorConfig: async () => {
    const state = get();
    try {
      const config = await internalApi.getTranslatorConfig(state.wsUrl);
      set({
        translatorLanguages: (config.languages ?? []).map((language) => ({
          code: language.code,
          label: language.label
        })),
        translatorBindings: config.accountBindings ?? {}
      });
    } catch {
      // Нет сети/доступа: селектор языков останется на встроенном дефолте.
    }
  },
  clientDeposits: {},
  clientHotLeads: {},
  clientStatusSettings: null,
  clientStatusFilter: null,
  refreshClientDeposits: async () => {
    const state = get();
    if (!state.accountId) {
      return;
    }
    try {
      const { records, settings, hotLeads } = await internalApi.getClientDeposits(
        state.accountId,
        state.wsUrl
      );
      const clientDeposits: Record<number, ClientDepositRecord> = {};
      for (const record of records) {
        clientDeposits[record.telegramUserId] = record;
      }
      const clientHotLeads: Record<number, ClientHotLeadRecord> = {};
      // Старый gateway (до запроса №24) поля не отдаёт.
      for (const lead of hotLeads ?? []) {
        clientHotLeads[lead.telegramUserId] = lead;
      }
      set({ clientDeposits, clientHotLeads, clientStatusSettings: settings });
    } catch {
      // Нет доступа/сети — список работает без бейджей статусов.
    }
  },
  setClientStatusFilter: (status) => set({ clientStatusFilter: status }),
  folderLines: null,
  refreshFolderLines: async () => {
    const state = get();
    if (!state.accountId) {
      return;
    }
    try {
      const result = await internalApi.getFolderLines(state.accountId, state.wsUrl);
      if (get().accountId === state.accountId) {
        set({ folderLines: result });
      }
    } catch {
      // Нет доступа/сети — фильтр не применяем, папки видны все.
    }
  },
  setFolderLineBinding: async (listId, lineIds) => {
    const state = get();
    if (!state.accountId || (state.appRole !== "admin" && state.appRole !== "geo_admin")) {
      return;
    }
    try {
      const { bindings } = await internalApi.setFolderLineBinding(
        { accountId: state.accountId, listId, lineIds },
        state.wsUrl
      );
      set((current) => ({
        folderLines: current.folderLines ? { ...current.folderLines, bindings } : current.folderLines
      }));
    } catch (cause) {
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось сохранить привязку папки к линиям.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
    }
  },
  frequentChatsByAccount: {},
  refreshFrequentChats: async () => {
    const state = get();
    try {
      const { frequentChats } = await internalApi.listFrequentChats(state.wsUrl);
      set({ frequentChatsByAccount: frequentChats ?? {} });
    } catch {
      // Нет доступа / сеть — не шумим: кнопка «В Частые группы» просто
      // будет недоступна, пока значение не подтянется.
    }
  },
  setAccountFrequentChat: async (accountId, chatId, title, action) => {
    const state = get();
    if (!accountId || (state.appRole !== "admin" && state.appRole !== "geo_admin")) {
      return;
    }
    try {
      const { frequentChats } = await internalApi.setFrequentChat(
        { accountId, chatId, title, action },
        state.wsUrl
      );
      set({ frequentChatsByAccount: frequentChats ?? {} });
    } catch (cause) {
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось сохранить список частых групп.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
    }
  },
  refreshLocalFolders: async () => {
    const state = get();
    if (!state.accountId) {
      return;
    }
    try {
      const { folders } = await internalApi.listLocalFolders(state.accountId, state.wsUrl);
      const next = folders.map(toLocalFolderEntry);
      set((current) => {
        const merged = mergeLocalFoldersIntoLists(current, next);
        // Открытую папку удалили не отсюда (другой вкладкой/саппортом или
        // автоудалением после ИИ-пуша) — уходим в «Все чаты», как при ручном
        // удалении (deleteChatFolder), а не остаёмся на пустой вкладке.
        const active = current.activeDialogListId;
        if (
          active.startsWith("local:") &&
          !next.some((folder) => `local:${folder.folderId}` === active)
        ) {
          return { ...merged, activeDialogListId: DEFAULT_DIALOG_LIST_ID };
        }
        return merged;
      });
    } catch (cause) {
      // На неавторизованном сокете API даст 401 — это норма во время загрузки.
      // Не паникуем, просто не обновляем папки. Ошибки авторизации
      // отдельный путь обработки.
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось загрузить папки. Обновите страницу или попробуйте позже.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
    }
  },
  folderPick: null,
  pickFolderChats: (input) => {
    const state = get();
    if (!state.accountId || !state.socket) {
      set({
        folderPick: {
          requestId: "",
          status: "error",
          sourceListId: input.sourceListId,
          requestedLimit: input.limit,
          chatIds: [],
          scannedCount: 0,
          skippedInOtherFoldersCount: 0,
          exhausted: false,
          message: "Нет связи с сервером. Обновите страницу и попробуйте снова."
        }
      });
      return;
    }
    const requestId = generateId();
    set({
      folderPick: {
        requestId,
        status: "pending",
        sourceListId: input.sourceListId,
        requestedLimit: input.limit,
        chatIds: [],
        scannedCount: 0,
        skippedInOtherFoldersCount: 0,
        exhausted: false
      }
    });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chatFolder.pickChats",
        requestId,
        sourceListId: input.sourceListId,
        limit: input.limit,
        ...(input.excludeChatIds && input.excludeChatIds.length > 0
          ? { excludeChatIds: input.excludeChatIds }
          : {}),
        ...(input.from ? { from: input.from } : {}),
        ...(input.to ? { to: input.to } : {}),
        ...(typeof input.dayFromMinutes === "number" &&
        typeof input.dayToMinutes === "number"
          ? {
              dayFromMinutes: input.dayFromMinutes,
              dayToMinutes: input.dayToMinutes,
              timeZoneOffsetMinutes: input.timeZoneOffsetMinutes ?? 0
            }
          : {}),
        onlyUnread: input.onlyUnread !== false
      })
    });
    // Подбор — синхронная команда воркера с бюджетом 20с, gateway ждёт её до
    // REQUEST_TIMEOUT_MS. Если ответ не пришёл вовсе (воркер перезапустился,
    // сокет моргнул), снимаем спиннер сами, иначе модалка висит навсегда.
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        const current = get().folderPick;
        if (current?.requestId === requestId && current.status === "pending") {
          set({
            folderPick: {
              ...current,
              status: "error",
              message: "Подбор не ответил вовремя. Попробуйте ещё раз."
            }
          });
        }
      }, 45_000);
    }
  },
  clearFolderPick: () => {
    set({ folderPick: null });
  },
  pushJobs: {},
  upsertPushJob: (job) => {
    if (job.accountId !== get().accountId) {
      return;
    }
    set((state) => ({ pushJobs: { ...state.pushJobs, [job.jobId]: job } }));
  },
  chatExport: null,
  startChatExport: (chatId, limit) => {
    const state = get();
    if (!state.accountId || !state.socket || !chatId) {
      set({
        chatExport: {
          requestId: "",
          chatId,
          status: "error",
          limit,
          lines: [],
          message: "Нет связи с сервером. Обновите страницу и попробуйте снова."
        }
      });
      return;
    }
    const requestId = generateId();
    set({ chatExport: { requestId, chatId, status: "pending", limit, lines: [] } });
    sendChatExportPage(get, requestId, undefined);
  },
  cancelChatExport: () => {
    set({ chatExport: null });
  },
  appendChatsToLocalFolder: async (listId, chatIds) => {
    const state = get();
    if (!listId.startsWith("local:") || !state.accountId || chatIds.length === 0) {
      return 0;
    }
    const folderId = listId.slice("local:".length);
    const folder = state.localFolders.find((entry) => entry.folderId === folderId);
    if (!folder) {
      return 0;
    }
    const existing = new Set(folder.includedChatIds);
    const fresh = chatIds.filter((chatId) => !existing.has(chatId));
    if (fresh.length === 0) {
      return 0;
    }
    // Дельту применяет и режет по лимиту папки сервер: наш снимок состава
    // и «свободного места» мог устареть (другая вкладка или другой саппорт).
    // Сколько реально вошло — считаем по фактическому составу из ответа.
    const updated = await get().updateChatFolder(listId, { addChatIds: fresh });
    if (!updated) {
      return 0;
    }
    const nextIncluded = new Set(updated.includedChatIds);
    return fresh.filter((chatId) => nextIncluded.has(chatId)).length;
  },
  createChatFolder: async (title, includedChatIds, chatLimit) => {
    const state = get();
    const trimmed = title.trim();
    if (trimmed.length === 0 || !state.accountId) {
      return;
    }
    try {
      await internalApi.createLocalFolder(
        {
          accountId: state.accountId,
          title: trimmed,
          includedChatIds,
          ...(chatLimit !== undefined ? { chatLimit } : {})
        },
        state.wsUrl
      );
      await get().refreshLocalFolders();
    } catch (cause) {
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось создать папку.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
    }
  },
  deleteChatFolder: async (listId) => {
    const state = get();
    // local:* — наши папки. Старые TDLib id "folder:*" больше не удаляем
    // через этот action; для них пользователь должен использовать оригинальный
    // клиент Telegram (в нашей UI кнопка удаления показывается только для
    // local-папок).
    if (!listId.startsWith("local:") || !state.accountId) {
      return;
    }
    const folderId = listId.slice("local:".length);
    try {
      await internalApi.deleteLocalFolder(state.accountId, folderId, state.wsUrl);
      await get().refreshLocalFolders();
      // Если удалили активную папку — переключаемся на main.
      set((current) => {
        if (current.activeDialogListId !== listId) {
          return current;
        }
        return { activeDialogListId: DEFAULT_DIALOG_LIST_ID };
      });
    } catch (cause) {
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось удалить папку.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
    }
  },
  updateChatFolder: async (listId, patch) => {
    const state = get();
    if (!listId.startsWith("local:") || !state.accountId) {
      return null;
    }
    const folderId = listId.slice("local:".length);
    try {
      const { folder } = await internalApi.updateLocalFolder(
        {
          accountId: state.accountId,
          folderId,
          ...(typeof patch.title === "string" && patch.title.trim().length > 0
            ? { title: patch.title.trim() }
            : {}),
          ...(Array.isArray(patch.includedChatIds)
            ? { includedChatIds: patch.includedChatIds }
            : {}),
          ...(Array.isArray(patch.addChatIds) && patch.addChatIds.length > 0
            ? { addChatIds: patch.addChatIds }
            : {}),
          ...(Array.isArray(patch.removeChatIds) && patch.removeChatIds.length > 0
            ? { removeChatIds: patch.removeChatIds }
            : {}),
          ...(patch.chatLimit !== undefined ? { chatLimit: patch.chatLimit } : {})
        },
        state.wsUrl
      );
      // Ответ — актуальная строка папки: кладём её в снимок сразу, не
      // дожидаясь broadcast'а chat.localFoldersUpdated (он придёт следом и
      // перечитает все папки аккаунта).
      const entry = toLocalFolderEntry(folder);
      set((current) =>
        mergeLocalFoldersIntoLists(
          current,
          current.localFolders.map((item) => (item.folderId === entry.folderId ? entry : item))
        )
      );
      return entry;
    } catch (cause) {
      set((current) => ({
        errors: pushError(
          current.errors,
          "Не удалось изменить папку.",
          cause instanceof Error ? cause.message : undefined
        )
      }));
      void get().refreshLocalFolders();
      return null;
    }
  },
  requestProfile: (chatId) => {
    const state = get();
    if (!chatId) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.requestProfile",
        chatId
      })
    });
  },
  setChatAutoDeleteTime: (chatId, seconds) => {
    const state = get();
    if (!chatId || !Number.isFinite(seconds) || seconds < 0) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.setAutoDeleteTime",
        chatId,
        seconds
      })
    });
    // Оптимистично обновляем и профиль, и чат (бейдж ⏱ в списке/шапке):
    // TDLib пришлёт updateChatMessageAutoDeleteTime, но с задержкой.
    set((current) => {
      const chat = current.chats[chatId];
      let nextChat = chat;
      if (chat) {
        if (seconds > 0) {
          nextChat = { ...chat, autoDeleteTime: seconds };
        } else {
          const { autoDeleteTime: _autoDeleteTime, ...rest } = chat;
          nextChat = rest;
        }
      }
      return {
        profilesByChat: {
          ...current.profilesByChat,
          [chatId]: { ...current.profilesByChat[chatId], autoDeleteTime: seconds }
        },
        ...(nextChat && nextChat !== chat
          ? { chats: { ...current.chats, [chatId]: nextChat } }
          : {})
      };
    });
  },
  toggleMessagePinned: (chatId, messageId, pinned, onlyForSelf) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.toggleMessagePinned",
        chatId,
        messageId,
        pinned,
        ...(pinned && onlyForSelf !== undefined ? { onlyForSelf } : {})
      })
    });
  },
  setChatInternalTags: (chatId, tags) => {
    const normalizedTags = tags
      .map((tag) => tag.trim())
      .filter((tag, index, allTags) => tag.length > 0 && allTags.indexOf(tag) === index)
      .slice(0, 24);
    set((state) => {
      const chat = state.chats[chatId];
      if (!chat) {
        return state;
      }

      return {
        chats: {
          ...state.chats,
          [chatId]: {
            ...chat,
            internalTags: normalizedTags
          }
        }
      };
    });
  },
  setTyping: (chatId, active) => {
    const state = get();
    if (!chatId) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.typing",
        chatId,
        active
      })
    });
  },
  listDialogs: (limit = INITIAL_DIALOG_LIMIT, listId) => {
    const state = get();
    const targetListId = listId ?? state.activeDialogListId ?? DEFAULT_DIALOG_LIST_ID;
    if (!state.accountId) {
      return;
    }

    set((current) => ({
      dialogListStateById: {
        ...current.dialogListStateById,
        [targetListId]: {
          isLoading: true,
          isLoaded: current.dialogListStateById[targetListId]?.isLoaded ?? false
        }
      },
      dialogRequestedLimitById: {
        ...current.dialogRequestedLimitById,
        [targetListId]: Math.max(
          limit,
          current.dialogRequestedLimitById[targetListId] ?? 0
        )
      }
    }));

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.listDialogs",
        limit,
        listId: targetListId
      })
    });

    scheduleDialogListLoadingWatchdog(get, targetListId);
  },
  loadMoreDialogs: (listId) => {
    const state = get();
    const targetListId = listId ?? state.activeDialogListId ?? DEFAULT_DIALOG_LIST_ID;
    const dialogState = state.dialogListStateById[targetListId];
    if (dialogState?.isLoading || state.dialogHasMoreById[targetListId] === false) {
      return;
    }

    const currentCount = state.chatIdsByList[targetListId]?.length ?? 0;
    // Лимит — от того, что реально есть у вкладки, без ратчета по прошлым
    // запросам. Раньше брался max(прошлый лимит, count) + шаг: у папки, которую
    // воркер никогда не помечает «догружена» (hasMore=true всегда, ответ зажат
    // клампом 600), лимит уходил в сотни тысяч, а каждый ответ тянул полный
    // снапшот папки (~730 КБ) раз в секунду — прод 2026-10-15, вкладки
    // качали по 5-15 ГБ за соединение и «жёстко лагали».
    const nextLimit = currentCount + DIALOG_PAGE_STEP;
    state.listDialogs(nextLimit, targetListId);
  },
  loadHistory: (chatId, limit = 80, messageThreadId) => {
    const state = get();
    if (!chatId || state.historyLoadingByChat[chatId]) {
      return;
    }

    // Если threadId не передан, берём активный топик для этого чата —
    // вызовы вроде selectChat → loadHistory не знают про forum context,
    // и без этого открытие forum-чата в активной теме грузило бы общую
    // ленту вместо темы.
    const resolvedThreadId =
      messageThreadId !== undefined
        ? messageThreadId
        : state.activeForumTopicByChat[chatId] ?? null;

    set((current) => ({
      historyLoadingByChat: {
        ...current.historyLoadingByChat,
        [chatId]: true
      },
      historyReachedStartByChat: {
        ...current.historyReachedStartByChat,
        [chatId]: false
      }
    }));

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.loadHistory",
        chatId,
        limit,
        ...(resolvedThreadId ? { messageThreadId: resolvedThreadId } : {})
      })
    });

    scheduleHistoryLoadingWatchdog(get, chatId);
  },
  loadOlderHistory: (chatId, limit = 60) => {
    const state = get();
    if (
      !chatId ||
      state.historyLoadingByChat[chatId] ||
      state.historyReachedStartByChat[chatId]
    ) {
      return;
    }

    const existingMessages = state.messagesByChat[chatId] ?? [];
    if (existingMessages.length === 0) {
      state.loadHistory(chatId, limit);
      return;
    }

    const cursor = existingMessages[0]!.id;
    set((current) => ({
      historyLoadingByChat: {
        ...current.historyLoadingByChat,
        [chatId]: true
      }
    }));

    const activeThreadId = state.activeForumTopicByChat[chatId] ?? null;
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.loadHistory",
        chatId,
        cursor,
        limit,
        ...(activeThreadId ? { messageThreadId: activeThreadId } : {})
      })
    });

    scheduleHistoryLoadingWatchdog(get, chatId);
  },
  searchMessages: (chatId, query, scope, limit = 30, options) => {
    const state = get();
    if (!chatId) {
      return;
    }

    const trimmedQuery = query.trim();
    const stateKey = options?.requestKey ?? chatId;
    const cursor = options?.cursor;
    // Тот же запрос уже в полёте (дебаунс + Enter дают два одинаковых
    // запроса с разницей в сотни мс) — второй не шлём, воркер и так ответит.
    // force=true — ручной ретрай пользователя (кнопка «Повторить» в галерее):
    // guard игнорируем и, чтобы watchdog не бросил обратно старую загрузку,
    // сначала снимаем isLoading перед новым запросом.
    const inFlight = state.searchStateByChat[stateKey];
    const forced = options?.force === true;
    if (
      !forced &&
      !cursor &&
      inFlight &&
      inFlight.isLoading &&
      inFlight.query === trimmedQuery &&
      inFlight.scope === scope
    ) {
      return;
    }
    if (forced) {
      cancelSearchLoadingWatchdog(stateKey);
    }
    set((current) => {
      const existing = current.searchStateByChat[stateKey];
      const sameRequest = existing?.query === trimmedQuery && existing?.scope === scope;
      // Догрузка страницы без совпадения запроса — устаревший вызов, не
      // затираем текущие результаты чужим курсором.
      if (cursor && !sameRequest) {
        return current;
      }
      return {
        searchStateByChat: {
          ...current.searchStateByChat,
          [stateKey]: {
            query: trimmedQuery,
            scope,
            isLoading: true,
            results: sameRequest ? existing!.results : [],
            ...(cursor ? { cursor } : {}),
            hasMore: sameRequest ? existing!.hasMore : false
          }
        }
      };
    });
    if (cursor && get().searchStateByChat[stateKey]?.cursor !== cursor) {
      return;
    }

    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.searchMessages",
        chatId,
        query: trimmedQuery,
        scope,
        limit,
        ...(cursor ? { cursor } : {}),
        ...(options?.requestKey ? { requestKey: options.requestKey } : {})
      })
    });

    scheduleSearchLoadingWatchdog(get, stateKey);
  },
  clearSearch: (chatId) => {
    set((state) => {
      const keys = state.searchStateByChat[chatId]
        ? [chatId, ...searchStateKeysForChat(state.searchStateByChat, chatId)]
        : searchStateKeysForChat(state.searchStateByChat, chatId);
      if (keys.length === 0) {
        return state;
      }

      const next = { ...state.searchStateByChat };
      for (const key of keys) {
        delete next[key];
      }
      return {
        ...state,
        searchStateByChat: next
      };
    });
  },
  translateMessage: (chatId, messageId, targetLanguage) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    const lang = targetLanguage ?? resolveChatTranslationLanguage(state, chatId);
    if (state.translationsByMessage[translationKey(chatId, messageId)]) {
      return;
    }
    const message = (state.messagesByChat[chatId] ?? []).find((entry) => entry.id === messageId);
    // Для голосового источник — расшифровка, а не лейбл превью
    // («Голосовое сообщение»), иначе переводили бы саму подпись.
    const sourceText = message ? translationSourceText(message.content) : "";

    const fallbackToWorker = (): void => {
      const fresh = get();
      socketSend(fresh.socket, {
        type: "command",
        payload: buildCommandEnvelope(fresh.accountId, fresh.connectionId, {
          type: "chat.translateMessage",
          chatId,
          messageId,
          targetLanguage: lang
        })
      });
    };

    if (sourceText.length === 0) {
      // Если текста в локальном state нет (например, медиа без подписи) — нечего слать в LLM.
      // Worker возьмёт текст из TDLib и попробует перевести встроенным сервисом.
      fallbackToWorker();
      return;
    }

    const apiBase = httpBaseFromWsUrl(state.wsUrl);
    void (async () => {
      try {
        const response = await fetch(`${apiBase}/api/translate`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            text: sourceText,
            targetLanguage: lang,
            accountId: state.accountId
          })
        });
        if (response.status === 503) {
          fallbackToWorker();
          return;
        }
        const payload = (await response.json().catch(() => null)) as
          | { ok?: boolean; text?: string; targetLanguage?: string }
          | null;
        if (!response.ok || !payload?.ok || typeof payload.text !== "string") {
          // Тихо падаем — UI сам останется без перевода (translationsByMessage пусто), кнопка останется доступной.
          return;
        }
        // Если пользователь успел сменить целевой язык чата, пока этот запрос
        // летал, сохранять устаревший перевод нельзя — иначе после смены
        // языка экран продолжает заполняться старыми переводами и кажется,
        // что выбор языка ни на что не влияет.
        const fresh = get();
        const currentLang = resolveChatTranslationLanguage(fresh, chatId);
        if (currentLang !== lang) {
          return;
        }
        set((current) => ({
          translationsByMessage: boundMemoryTranslations(
            {
              ...current.translationsByMessage,
              [translationKey(chatId, messageId)]: {
                text: payload.text!,
                targetLanguage: payload.targetLanguage ?? lang
              }
            },
            current.selectedChatId
          )
        }));
      } catch {
        // Сетевая ошибка — попробуем встроенный TDLib-перевод.
        fallbackToWorker();
      }
    })();
  },
  transcribeVoice: (chatId, messageId) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    // Оптимистично помечаем «распознаём» — реальный статус приедет через
    // chat.messageUpserted (worker перечитает сообщение из TDLib).
    set((current) => {
      const messages = current.messagesByChat[chatId];
      if (!messages) {
        return current;
      }
      let mutated = false;
      const next = messages.map((message) => {
        if (
          message.id !== messageId ||
          (message.content.kind !== "voice" && message.content.kind !== "videoNote")
        ) {
          return message;
        }
        if (message.content.transcriptStatus === "pending") {
          return message;
        }
        mutated = true;
        return {
          ...message,
          content: { ...message.content, transcriptStatus: "pending" as const }
        };
      });
      if (!mutated) {
        return current;
      }
      return { messagesByChat: { ...current.messagesByChat, [chatId]: next } };
    });
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.transcribeVoice",
        chatId,
        messageId
      })
    });
  },
  loadScheduledMessages: (chatId) => {
    const state = get();
    if (!chatId || !state.socket) {
      return;
    }
    set((current) => ({
      scheduledLoadingByChat: { ...current.scheduledLoadingByChat, [chatId]: true }
    }));
    scheduleScheduledLoadingWatchdog(chatId);
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.loadScheduledMessages",
        chatId
      })
    });
  },
  sendScheduledNow: (chatId, messageId) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    // Оптимистично убираем из списка: TDLib пришлёт свежий снимок ответом,
    // но кнопка должна отзываться мгновенно.
    set((current) => ({
      scheduledByChat: {
        ...current.scheduledByChat,
        [chatId]: (current.scheduledByChat[chatId] ?? []).filter(
          (entry) => entry.id !== messageId
        )
      }
    }));
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.sendScheduledNow",
      chatId,
      messageId
    });
    rememberScheduledMutation(envelope.commandId, chatId);
    socketSend(state.socket, { type: "command", payload: envelope });
  },
  deleteScheduledMessage: (chatId, messageId) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    set((current) => ({
      scheduledByChat: {
        ...current.scheduledByChat,
        [chatId]: (current.scheduledByChat[chatId] ?? []).filter(
          (entry) => entry.id !== messageId
        )
      }
    }));
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.deleteScheduledMessage",
      chatId,
      messageId
    });
    rememberScheduledMutation(envelope.commandId, chatId);
    socketSend(state.socket, { type: "command", payload: envelope });
  },
  rescheduleMessage: (chatId, messageId, schedule) => {
    const state = get();
    if (!chatId || !messageId) {
      return;
    }
    const whenOnline = schedule.whenOnline === true;
    const sendAtIso =
      !whenOnline && schedule.sendAt ? new Date(schedule.sendAt).toISOString() : undefined;
    if (!whenOnline && !sendAtIso) {
      return;
    }
    // Оптимистично двигаем запись на полке; подтверждённый снимок придёт
    // ответом воркера.
    set((current) => {
      const list = current.scheduledByChat[chatId] ?? [];
      const next = list.map((entry) =>
        entry.id === messageId
          ? {
              ...entry,
              isPending: true,
              ...(whenOnline
                ? { sendWhenOnline: true as const }
                : { sendWhenOnline: false as const }),
              ...(sendAtIso ? { sendAt: sendAtIso } : {})
            }
          : entry
      );
      if (whenOnline) {
        for (const entry of next) {
          if (entry.id === messageId) {
            delete entry.sendAt;
          }
        }
      }
      return {
        scheduledByChat: {
          ...current.scheduledByChat,
          [chatId]: sortScheduledEntries(next)
        }
      };
    });
    const envelope = buildCommandEnvelope(state.accountId, state.connectionId, {
      type: "chat.rescheduleMessage",
      chatId,
      messageId,
      ...(sendAtIso ? { sendAt: sendAtIso } : {}),
      ...(whenOnline ? { whenOnline: true } : {})
    });
    rememberScheduledMutation(envelope.commandId, chatId);
    socketSend(state.socket, { type: "command", payload: envelope });
  },
  clearMessageTranslation: (chatId, messageId) => {
    // Пользователь скрыл перевод руками — сверка не должна его воскрешать.
    translationsHiddenByUser.add(translationKey(chatId, messageId));
    set((state) => {
      const key = translationKey(chatId, messageId);
      if (!state.translationsByMessage[key]) {
        return state;
      }
      const next = { ...state.translationsByMessage };
      delete next[key];
      return { translationsByMessage: next };
    });
  },
  setChatTranslation: (chatId, enabled) => {
    if (!chatId) {
      return;
    }
    set((state) => ({
      translatedChats: { ...state.translatedChats, [chatId]: enabled }
    }));
    if (enabled) {
      // Восстанавливаем из shadow-кэша ранее переведённые сообщения того же
      // языка — их не нужно переводить повторно (ТЗ: не дёргать переводчик,
      // если перевод уже существует).
      {
        const state = get();
        const lang = resolveChatTranslationLanguage(state, chatId);
        const restore: RealtimeState["translationsByMessage"] = {};
        for (const message of state.messagesByChat[chatId] ?? []) {
          const key = translationKey(chatId, message.id);
          const cached = translationShadowCache.get(key);
          if (cached && cached.targetLanguage === lang && !state.translationsByMessage[key]) {
            restore[key] = cached;
          }
        }
        if (Object.keys(restore).length > 0) {
          set((current) => ({
            translationsByMessage: { ...current.translationsByMessage, ...restore }
          }));
        }
      }
      const state = get();
      // reverse: messagesByChat отсортирован старые→новые, а переводить нужно
      // снизу вверх — сначала свежие/видимые сообщения, потом старую историю.
      const pending = (state.messagesByChat[chatId] ?? [])
        .filter(
          (message) =>
            isTranslatableMessage(message) &&
            !state.translationsByMessage[translationKey(chatId, message.id)]
        )
        .map((message) => message.id)
        .reverse();
      enqueueChatTranslation(get, chatId, pending);
      return;
    }
    chatTranslateQueues.delete(chatId);
    resetChatTranslationBookkeeping(chatId);
    // Перед очисткой сохраняем переводы в shadow-кэш: повторное включение
    // восстановит их без обращения к переводчику (ТЗ).
    {
      const chatPrefix = `${chatId}:`;
      for (const [key, value] of Object.entries(get().translationsByMessage)) {
        if (key.startsWith(chatPrefix)) {
          translationShadowCache.set(key, value);
        }
      }
    }
    set((state) => {
      // Ключи составные (chatId:messageId) — чистим всё, что принадлежит
      // этому чату, включая переводы сообщений, уже выпавших из ленты.
      const chatPrefix = `${chatId}:`;
      let mutated = false;
      const nextTranslations: RealtimeState["translationsByMessage"] = {};
      for (const [key, value] of Object.entries(state.translationsByMessage)) {
        if (key.startsWith(chatPrefix)) {
          mutated = true;
          continue;
        }
        nextTranslations[key] = value;
      }
      if (!mutated) {
        return state;
      }
      return { translationsByMessage: nextTranslations };
    });
  },
  setChatTranslationLanguage: (chatId, language) => {
    if (!chatId) {
      return;
    }
    const normalized = language.trim().toLowerCase();
    if (normalized.length === 0) {
      return;
    }
    set((state) => ({
      translationLanguageByChat: {
        ...state.translationLanguageByChat,
        [chatId]: normalized
      }
    }));
    // Если для чата уже включён перевод и есть ранее переведённые сообщения —
    // сбрасываем их и заново ставим в очередь под новый язык, иначе менять
    // язык было бы бесполезно (старые переводы остались бы на прежнем языке).
    const state = get();
    if (!state.translatedChats[chatId]) {
      return;
    }
    set((current) => {
      const chatPrefix = `${chatId}:`;
      let mutated = false;
      const next: RealtimeState["translationsByMessage"] = {};
      for (const [key, value] of Object.entries(current.translationsByMessage)) {
        if (key.startsWith(chatPrefix)) {
          mutated = true;
          continue;
        }
        next[key] = value;
      }
      return mutated ? { translationsByMessage: next } : current;
    });
    chatTranslateQueues.delete(chatId);
    resetChatTranslationBookkeeping(chatId);
    // reverse: снизу вверх (свежие сообщения раньше старой истории).
    const pending = (get().messagesByChat[chatId] ?? [])
      .filter(isTranslatableMessage)
      .map((message) => message.id)
      .reverse();
    enqueueChatTranslation(get, chatId, pending);
  },
  rememberOutgoingOriginal: (chatId, sentText, originalText) => {
    const sent = sentText.trim();
    const original = originalText.trim();
    if (!chatId || sent.length === 0 || original.length === 0 || sent === original) {
      return;
    }
    outgoingOriginals.set(outgoingOriginalKey(chatId, sent), original);
    // Мапа живёт только до прихода сообщения из TDLib; кап на случай, если
    // что-то не доехало (черновик перевели и не отправили).
    while (outgoingOriginals.size > OUTGOING_ORIGINALS_MAX) {
      const oldest = outgoingOriginals.keys().next().value;
      if (oldest === undefined) {
        break;
      }
      outgoingOriginals.delete(oldest);
    }
  },
  retranslateMissing: (chatId) => {
    const state = get();
    if (!chatId || !state.translatedChats[chatId]) {
      return;
    }
    // Сбрасываем именно счётчик попыток: сообщение, которое три раза
    // не доехало (упавший провайдер, 429, разрыв сети), иначе оставалось
    // без перевода до выключения/включения перевода в чате.
    const prefix = `${chatId}:`;
    for (const key of [...translationAttempts.keys()]) {
      if (key.startsWith(prefix)) {
        translationAttempts.delete(key);
      }
    }
    const pending = (state.messagesByChat[chatId] ?? [])
      .filter(
        (message) =>
          isTranslatableMessage(message) &&
          !state.translationsByMessage[translationKey(chatId, message.id)] &&
          !translationsHiddenByUser.has(translationKey(chatId, message.id))
      )
      .map((message) => message.id)
      .reverse();
    enqueueChatTranslation(get, chatId, pending);
  },
  setOutgoingLanguage: (chatId, language) => {
    const normalized = language.trim().toLowerCase();
    if (!chatId || normalized.length === 0) {
      return;
    }
    set((state) => ({
      outgoingLanguageByChat: { ...state.outgoingLanguageByChat, [chatId]: normalized },
      outgoingLanguageByAccount: state.accountId
        ? { ...state.outgoingLanguageByAccount, [state.accountId]: normalized }
        : state.outgoingLanguageByAccount
    }));
  },
  openForwardComposition: (sourceChatId, sourceMessageIds) => {
    const ids = (Array.isArray(sourceMessageIds) ? sourceMessageIds : [sourceMessageIds]).filter(
      (id) => typeof id === "string" && id.length > 0
    );
    const first = ids[0];
    if (!sourceChatId || !first) {
      return;
    }
    set({
      forwardComposition: {
        sourceChatId,
        sourceMessageId: first,
        sourceMessageIds: ids,
        hideSenderName: false,
        hideCaption: false
      }
    });
  },
  closeForwardComposition: () => {
    set({ forwardComposition: null });
  },
  setForwardHideSenderName: (value) => {
    set((current) => {
      if (!current.forwardComposition) {
        return current;
      }
      return {
        forwardComposition: { ...current.forwardComposition, hideSenderName: value }
      };
    });
  },
  setForwardHideCaption: (value) => {
    set((current) => {
      if (!current.forwardComposition) {
        return current;
      }
      return {
        forwardComposition: { ...current.forwardComposition, hideCaption: value }
      };
    });
  },
  toggleReaction: (chatId, messageId, emoji) => {
    const state = get();
    if (!chatId || !messageId || !emoji) {
      return;
    }
    socketSend(state.socket, {
      type: "command",
      payload: buildCommandEnvelope(state.accountId, state.connectionId, {
        type: "chat.toggleReaction",
        chatId,
        messageId,
        emoji
      })
    });
  },
  refreshNotificationPermission: () => {
    set({ notificationPermission: getNotificationPermission() });
  },
  setNotificationsEnabled: (enabled) => {
    setNotificationEnabled(enabled);
    set({ notificationsEnabled: enabled });
  },
  applySocketMessage: (message) => {
    switch (message.type) {
      case "session.attached":
        set({
          accountId: message.accountId,
          connectionId: message.connectionId,
          connectionStatus: "connected"
        });
        get().listDialogs(INITIAL_DIALOG_LIMIT, DEFAULT_DIALOG_LIST_ID);
        if (!get().selfIdentity) {
          // Воркер шлёт account.identity только при старте сессии; вкладка,
          // открытая позже, без явного запроса не узнает имя аккаунта (нужно
          // для «[дата] Имя: текст» при копировании/экспорте).
          get().requestSelfIdentity();
        }
        {
          // Незавершённые ИИ-пуши аккаунта: перезагруженная вкладка должна
          // видеть идущие и стоящие в очереди папки (дальше — push.jobUpdated).
          // Серверный список заменяет все незавершённые записи: то, что
          // закончилось за время разрыва, иначе так и висело бы «в работе».
          const attachedAccountId = message.accountId;
          void pushApi
            .listJobs(attachedAccountId, get().wsUrl, 100, { activeOnly: true })
            .then(({ jobs }) => {
              if (get().accountId !== attachedAccountId) return;
              set((state) => {
                const next: Record<string, PushJobSummary> = {};
                for (const job of Object.values(state.pushJobs)) {
                  if (
                    job.accountId === attachedAccountId &&
                    (job.status === "done" || job.status === "cancelled" || job.status === "failed")
                  ) {
                    next[job.jobId] = job;
                  }
                }
                for (const job of jobs) {
                  next[job.jobId] = job;
                }
                return { pushJobs: next };
              });
            })
            .catch(() => undefined);
        }
        {
          // Пользователь мог сидеть в папке/архиве во время reconnect'а:
          // isLoaded переживает reconnect (persist), а воркер после рестарта
          // забывает knownToClient — живые события по списку глохнут до
          // ручного переключения. Перезапрашиваем активный список сразу.
          const active = get().activeDialogListId;
          if (
            active &&
            active !== DEFAULT_DIALOG_LIST_ID &&
            !active.startsWith("local:")
          ) {
            folderLastRefreshAt.set(active, Date.now());
            const currentCount = get().chatIdsByList[active]?.length ?? 0;
            get().listDialogs(
              Math.min(
                Math.max(INITIAL_DIALOG_LIMIT, currentCount),
                FOLDER_REFRESH_MAX_LIMIT
              ),
              active
            );
          }
        }
        return;
      case "pong":
        return;
      case "error": {
        // Перестановка пинов не прошла — возвращаем список к серверному
        // порядку, иначе оптимистичная перестановка выглядит как сделанная.
        {
          const pinnedListId = message.commandId
            ? pendingPinnedOrderByCommandId.get(message.commandId)
            : undefined;
          if (pinnedListId && message.commandId) {
            pendingPinnedOrderByCommandId.delete(message.commandId);
            if (!isLocalDialogList(pinnedListId)) {
              const current = get();
              current.listDialogs(
                Math.max(
                  INITIAL_DIALOG_LIMIT,
                  current.chatIdsByList[pinnedListId]?.length ?? 0
                ),
                pinnedListId
              );
            }
          }
        }
        const failedSend = message.commandId
          ? pendingSendByCommandId.get(message.commandId)
          : undefined;
        if (failedSend && message.commandId) {
          pendingSendByCommandId.delete(message.commandId);
        }
        // Исход известен — следующий такой же текст уже новая отправка.
        // Таймаут не считается: сообщение могло уйти, повтор режет дедупер.
        if (message.commandId && message.code !== "WORKER_COMMAND_TIMEOUT") {
          resolveTextSendCommand(message.commandId);
        }
        // «Команда уже выполнена» — это повтор того, что уже сделано
        // (двойной клик, переотправка после реконнекта), а не ошибка:
        // лишний плейсхолдер снимаем ниже, тост не показываем.
        const alreadyProcessed = message.code === "COMMAND_ALREADY_PROCESSED";
        // WORKER_COMMAND_TIMEOUT: TDLib мог доставить сообщение позже — если
        // снять пузырь, саппорт жмёт «Отправить» ещё раз и в TG появляется
        // дубль. Оставляем пузырь «отправляется» до 90с; за это время либо
        // придёт messageUpserted (пузырь смержится), либо watchdog его снимет.
        const keepBubbleOnTimeout =
          failedSend && message.code === "WORKER_COMMAND_TIMEOUT";
        if (keepBubbleOnTimeout && typeof window !== "undefined") {
          const localId = failedSend.localId;
          const chatId = failedSend.chatId;
          window.setTimeout(() => {
            const list = useRealtimeStore.getState().messagesByChat[chatId];
            if (!list) return;
            const stillPending = list.some(
              (entry) => entry.localId === localId && entry.isPending === true
            );
            if (!stillPending) return;
            useRealtimeStore.setState((current) => {
              const existing = current.messagesByChat[chatId];
              if (!existing) return current;
              return {
                messagesByChat: {
                  ...current.messagesByChat,
                  [chatId]: existing.filter(
                    (entry) =>
                      entry.localId !== localId &&
                      entry.id !== `optimistic:${localId}`
                  )
                }
              };
            });
          }, 90_000);
        }
        set((state) => {
          // Отправка не прошла через gateway/воркер: снимаем плейсхолдер,
          // чтобы он не висел «отправляется…» вечно. При таймауте (см. выше)
          // держим пузырь; для остальных ошибок — убираем сразу.
          const list =
            failedSend && !keepBubbleOnTimeout
              ? state.messagesByChat[failedSend.chatId]
              : undefined;
          const nextMessagesByChat =
            failedSend && !keepBubbleOnTimeout && list
              ? {
                  ...state.messagesByChat,
                  [failedSend.chatId]: list.filter(
                    (entry) =>
                      entry.localId !== failedSend.localId &&
                      entry.id !== `optimistic:${failedSend.localId}`
                  )
                }
              : state.messagesByChat;
          const errors =
            failedSend && message.code === "WORKER_COMMAND_TIMEOUT"
              ? pushError(
                  state.errors,
                  "Сервер не ответил вовремя: сообщение могло уйти. Проверьте чат, прежде чем отправлять повторно.",
                  `${message.code}: ${message.message}`
                )
              : alreadyProcessed
                ? state.errors
                : pushRealtimeError(state.errors, message.code, message.message);
          // Не удалось запланировать — снимаем оптимистичную запись с полки.
          // Таймаут (см. выше) — оставляем: TDLib мог поставить в очередь.
          const shelf =
            failedSend && !keepBubbleOnTimeout
              ? state.scheduledByChat[failedSend.chatId]
              : undefined;
          const nextScheduledByChat =
            failedSend && !keepBubbleOnTimeout && shelf && shelf.some((entry) => entry.id === `optimistic:${failedSend.localId}`)
              ? {
                  ...state.scheduledByChat,
                  [failedSend.chatId]: shelf.filter(
                    (entry) => entry.id !== `optimistic:${failedSend.localId}`
                  )
                }
              : state.scheduledByChat;
          return { errors, messagesByChat: nextMessagesByChat, scheduledByChat: nextScheduledByChat };
        });
        const failedScheduledChatId = message.commandId
          ? pendingScheduledMutationByCommandId.get(message.commandId)
          : undefined;
        if (failedScheduledChatId && message.commandId) {
          pendingScheduledMutationByCommandId.delete(message.commandId);
          get().loadScheduledMessages(failedScheduledChatId);
        }
        // При лимите/бурст-блоке принудительно закрываем чат — иначе саппорт
        // видит закэшированные сообщения и думает, что лимит не работает.
        if (message.code === "SUPPORT_CHAT_LIMIT" || message.code === "SUPPORT_BURST_BLOCKED") {
          set({ selectedChatId: null });
        }
        return;
      }
      case "admin.alert": {
        const who = message.targetLogin ?? message.targetUserId;
        set((state) => ({
          errors: pushError(
            state.errors,
            message.kind === "push.completed"
              ? `🤖 ИИ-пропуш ${message.reason} (запустил ${who})`
              : `🚨 Саппорт «${who}» заблокирован: ${message.reason}`
          )
        }));
        return;
      }
      case "event":
        switch (message.payload.type) {
          case "chat.dialogsListed": {
            const payload = message.payload;
            cancelDialogListLoadingWatchdog(payload.listId);
            set((state) => {
              const synchronizedDialogLists = synchronizeDialogListState(
                state,
                payload.availableLists,
                {
                  loadedListId: payload.listId
                }
              );

              // re-merge локальных папок, чтобы их вкладки не пропали при
              // первичной/повторной синхронизации с TDLib-листами.
              const reMerged = mergeLocalFoldersIntoLists(
                {
                  ...state,
                  dialogLists: synchronizedDialogLists.dialogLists,
                  dialogListOrder: synchronizedDialogLists.dialogListOrder,
                  chatIdsByList: synchronizedDialogLists.chatIdsByList
                },
                state.localFolders
              );
              const mergedDialogLists =
                reMerged.dialogLists ?? synchronizedDialogLists.dialogLists;
              const mergedDialogListOrder =
                reMerged.dialogListOrder ?? synchronizedDialogLists.dialogListOrder;
              const mergedChatIdsByList =
                reMerged.chatIdsByList ?? synchronizedDialogLists.chatIdsByList;

              // Мерж страницы считаем заранее: он нужен и для chatIdsByList,
              // и для guard'а hasMore ниже (прогресс = вырос ли список ПОСЛЕ
              // мержа, а не размер страницы — страница может быть меньше
              // запрошенного из-за клампа воркера или выпавших болванок).
              const nextChatIdsByList = mergeChatsIntoLists(
                mergedChatIdsByList,
                payload.dialogs,
                payload.listId,
                state.chats
              );

              return {
                dialogLists: mergedDialogLists,
                dialogListOrder: mergedDialogListOrder,
                dialogListStateById: {
                  // re-merge возвращает state с local:* пометками
                  // (isLoaded=true). Базой берём именно его, чтобы local
                  // не сбрасывались на каждом серверном push'е.
                  ...(reMerged.dialogListStateById ?? synchronizedDialogLists.dialogListStateById),
                  // Критично: synchronizeDialogListState копирует isLoading
                  // как есть из текущего state, поэтому на success-ответе
                  // спиннер «Подгружаем…» залипает навсегда. Явно ставим
                  // isLoading=false и isLoaded=true для папки которая
                  // только что вернулась.
                  [payload.listId]: {
                    isLoading: false,
                    isLoaded: true
                  }
                },
                dialogRequestedLimitById: {
                  ...synchronizedDialogLists.dialogRequestedLimitById,
                  [payload.listId]: Math.max(
                    state.dialogRequestedLimitById[payload.listId] ?? 0,
                    payload.dialogs.length
                  )
                },
                dialogHasMoreById: {
                  ...(reMerged.dialogHasMoreById ?? synchronizedDialogLists.dialogHasMoreById),
                  // Guard от бесконечного «Подгружаем…»:
                  // - если worker сам сказал «всё» — верим
                  // - если worker отдал ровно сколько в кэше клиента уже было
                  //   И при этом отдал меньше чем мы просили → стопор без
                  //   прогресса, ставим hasMore=false.
                  // Иначе разрешаем — пусть SPA попробует loadMore ещё раз
                  // (worker мог тянуть ещё чаты в primeDialogList в фоне).
                  [payload.listId]: (() => {
                    if (!payload.hasMore) {
                      return false;
                    }
                    // Кусок снапшота кэша воркера, а не ответ на запрос
                    // страницы: эвристики «страница меньше запрошенной»
                    // к нему неприменимы — куски по 500 диалогов иначе
                    // погасили бы «Загрузить ещё» на первом же из них.
                    if (payload.snapshot) {
                      return true;
                    }
                    // Для папок и архива верим hasMore воркера: false он теперь
                    // присылает только по настоящему 404 от TDLib loadChats.
                    // Раньше эвристика ниже гасила hasMore на первом же ответе
                    // (кэш папки < запрошенного лимита) — и папка навсегда
                    // переставала догружаться.
                    if (payload.listId !== "main") {
                      return true;
                    }
                    const previousCount =
                      state.chatIdsByList[payload.listId]?.length ?? 0;
                    const nextCount =
                      nextChatIdsByList[payload.listId]?.length ?? previousCount;
                    // В кэше воркера чатов больше, чем у нас после мержа —
                    // хвост придёт снапшотом (on-demand или префетч), кнопку
                    // не гасим даже без видимого прогресса этой страницы.
                    if (
                      typeof payload.cachedCount === "number" &&
                      payload.cachedCount > nextCount
                    ) {
                      return true;
                    }
                    const requestedLimit =
                      state.dialogRequestedLimitById[payload.listId] ?? 0;
                    const noProgress = nextCount <= previousCount;
                    const workerCappedOut = payload.dialogs.length < requestedLimit;
                    if (noProgress && workerCappedOut) {
                      return false;
                    }
                    return true;
                  })()
                },
                // Если active был local:* и папка существует, mergeLocal
                // вернёт её id. Иначе откатываемся на серверный fallback.
                activeDialogListId:
                  reMerged.activeDialogListId &&
                  reMerged.activeDialogListId.startsWith("local:")
                    ? reMerged.activeDialogListId
                    : synchronizedDialogLists.activeDialogListId,
                chats: payload.dialogs.reduce<Record<string, ChatViewModel>>((acc, dialog) => {
                  const merged = mergeDialogSummary(state.chats[dialog.chatId], dialog);
                  // Если этот чат сейчас открыт, считаем его прочитанным —
                  // worker может прислать снимок раньше, чем applied viewMessages,
                  // и иначе мы перетрём оптимистичный unreadCount=0 обратно.
                  acc[dialog.chatId] =
                    isViewingChat(state, dialog.chatId)
                      ? { ...merged, unreadCount: 0 }
                      : merged;
                  return acc;
                }, { ...state.chats }),
                // База для слияния — mergedChatIdsByList (с local:* папками),
                // а не synchronizedDialogLists.chatIdsByList, иначе local:*
                // вкладки потеряли бы свои chatId после первого dialogsListed.
                chatIdsByList: nextChatIdsByList,
                draftsByChat: payload.dialogs.reduce<Record<string, string>>(
                  (acc, dialog) => {
                    if (typeof dialog.draftText === "string" && dialog.draftText.length > 0) {
                      // Тот же фильтр, что и в chat.dialogUpdated: снапшот
                      // списка чатов тоже приносит УСТАРЕВШИЙ draftText (мы
                      // уже отправили сообщение и очистили черновик, а TDLib
                      // отдаёт кэш) — без проверки текст «воскресал» в поле
                      // ввода при каждом обновлении списка. Это и был главный
                      // источник фантомных черновиков.
                      if (!isDraftEchoBlocked(dialog.chatId, dialog.draftText)) {
                        acc[dialog.chatId] = dialog.draftText;
                      }
                    }
                    // draftText отсутствует = черновика на сервере нет. Ничего
                    // не трогаем: локальный текст мог быть набран секунду назад
                    // и ещё не доехать до TDLib (push дебаунсится).
                    return acc;
                  },
                  { ...state.draftsByChat }
                )
              };
            });
            return;
          }
          case "client.depositsUpdated": {
            const records = message.payload.records;
            if (records.length === 0) {
              return;
            }
            set((state) => {
              const next = { ...state.clientDeposits };
              for (const record of records) {
                const current = next[record.telegramUserId];
                // Дельта из другого гейтвея/старого тика не должна откатывать
                // более свежий депозит.
                if (!current || Date.parse(record.lastDepositAt) >= Date.parse(current.lastDepositAt)) {
                  next[record.telegramUserId] = record;
                }
              }
              return { clientDeposits: next };
            });
            return;
          }
          case "client.hotLeadsUpdated": {
            const { records, removedTelegramUserIds } = message.payload;
            if (records.length === 0 && removedTelegramUserIds.length === 0) {
              return;
            }
            set((state) => {
              const next = { ...state.clientHotLeads };
              for (const id of removedTelegramUserIds) {
                delete next[id];
              }
              for (const record of records) {
                next[record.telegramUserId] = record;
              }
              return { clientHotLeads: next };
            });
            return;
          }
          case "client.statusSettingsUpdated": {
            set({ clientStatusSettings: message.payload.settings });
            return;
          }
          case "chat.localFoldersUpdated": {
            // Папки правили в другой вкладке или другим саппортом. Перечитываем
            // состав, иначе снимок вкладки устареет: чужие чаты будут выглядеть
            // «без папки», а следующее сохранение из неё их затёрло бы.
            scheduleLocalFoldersRefresh(() => get().refreshLocalFolders());
            return;
          }
          case "chat.dialogListsUpdated": {
            const payload = message.payload;
            let activeFolderToReload: string | null = null;
            set((state) => {
              const synchronizedDialogLists = synchronizeDialogListState(
                state,
                payload.availableLists
              );

              // Сначала применяем серверный диф, потом docking-merge
              // локальных папок — иначе serverside сводка вытерла бы наши
              // local:* записи.
              const merged = mergeLocalFoldersIntoLists(
                {
                  ...state,
                  dialogLists: synchronizedDialogLists.dialogLists,
                  dialogListOrder: synchronizedDialogLists.dialogListOrder,
                  chatIdsByList: synchronizedDialogLists.chatIdsByList
                },
                state.localFolders
              );

              // Bug 15: если пользователь в основном TG правил папки
              // (добавил/убрал чаты, поменял фильтры) — chatIdsByList
              // для TDLib-папок мог стать несинхронным. Помечаем все не-main
              // TDLib-папки (kind === "folder") как «не загружены», чтобы при
              // следующем открытии они переспрашивали листинг; активную
              // перезапрашиваем сразу после set() ниже.
              //
              // ВАЖНО: chatIdsByList при этом НЕ стираем. Раньше delete
              // опустошал активную папку синхронно → кадр со скелетоном,
              // схлопывание скролла в 0 и перезаезд списка (клиентская жалоба
              // «интерфейс перезагружается при добавлении чатов в папку»).
              // Устаревшие id безопасны: chat.dialogsListed заменяет список
              // папки целиком (mergeChatsIntoLists), убранные чаты исчезнут
              // при подмене, а до неё юзер видит прежний список без прыжков.
              const mergedDialogListStateById =
                merged.dialogListStateById ?? synchronizedDialogLists.dialogListStateById;
              const mergedChatIdsByList =
                merged.chatIdsByList ?? synchronizedDialogLists.chatIdsByList;
              const mergedDialogLists =
                merged.dialogLists ?? synchronizedDialogLists.dialogLists;
              const nextActive =
                merged.activeDialogListId &&
                merged.activeDialogListId.startsWith("local:")
                  ? merged.activeDialogListId
                  : synchronizedDialogLists.activeDialogListId;

              // Эхо нашей же правки состава папок (setListMembership из этого
              // окна): активную папку НЕ помечаем stale и не перезагружаем —
              // оптимистичный апдейт + dialogUpdated уже применили изменение.
              // Остальные папки всё равно помечаем: переключение вкладки
              // сделает им один listDialogs.
              const ownEditRecent =
                Date.now() - lastOwnFolderEditAt < OWN_FOLDER_EDIT_WINDOW_MS;

              const invalidatedStateById: Record<string, typeof mergedDialogListStateById[string]> = {
                ...mergedDialogListStateById
              };
              for (const listId of Object.keys(mergedDialogListStateById)) {
                if (mergedDialogLists[listId]?.kind !== "folder") {
                  continue;
                }
                if (ownEditRecent && listId === nextActive) {
                  continue;
                }
                const previous = mergedDialogListStateById[listId];
                if (previous && previous.isLoaded === false) {
                  continue;
                }
                invalidatedStateById[listId] = {
                  isLoading: previous?.isLoading ?? false,
                  isLoaded: false
                };
              }

              if (
                !ownEditRecent &&
                nextActive &&
                mergedDialogLists[nextActive]?.kind === "folder"
              ) {
                activeFolderToReload = nextActive;
              }

              return {
                dialogLists: merged.dialogLists ?? synchronizedDialogLists.dialogLists,
                dialogListOrder:
                  merged.dialogListOrder ?? synchronizedDialogLists.dialogListOrder,
                dialogListStateById: invalidatedStateById,
                dialogRequestedLimitById: synchronizedDialogLists.dialogRequestedLimitById,
                dialogHasMoreById:
                  merged.dialogHasMoreById ?? synchronizedDialogLists.dialogHasMoreById,
                chatIdsByList: mergedChatIdsByList,
                activeDialogListId: nextActive
              };
            });
            if (activeFolderToReload && typeof window !== "undefined") {
              // Дебаунс 2с: серия updateChatFolders (правка нескольких папок,
              // массовые изменения с телефона) схлопывается в ОДИН listDialogs.
              // Немедленная перезагрузка на каждое событие заваливала воркер:
              // каждый listDialogs = секунды TDLib-prime, очередь давила
              // остальные команды до WORKER_COMMAND_TIMEOUT.
              folderResyncListId = activeFolderToReload;
              if (folderResyncTimer !== null) {
                window.clearTimeout(folderResyncTimer);
              }
              folderResyncTimer = window.setTimeout(() => {
                folderResyncTimer = null;
                const listId = folderResyncListId;
                folderResyncListId = null;
                if (!listId) {
                  return;
                }
                // Лимит = текущий размер (мин. 50, потолок 400 — защита
                // воркера от гигантских prime'ов): с фикс. 50 пропагиниро-
                // ванная папка «усыхала» и прыгала при автодогрузке.
                const currentCount = get().chatIdsByList[listId]?.length ?? 0;
                get().listDialogs(
                  Math.min(Math.max(INITIAL_DIALOG_LIMIT, currentCount), 400),
                  listId
                );
              }, 2_000);
            }
            return;
          }
          case "chat.dialogUpdated": {
            const payload = message.payload;
            set((state) => {
              // Жёсткий фильтр: НЕ добавлять чат в state.chats через
              // dialogUpdated. Worker эмитит этот ивент на любой TDLib-update
              // (updateChatLastMessage / Position / Title) — на аккаунте с 10k
              // чатов TDLib потихоньку «подсасывает» весь свой кэш в state и
              // ломает пользовательскую пагинацию (видно как «11212 и растёт»).
              // Чат должен попадать в стейт только через явный listDialogs
              // (юзерский скролл) или через chat.messageUpserted (новое сообщение
              // в видимом чате). Здесь — только апдейт уже известного чата.
              if (!state.chats[payload.dialog.chatId]) {
                return state;
              }
              const mergedChat = mergeDialogSummary(
                state.chats[payload.dialog.chatId],
                payload.dialog
              );
              // см. chat.dialogsListed — не даём worker'у затереть оптимистичный
              // 0 на открытом чате (snapshot может прийти до того как viewMessages
              // отразится в TDLib cache).
              const nextChat =
                isViewingChat(state, payload.dialog.chatId)
                  ? { ...mergedChat, unreadCount: 0 }
                  : mergedChat;
              const nextChatsRecord = {
                ...state.chats,
                [payload.dialog.chatId]: nextChat
              };

              const incomingDraft =
                typeof payload.dialog.draftText === "string" ? payload.dialog.draftText : "";
              const currentDraft = state.draftsByChat[payload.dialog.chatId] ?? "";
              // Если мы только что локально очистили draft (send/clear), TDLib
              // может ещё раз прислать stale draftText — игнорируем непустой
              // incoming в этом окне, иначе текст «воскресает» в поле ввода.
              const echoBlocked =
                incomingDraft.length > 0 &&
                isDraftEchoBlocked(payload.dialog.chatId, incomingDraft);
              // Пустой draft от сервера = подтверждение нашей очистки: снимаем
              // защиту, иначе следующий НАСТОЯЩИЙ черновик (набранный на
              // телефоне) не доедет до этого чата ещё две минуты.
              if (incomingDraft.length === 0) {
                forgetDraftClearStamp(payload.dialog.chatId);
              }
              const nextDraftsByChat =
                incomingDraft !== currentDraft && !echoBlocked
                  ? (() => {
                      const next = { ...state.draftsByChat };
                      if (incomingDraft.length > 0) {
                        next[payload.dialog.chatId] = incomingDraft;
                      } else {
                        delete next[payload.dialog.chatId];
                      }
                      return next;
                    })()
                  : state.draftsByChat;

              return {
                chats: nextChatsRecord,
                chatIdsByList: mergeSingleChatIntoLists(
                  state.chatIdsByList,
                  payload.dialog,
                  nextChatsRecord
                ),
                draftsByChat: nextDraftsByChat,
                serverUnreadByList: adjustServerUnreadForChat(
                  state.serverUnreadByList,
                  state.chats[payload.dialog.chatId],
                  nextChat
                )
              };
            });
            {
              const after = get();
              const chatId = payload.dialog.chatId;
              if (
                after.historyLoadedByChat[chatId] &&
                !after.historyLoadingByChat[chatId] &&
                hasHistoryTailGap(after, chatId)
              ) {
                scheduleHistoryTailReload(get, chatId);
              }
            }
            return;
          }
          case "chat.presenceUpdated": {
            const payload = message.payload;
            set((state) => {
              // updateUserStatus — одно из самых частых обновлений TDLib.
              // Не создаём чат ради presence неизвестного диалога: тот же
              // guard, что в chat.dialogUpdated — иначе state.chats растёт
              // без предела и удорожает каждую сортировку/спред.
              if (!state.chats[payload.chatId]) {
                return state;
              }
              const chat = withChat(state, payload.chatId);
              return {
                chats: {
                  ...state.chats,
                  [payload.chatId]: {
                    ...chat,
                    ...(payload.label !== undefined ? { peerStatusLabel: payload.label } : {}),
                    // Момент приходит только для offline; в online его нет —
                    // явным undefined затираем прошлое значение, иначе рядом
                    // с «в сети» осталась бы старая дата.
                    peerLastSeenAt: payload.lastSeenAt,
                    isPeerOnline: payload.isOnline,
                    // Флаг «собеседник заблокировал»: undefined в payload
                    // означает «воркер не менял», оставляем прежнее значение.
                    ...(payload.isBlockedByUser !== undefined
                      ? { isBlockedByUser: payload.isBlockedByUser }
                      : {})
                  }
                }
              };
            });
            return;
          }
          case "chat.messageUpserted": {
            const payload = message.payload;
            // Reply-keyboard от бота применяется к чату глобально.
            // remove → снять, иначе — заменить текущую.
            if (payload.botReplyKeyboard) {
              const kb = payload.botReplyKeyboard;
              if ("remove" in kb) {
                set((state) => {
                  if (!state.botReplyKeyboardByChat[payload.chatId]) return state;
                  const next = { ...state.botReplyKeyboardByChat };
                  delete next[payload.chatId];
                  return { botReplyKeyboardByChat: next };
                });
              } else {
                set((state) => ({
                  botReplyKeyboardByChat: {
                    ...state.botReplyKeyboardByChat,
                    [payload.chatId]: kb
                  }
                }));
              }
            }
            // Bug 1/10: если пришло сообщение в чат, который ещё не был
            // загружен через listDialogs — у emptyChat listIds=[], и upsertMessage
            // добавит его в state.chats, но НЕ во chatIdsByList. Итог: сообщение
            // получено, а чат в сайдбаре не всплывает до ручного refresh.
            // Дёргаем resolveKnown, чтобы worker прислал dialogsResolved с
            // корректными listIds и sidebar подцепил чат.
            {
              const snapshot = get();
              const existingChat = snapshot.chats[payload.chatId];
              if (!existingChat || existingChat.listIds.length === 0) {
                snapshot.resolveKnownChats([payload.chatId]);
              }
            }
            set((state) => {
              const existed = (state.messagesByChat[payload.chatId] ?? []).some(
                (message) => message.id === payload.messageId
              );
              const nextState = upsertMessage(state, payload);
              const chat = withChat(state, payload.chatId);
              const shouldNotify =
                !payload.outgoing &&
                !existed &&
                // Чат без звука (mute, раунд 48) — браузерное уведомление
                // не показываем, как официальный клиент.
                !chat.isMuted &&
                state.notificationPermission === "granted" &&
                state.notificationsEnabled &&
                !isViewingChat(state, payload.chatId);

              if (shouldNotify) {
                const preview = contentToText(payload.content);
                const senderPrefix =
                  chat.isGroupChat && payload.senderName ? `${payload.senderName}: ` : "";
                const body = `${senderPrefix}${preview}`.trim();
                showIncomingMessageNotification({
                  chat,
                  body: body.length > 0 ? body : "Новое сообщение",
                  onClick: () => {
                    if (typeof window !== "undefined") {
                      window.focus();
                    }
                    get().selectChat(payload.chatId);
                  }
                });
              }

              return nextState;
            });
            {
              const translated = get();
              if (
                translated.translatedChats[payload.chatId] &&
                !payload.outgoing &&
                // Именно translationSourceText: расшифровка голосового
                // приезжает этим же событием (updateMessageContent), и с
                // ней сообщение становится переводимым.
                translationSourceText(payload.content).length > 0 &&
                !translated.translationsByMessage[translationKey(payload.chatId, payload.messageId)]
              ) {
                enqueueChatTranslation(get, payload.chatId, [payload.messageId], { front: true });
              } else if (
                translated.translatedChats[payload.chatId] &&
                payload.outgoing &&
                translationSourceText(payload.content).length > 0 &&
                !translated.translationsByMessage[translationKey(payload.chatId, payload.messageId)]
              ) {
                // Это сообщение мы сами перевели перед отправкой? Тогда под
                // ним показываем русский оригинал слово в слово — без запроса
                // к LLM (он вернул бы обратный перевод, а не то, что саппорт
                // на самом деле написал).
                const sent = translationSourceText(payload.content).trim();
                const original = outgoingOriginals.get(
                  outgoingOriginalKey(payload.chatId, sent)
                );
                if (original !== undefined) {
                  outgoingOriginals.delete(outgoingOriginalKey(payload.chatId, sent));
                  const lang = resolveChatTranslationLanguage(translated, payload.chatId);
                  set((current) => ({
                    translationsByMessage: boundMemoryTranslations(
                      {
                        ...current.translationsByMessage,
                        [translationKey(payload.chatId, payload.messageId)]: {
                          text: original,
                          targetLanguage: lang
                        }
                      },
                      current.selectedChatId
                    )
                  }));
                  return;
                }
                // Исходящее в очередь не ставим напрямую: у только что
                // отправленного сообщения id ещё временный и сменится на
                // финальный (messageAccepted → messageUpserted), перевод
                // осел бы на мёртвом ключе. Сверка через паузу увидит уже
                // финальное сообщение в ленте и доберёт его — так же, как
                // это происходит при открытии чата.
                scheduleTranslationSweep(get, payload.chatId);
              }
            }
            return;
          }
          case "chat.messageDeleted": {
            const payload = message.payload;
            set((state) => {
              const existingMessages = state.messagesByChat[payload.chatId] ?? [];
              const existingMessage = existingMessages.find(
                (message) => message.id === payload.messageId
              );
              if (!existingMessage && !payload.snapshot) {
                return state;
              }
              if (existingMessage?.isDeleted) {
                return state;
              }
              const tombstoneSource = payload.snapshot
                ? toMessageViewModel(payload.snapshot)
                : existingMessage;
              const tombstoneMessage = tombstoneSource
                ? {
                    ...tombstoneSource,
                    id: payload.messageId,
                    isDeleted: true,
                    deletedAt: payload.occurredAt
                  }
                : null;
              const nextMessages = tombstoneMessage
                ? upsertSortedMessageList(existingMessages, tombstoneMessage).messages
                : existingMessages.filter((message) => message.id !== payload.messageId);
              const chat = withChat(state, payload.chatId);
              const latestMessage = nextMessages.at(-1);
              let nextSearchState = state.searchStateByChat;
              for (const searchKey of searchStateKeysForChat(
                state.searchStateByChat,
                payload.chatId
              )) {
                nextSearchState = {
                  ...nextSearchState,
                  [searchKey]: {
                    ...state.searchStateByChat[searchKey]!,
                    results: state.searchStateByChat[searchKey]!.results.map((message) =>
                      message.id === payload.messageId && tombstoneMessage
                        ? {
                            ...message,
                            isDeleted: true,
                            deletedAt: payload.occurredAt
                          }
                        : message
                    )
                  }
                };
              }
              return {
                chats: {
                  ...state.chats,
                  [payload.chatId]: {
                    ...chat,
                    lastMessageText: messagePreviewText(latestMessage),
                    ...(latestMessage?.senderName
                      ? { lastMessageSenderName: latestMessage.senderName }
                      : {}),
                    ...lastMessageMeta(latestMessage),
                    lastActivityAt: latestMessage?.occurredAt ?? chat.lastActivityAt
                  }
                },
                messagesByChat: {
                  ...state.messagesByChat,
                  [payload.chatId]: nextMessages
                },
                searchStateByChat: nextSearchState
              };
            });
            return;
          }
          case "chat.historyLoaded": {
            const payload = message.payload;
            cancelHistoryLoadingWatchdog(payload.chatId);
            set((state) => {
              const historyMessages = payload.messages.map((entry) => toMessageViewModel(entry));
              const previousChat = withChat(state, payload.chatId);
              const previousMessages = state.messagesByChat[payload.chatId] ?? [];
              const { typingActorId: _typingActorId, ...chatWithoutTyping } = previousChat;
              const mergedMessages =
                previousMessages.length > 0
                  ? mergeSortedUniqueMessages(historyMessages, previousMessages)
                  : historyMessages;
              const lastReadOutboxAt =
                readTimestampForMessage(mergedMessages, payload.lastReadOutboxMessageId) ??
                previousChat.lastReadOutboxAt;
              const latestMessage = mergedMessages.at(-1);
              const isOlderPage = Boolean(payload.cursor && previousMessages.length > 0);

              const updatedChat: ChatViewModel = {
                ...chatWithoutTyping,
                title: payload.chatTitle ?? previousChat.title,
                isGroupChat: payload.isGroupChat ?? previousChat.isGroupChat,
                unreadCount:
                  isViewingChat(state, payload.chatId) ? 0 : previousChat.unreadCount,
                isTyping: false,
                lastMessageText:
                  isOlderPage
                    ? previousChat.lastMessageText
                    : latestMessage
                      ? latestMessage.text
                    : previousChat.lastMessageText,
                lastActivityAt:
                  isOlderPage
                    ? previousChat.lastActivityAt
                    : latestMessage?.occurredAt ??
                      previousChat.lastActivityAt ??
                      payload.occurredAt,
                ...(payload.lastReadInboxMessageId || previousChat.lastReadInboxMessageId
                  ? {
                      lastReadInboxMessageId:
                        payload.lastReadInboxMessageId ??
                        previousChat.lastReadInboxMessageId!
                    }
                  : {}),
                ...(payload.lastReadOutboxMessageId || previousChat.lastReadOutboxMessageId
                  ? {
                      lastReadOutboxMessageId:
                        payload.lastReadOutboxMessageId ??
                        previousChat.lastReadOutboxMessageId!
                    }
                  : {}),
                ...(lastReadOutboxAt ? { lastReadOutboxAt } : {})
              };
              // Метаданные last_message: на свежем page переписываем,
              // на older page оставляем как было.
              if (!isOlderPage) {
                if (latestMessage?.outgoing) {
                  updatedChat.lastMessageOutgoing = true;
                  updatedChat.lastMessageId = latestMessage.id;
                  updatedChat.lastMessageOccurredAt = latestMessage.occurredAt;
                } else if (latestMessage) {
                  delete updatedChat.lastMessageOutgoing;
                  delete updatedChat.lastMessageId;
                  delete updatedChat.lastMessageOccurredAt;
                }
                // Тип медиа нужен превью и для входящих (иконка в строке диалога).
                if (latestMessage) {
                  updatedChat.lastMessageKind = latestMessage.content.kind;
                }
              }
              // Восстановление bot reply keyboard при открытии чата: воркер
              // кладёт актуальное состояние (по chat.reply_markup_message_id)
              // прямо в historyLoaded. Отсутствие поля = неизвестно, не трогаем.
              let nextBotKeyboards = state.botReplyKeyboardByChat;
              if (payload.botReplyKeyboard) {
                if ("remove" in payload.botReplyKeyboard) {
                  if (state.botReplyKeyboardByChat[payload.chatId]) {
                    nextBotKeyboards = { ...state.botReplyKeyboardByChat };
                    delete nextBotKeyboards[payload.chatId];
                  }
                } else {
                  nextBotKeyboards = {
                    ...state.botReplyKeyboardByChat,
                    [payload.chatId]: payload.botReplyKeyboard
                  };
                }
              }
              return {
                chats: {
                  ...state.chats,
[payload.chatId]: updatedChat
                },
                botReplyKeyboardByChat: nextBotKeyboards,
                messagesByChat: {
                  ...state.messagesByChat,
                  [payload.chatId]: mergedMessages
                },
                historyLoadingByChat: {
                  ...state.historyLoadingByChat,
                  [payload.chatId]: false
                },
                historyLoadedByChat: {
                  ...state.historyLoadedByChat,
                  [payload.chatId]: true
                },
                historyReachedStartByChat: {
                  ...state.historyReachedStartByChat,
                  [payload.chatId]: !payload.hasMore
                }
              };
            });
            {
              const translated = get();
              if (translated.translatedChats[payload.chatId]) {
                // reverse: догруженную историю тоже переводим снизу вверх —
                // ближайшие к видимой области сообщения раньше самых старых.
                const pending = (translated.messagesByChat[payload.chatId] ?? [])
                  .filter(
                    (entry) =>
                      isTranslatableMessage(entry) &&
                      !translated.translationsByMessage[translationKey(payload.chatId, entry.id)]
                  )
                  .map((entry) => entry.id)
                  .reverse();
                enqueueChatTranslation(get, payload.chatId, pending);
              }
            }
            return;
          }
          case "chat.searchResultsLoaded": {
            const payload = message.payload;
            const stateKey = payload.requestKey ?? payload.chatId;
            cancelSearchLoadingWatchdog(stateKey);
            set((state) => {
              const existing = state.searchStateByChat[stateKey];
              const incoming = payload.results.map((entry) => toMessageViewModel(entry));
              let results = incoming;
              if (payload.append && existing) {
                // Страница по курсору: если запрос успел смениться — это
                // ответ на устаревший вызов, игнорируем.
                if (existing.query !== payload.query || existing.scope !== payload.scope) {
                  return state;
                }
                const known = new Set(existing.results.map((entry) => entry.id));
                results = [
                  ...existing.results,
                  ...incoming.filter((entry) => !known.has(entry.id))
                ];
              }
              return {
                searchStateByChat: {
                  ...state.searchStateByChat,
                  [stateKey]: {
                    query: payload.query,
                    scope: payload.scope,
                    isLoading: false,
                    results,
                    ...(payload.cursor ? { cursor: payload.cursor } : {}),
                    hasMore: Boolean(payload.hasMore && payload.cursor)
                  }
                }
              };
            });
            return;
          }
          case "chat.readStateUpdated": {
            const payload = message.payload;
            set((state) => {
              // Guard как в chat.dialogUpdated: не создаём чат ради read-state
              // неизвестного диалога — state.chats не должен расти без предела.
              if (!state.chats[payload.chatId]) {
                return state;
              }
              const chat = withChat(state, payload.chatId);
              if (payload.scope === "inbox") {
                const nextChat: ChatViewModel = {
                  ...chat,
                  lastReadInboxMessageId: payload.lastReadMessageId,
                  // Открытый чат держим на 0 (оптимистично), остальным
                  // ставим счётчик TDLib, если воркер его прислал: иначе
                  // в другой вкладке/окне того же аккаунта бейдж
                  // оставался старым до следующего dialogUpdated.
                  unreadCount:
                    isViewingChat(state, payload.chatId)
                      ? 0
                      : typeof payload.unreadCount === "number" &&
                          Number.isFinite(payload.unreadCount)
                        ? Math.max(0, payload.unreadCount)
                        : chat.unreadCount
                };
                return {
                  chats: {
                    ...state.chats,
                    [payload.chatId]: nextChat
                  },
                  serverUnreadByList: adjustServerUnreadForChat(
                    state.serverUnreadByList,
                    chat,
                    nextChat
                  )
                };
              }

              return {
                chats: {
                  ...state.chats,
                  [payload.chatId]: {
                    ...chat,
                    lastReadOutboxMessageId: payload.lastReadMessageId,
                    lastReadOutboxAt: payload.occurredAt
                  }
                }
              };
            });
            return;
          }
          case "chat.typingUpdated": {
            const payload = message.payload;
            set((state) => {
              // Guard как в chat.dialogUpdated: typing по неизвестному чату
              // не должен создавать запись в state.chats.
              if (!state.chats[payload.chatId]) {
                return state;
              }
              const chat = withChat(state, payload.chatId);
              const { typingActorId: _typingActorId, ...chatWithoutTyping } = chat;
              return {
                chats: {
                  ...state.chats,
                  [payload.chatId]: {
                    ...chatWithoutTyping,
                    isTyping: payload.active && payload.actorId !== "self",
                    ...(payload.active && payload.actorId !== "self"
                      ? { typingActorId: payload.actorId }
                      : {})
                  }
                }
              };
            });
            return;
          }
          case "chat.pinnedMessageUpdated": {
            const payload = message.payload;
            set((state) => {
              const pinnedMessageByChat = { ...state.pinnedMessageByChat };
              if (payload.pinnedMessage) {
                pinnedMessageByChat[payload.chatId] = toMessageViewModel(payload.pinnedMessage);
              } else {
                delete pinnedMessageByChat[payload.chatId];
              }

              return {
                pinnedMessageByChat
              };
            });
            return;
          }
          case "session.stateChanged":
            return;
          case "account.identity": {
            const payload = message.payload;
            set(() => ({
              selfIdentity: {
                telegramUserId: payload.telegramUserId,
                ...(payload.username ? { username: payload.username } : {}),
                ...(payload.firstName ? { firstName: payload.firstName } : {}),
                ...(payload.lastName ? { lastName: payload.lastName } : {}),
                ...(payload.phoneNumber ? { phoneNumber: payload.phoneNumber } : {}),
                ...(payload.bio ? { bio: payload.bio } : {}),
                ...(payload.avatarUrl !== undefined ? { avatarUrl: payload.avatarUrl } : {})
              }
            }));
            return;
          }
          case "chat.messageAccepted": {
            const payload = message.payload;
            // Воркер принял отправку — ошибка gateway по этой команде уже
            // не должна снимать плейсхолдер.
            forgetPendingSendByLocalId(payload.localId);
            set((state) => {
              const list = state.messagesByChat[payload.chatId];
              if (!list) {
                return state;
              }
              const optimisticId = `optimistic:${payload.localId}`;
              let mutated = false;
              const next = list.map((entry) => {
                if (
                  entry.id !== optimisticId &&
                  entry.localId !== payload.localId
                ) {
                  return entry;
                }
                mutated = true;
                // Re-key the placeholder to TDLib's temporary id so the
                // follow-up messageUpserted (which carries
                // replacesMessageId === temporaryMessageId) reconciles it
                // into the final message instead of duplicating it.
                return { ...entry, id: payload.temporaryMessageId };
              });
              if (!mutated) {
                return state;
              }
              return {
                messagesByChat: {
                  ...state.messagesByChat,
                  [payload.chatId]: next
                }
              };
            });
            return;
          }
          case "chat.scheduledMessagesLoaded": {
            const payload = message.payload;
            cancelScheduledLoadingWatchdog(payload.chatId);
            set((state) => ({
              scheduledLoadingByChat: {
                ...state.scheduledLoadingByChat,
                [payload.chatId]: false
              },
              scheduledByChat: {
                ...state.scheduledByChat,
                [payload.chatId]: sortScheduledEntries(
                  payload.messages.map((entry) => ({
                    id: entry.messageId,
                    content: entry.content,
                    text: contentToText(entry.content),
                    outgoing: entry.outgoing,
                    ...(entry.senderName ? { senderName: entry.senderName } : {}),
                    ...(entry.sendAt ? { sendAt: entry.sendAt } : {}),
                    ...(entry.sendWhenOnline ? { sendWhenOnline: true } : {})
                  }))
                )
              }
            }));
            return;
          }
          case "chat.messageSendFailed": {
            const payload = message.payload;
            set((state) => {
              const list = state.messagesByChat[payload.chatId];
              if (!list) {
                return {
                  errors: pushError(
                    state.errors,
                    `Сообщение не отправлено${
                      payload.errorMessage ? `: ${payload.errorMessage}` : ""
                    }`
                  )
                };
              }
              // Плейсхолдер ищем по обоим ключам: до chat.messageAccepted он
              // ещё "optimistic:<localId>", после — уже temp-id.
              const next = list.filter(
                (entry) =>
                  entry.id !== payload.messageId &&
                  !(payload.localId !== undefined && entry.localId === payload.localId)
              );
              return {
                messagesByChat: {
                  ...state.messagesByChat,
                  [payload.chatId]: next
                },
                errors: pushError(
                  state.errors,
                  `Сообщение не отправлено${
                    payload.errorMessage ? `: ${payload.errorMessage}` : ""
                  }`
                )
              };
            });
            return;
          }
          case "chat.messageReactionsUpdated": {
            const payload = message.payload;
            set((state) => {
              const list = state.messagesByChat[payload.chatId];
              if (!list) {
                return state;
              }
              let mutated = false;
              const next = list.map((entry) => {
                if (entry.id !== payload.messageId) {
                  return entry;
                }
                mutated = true;
                if (payload.reactions.length === 0) {
                  const { reactions: _r, ...rest } = entry;
                  return rest;
                }
                return { ...entry, reactions: payload.reactions };
              });
              if (!mutated) {
                return state;
              }
              return {
                messagesByChat: {
                  ...state.messagesByChat,
                  [payload.chatId]: next
                }
              };
            });
            return;
          }
          case "chat.messageTranslated": {
            const payload = message.payload;
            // Защита от устаревших переводов: если пользователь успел
            // переключить язык чата, пока worker фолбэк-перевод отвечал,
            // сохранять старый результат нельзя. chatId есть прямо в событии —
            // раньше тут был линейный скан ВСЕХ сообщений всех чатов на
            // каждый перевод (O(n) × десятки событий при «Перевести весь чат»).
            if (payload.chatId) {
              const currentLang = resolveChatTranslationLanguage(get(), payload.chatId);
              if (currentLang !== payload.targetLanguage) {
                return;
              }
            }
            set((state) => ({
              translationsByMessage: boundMemoryTranslations(
                {
                  ...state.translationsByMessage,
                  [translationKey(payload.chatId, payload.messageId)]: {
                    text: payload.text,
                    ...(payload.entities ? { entities: payload.entities } : {}),
                    targetLanguage: payload.targetLanguage
                  }
                },
                state.selectedChatId
              )
            }));
            return;
          }
          case "account.quickRepliesLoaded": {
            set({ quickReplies: message.payload.shortcuts });
            return;
          }
          case "chat.listUnreadUpdated": {
            const payload = message.payload;
            set((state) => {
              const previous = state.serverUnreadByList[payload.listId];
              const chats = payload.unreadChatsCount ?? previous?.chats;
              const unmutedChats =
                payload.unreadUnmutedChatsCount ?? previous?.unmutedChats;
              if (
                previous &&
                previous.messages === payload.unreadCount &&
                previous.chats === chats &&
                previous.unmutedChats === unmutedChats
              ) {
                return state;
              }
              return {
                serverUnreadByList: {
                  ...state.serverUnreadByList,
                  [payload.listId]: {
                    messages: payload.unreadCount,
                    ...(chats !== undefined ? { chats } : {}),
                    ...(unmutedChats !== undefined ? { unmutedChats } : {})
                  }
                }
              };
            });
            return;
          }
          case "chat.replyKeyboardUpdated": {
            // Живое обновление bot reply keyboard (updateChatReplyMarkup):
            // в т.ч. «one-time клавиатура использована» → remove.
            const payload = message.payload;
            set((state) => {
              if ("remove" in payload.keyboard) {
                if (!state.botReplyKeyboardByChat[payload.chatId]) return state;
                const next = { ...state.botReplyKeyboardByChat };
                delete next[payload.chatId];
                return { botReplyKeyboardByChat: next };
              }
              return {
                botReplyKeyboardByChat: {
                  ...state.botReplyKeyboardByChat,
                  [payload.chatId]: payload.keyboard
                }
              };
            });
            return;
          }
          case "chat.botCallbackAnswer": {
            const payload = message.payload;
            const text = payload.text ?? (payload.url ? `Открыть: ${payload.url}` : "Готово");
            set((state) => ({
              botCallbackToasts: [
                ...state.botCallbackToasts,
                {
                  id: `${payload.chatId}:${payload.messageId}:${payload.occurredAt}`,
                  chatId: payload.chatId,
                  messageId: payload.messageId,
                  text,
                  ...(payload.url ? { url: payload.url } : {}),
                  showAlert: payload.showAlert,
                  occurredAt: payload.occurredAt
                }
              ]
            }));
            return;
          }
          case "chat.callRinging": {
            const payload = message.payload;
            set((state) => {
              if (!state.pendingCall || state.pendingCall.chatId !== payload.chatId) {
                return {};
              }
              if (state.pendingCall.ringing) {
                return {};
              }
              return {
                pendingCall: { ...state.pendingCall, ringing: true }
              };
            });
            return;
          }
          case "chat.callPlaced": {
            const payload = message.payload;
            set((state) => {
              // Привязываемся к pendingCall по chatId. Если за время звонка
              // юзер успел запустить новый вызов в другой чат, событие
              // от старого не должно перезаписывать новую модалку.
              if (!state.pendingCall || state.pendingCall.chatId !== payload.chatId) {
                return {};
              }
              return {
                pendingCall: {
                  ...state.pendingCall,
                  outcome: payload.outcome,
                  ...(payload.failureMessage
                    ? { failureMessage: payload.failureMessage }
                    : {}),
                  finishedAt: payload.occurredAt
                }
              };
            });
            return;
          }
          case "chatFolder.chatsPicked": {
            const payload = message.payload;
            set((state) => {
              // События идут broadcast'ом на все вкладки аккаунта — чужой
              // подбор игнорируем, иначе соседняя вкладка подменит результат
              // в открытой здесь модалке.
              if (!state.folderPick || state.folderPick.requestId !== payload.requestId) {
                return {};
              }
              return {
                folderPick: {
                  ...state.folderPick,
                  status: "done",
                  chatIds: payload.chatIds,
                  scannedCount: payload.scannedCount,
                  skippedInOtherFoldersCount: payload.skippedInOtherFoldersCount,
                  exhausted: payload.exhausted
                }
              };
            });
            return;
          }
          case "push.jobUpdated": {
            const payload = message.payload;
            if (payload.accountId !== get().accountId) {
              return;
            }
            get().upsertPushJob(payload.job);
            return;
          }
          case "push.jobFinished": {
            // Личное уведомление: gateway шлёт его только на подключения
            // того, кто ставил папку на пуш, — поэтому без фильтра по
            // аккаунту (вкладка могла уже переключиться на другой).
            const payload = message.payload;
            get().upsertPushJob(payload.job);
            const job = payload.job;
            const kind = job.mode === "scripts" ? "Пуш скриптами" : "ИИ-пуш";
            const headline =
              job.status === "done"
                ? `✅ ${kind} папки «${job.listTitle}» завершён`
                : job.status === "cancelled"
                  ? `⛔ ${kind} папки «${job.listTitle}» отменён`
                  : `⚠️ ${kind} папки «${job.listTitle}» остановлен с ошибкой`;
            const details = [
              `отправлено ${job.sentCount}, пропущено ${job.skippedCount}, ошибок ${job.errorCount}`,
              ...(job.errorMessage && job.status !== "done" ? [job.errorMessage] : []),
              ...(payload.folderDeleted ? ["папка удалена"] : [])
            ].join(" · ");
            set((state) => ({ errors: pushError(state.errors, `${headline}: ${details}`) }));
            if (get().notificationsEnabled) {
              showSystemNotification(headline, details, `push-finished:${job.jobId}`);
            }
            return;
          }
          case "chat.historyExported": {
            const payload = message.payload;
            const current = get().chatExport;
            // Broadcast на все вкладки аккаунта — чужой экспорт игнорируем.
            if (!current || current.requestId !== payload.requestId || current.status !== "pending") {
              return;
            }
            // Страницы идут от новых к старым, внутри — хронологически.
            const lines = [...payload.lines, ...current.lines].slice(-current.limit);
            const finished = payload.done || !payload.cursor || lines.length >= current.limit;
            set({
              chatExport: { ...current, lines, status: finished ? "done" : "pending" }
            });
            if (!finished) {
              sendChatExportPage(get, current.requestId, payload.cursor);
            }
            return;
          }
          case "chat.globalMessagesSearched": {
            const payload = message.payload;
            const current = get().messageSearch;
            if (current.requestId !== payload.requestId) {
              return;
            }
            set((state) => ({
              ...mergeSearchedDialogsIntoState(state, payload.dialogs),
              messageSearch: {
                query: current.query,
                requestId: current.requestId,
                hits: payload.append ? [...current.hits, ...payload.hits] : payload.hits,
                cursor: payload.cursor ?? null,
                hasMore: payload.hasMore,
                loading: false,
                error: payload.error ?? null
              }
            }));
            return;
          }
          case "chat.dialogsSearched": {
            const payload = message.payload;
            const current = get().dialogSearch;
            // Устаревший ответ (юзер уже дописал запрос) — игнорируем целиком,
            // иначе результаты старого запроса перекроют новые.
            if (current.requestId !== payload.requestId) {
              return;
            }
            set((state) => {
              return {
                ...mergeSearchedDialogsIntoState(state, payload.dialogs),
                dialogSearch: {
                  query: current.query,
                  requestId: current.requestId,
                  chatIds: payload.dialogs.map((dialog) => dialog.chatId),
                  loading: false
                }
              };
            });
            return;
          }
          case "chat.dialogsResolved": {
            const payload = message.payload;
            if (payload.dialogs.length === 0) {
              return;
            }
            set((state) => {
              const nextChats = { ...state.chats };
              for (const dialog of payload.dialogs) {
                nextChats[dialog.chatId] = mergeDialogSummary(
                  state.chats[dialog.chatId],
                  dialog
                );
              }
              // Bug 1/10: чат, только что появившийся через resolveKnown,
              // должен попасть в chatIdsByList своей папки, иначе sidebar
              // не покажет его до ручного refresh. mergeSingleChatIntoLists
              // корректно переставляет чат из старых листов в новые.
              let nextChatIdsByList = state.chatIdsByList;
              for (const dialog of payload.dialogs) {
                nextChatIdsByList = mergeSingleChatIntoLists(
                  nextChatIdsByList,
                  dialog,
                  nextChats
                );
              }
              // После добавления новых чатов пересчитываем chatIdsByList
              // для local:* папок — теперь sortChatIds сможет найти эти
              // чаты в кэше и поставить их на правильные места.
              const reMerged = mergeLocalFoldersIntoLists(
                { ...state, chats: nextChats, chatIdsByList: nextChatIdsByList },
                state.localFolders
              );
              return {
                chats: nextChats,
                chatIdsByList: reMerged.chatIdsByList ?? nextChatIdsByList
              };
            });
            return;
          }
          case "stickers.setsLoaded": {
            const payload = message.payload;
            set({
              stickerSetsLoading: false,
              stickerSets: payload.sets.map((set) => ({
                id: set.id,
                title: set.title,
                kind: set.kind,
                stickers: set.stickers.map((sticker) => ({
                  fileId: sticker.fileId,
                  ...(sticker.emoji ? { emoji: sticker.emoji } : {}),
                  ...(sticker.thumbUrl ? { thumbUrl: sticker.thumbUrl } : {}),
                  ...(typeof sticker.width === "number" ? { width: sticker.width } : {}),
                  ...(typeof sticker.height === "number" ? { height: sticker.height } : {})
                }))
              }))
            });
            return;
          }
          case "chat.forumTopicsLoaded": {
            const payload = message.payload;
            set((state) => ({
              forumTopicsByChat: {
                ...state.forumTopicsByChat,
                [payload.chatId]: payload.topics.map((topic) => ({
                  messageThreadId: topic.messageThreadId,
                  name: topic.name,
                  ...(topic.iconEmoji ? { iconEmoji: topic.iconEmoji } : {}),
                  unreadCount: topic.unreadCount,
                  ...(topic.isClosed ? { isClosed: topic.isClosed } : {}),
                  ...(topic.isPinned ? { isPinned: topic.isPinned } : {})
                }))
              }
            }));
            return;
          }
          case "chat.profileLoaded": {
            const payload = message.payload;
            set((state) => ({
              profilesByChat: {
                ...state.profilesByChat,
                [payload.chatId]: {
                  ...(payload.telegramUserId !== undefined ? { telegramUserId: payload.telegramUserId } : {}),
                  ...(payload.username ? { username: payload.username } : {}),
                  ...(payload.phoneNumber ? { phoneNumber: payload.phoneNumber } : {}),
                  ...(payload.phoneNumberCountry
                    ? { phoneNumberCountry: payload.phoneNumberCountry }
                    : {}),
                  ...(payload.registrationMonth
                    ? { registrationMonth: payload.registrationMonth }
                    : {}),
                  ...(payload.bio ? { bio: payload.bio } : {}),
                  ...(payload.description ? { description: payload.description } : {}),
                  ...(payload.inviteLink ? { inviteLink: payload.inviteLink } : {}),
                  ...(payload.autoDeleteTime !== undefined
                    ? { autoDeleteTime: payload.autoDeleteTime }
                    : {})
                }
              }
            }));
            return;
          }
          case "system.error": {
            const payload = message.payload;
            set((state) => ({
              errors: pushRealtimeError(state.errors, payload.code, payload.message)
            }));
            return;
          }
        }
    }
  }
}), {
  name: PERSIST_STORAGE_KEY,
  version: 3,
  migrate: (persistedState, fromVersion) => {
    if (fromVersion < 3 && persistedState && typeof persistedState === "object") {
      const snapshot = persistedState as Record<string, unknown>;
      const next: Partial<RealtimeState> = {};
      if (typeof snapshot.accountId === "string") {
        next.accountId = snapshot.accountId;
      }
      if (typeof snapshot.activeDialogListId === "string") {
        next.activeDialogListId = snapshot.activeDialogListId;
      }
      if (snapshot.draftsByChat && typeof snapshot.draftsByChat === "object") {
        next.draftsByChat = snapshot.draftsByChat as Record<string, string>;
      }
      return next as Partial<RealtimeState>;
    }
    return persistedState as Partial<RealtimeState>;
  },
  storage: createThrottledIDBStorage(),
  partialize: (state) => buildPersistedSnapshot(state) as unknown as Partial<RealtimeState>,
  merge: (persistedState, currentState) => {
    if (!persistedState || typeof persistedState !== "object") {
      return currentState;
    }
    const persisted = persistedState as Partial<RealtimeState>;
    const merged: RealtimeState = { ...currentState };

    // Гидрация IDB асинхронная: connect() мог уже выставить живой accountId —
    // не затираем его персистнутым (иначе команды уйдут со stale accountId).
    if (persisted.accountId !== undefined && !currentState.accountId) {
      merged.accountId = persisted.accountId;
    }
    if (persisted.activeDialogListId !== undefined) {
      merged.activeDialogListId = persisted.activeDialogListId;
    }
    if (persisted.draftsByChat) {
      merged.draftsByChat = {
        ...persisted.draftsByChat,
        ...currentState.draftsByChat
      };
    }
    if (persisted.translatedChats) {
      merged.translatedChats = { ...persisted.translatedChats };
    }
    if (persisted.translationLanguageByChat) {
      merged.translationLanguageByChat = { ...persisted.translationLanguageByChat };
    }
    if (persisted.translationsByMessage) {
      // Восстанавливаем кэш переводов: при следующем chat.historyLoaded дедуп
      // (translationsByMessage[id]) отсеет уже переведённые сообщения и
      // ретрансляции всей истории не будет.
      merged.translationsByMessage = { ...persisted.translationsByMessage };
    }
    return merged;
  }
}));

persistAccountIdReader = () => useRealtimeStore.getState().accountId;

// Пересчёт unreadTotals: один O(n)-проход по chats, троттл 300 мс, только
// когда сменилась identity state.chats. Держим вне компонентов, чтобы бейджи
// (вкладка + табы папок) не заставляли React-селекторы обходить 10k чатов на
// каждое WS-событие. Запаздывание бейджа на ≤300 мс незаметно.
// Оптимистичная поправка серверных счётчиков папок при переходе чата
// непрочитан → прочитан. TDLib присылает updateUnreadChatCount по папке не
// всегда (только по спискам, которые он сам «открыл»), и бейдж папки
// зависал с «1» после ответа саппорта. Сервер потом перезапишет своим
// значением, если пришлёт.
const isUnreadChatModel = (chat: ChatViewModel | undefined): boolean =>
  Boolean(chat && (chat.unreadCount > 0 || chat.isMarkedAsUnread === true));

const adjustServerUnreadForChat = (
  serverUnreadByList: RealtimeState["serverUnreadByList"],
  previous: ChatViewModel | undefined,
  next: ChatViewModel
): RealtimeState["serverUnreadByList"] => {
  const wasUnread = isUnreadChatModel(previous);
  const isUnread = isUnreadChatModel(next);
  if (wasUnread === isUnread) {
    return serverUnreadByList;
  }
  const delta = isUnread ? 1 : -1;
  let result = serverUnreadByList;
  for (const listId of next.listIds) {
    const entry = result[listId];
    if (!entry || (entry.chats === undefined && entry.unmutedChats === undefined)) {
      continue;
    }
    result = {
      ...result,
      [listId]: {
        ...entry,
        ...(entry.chats !== undefined ? { chats: Math.max(0, entry.chats + delta) } : {}),
        ...(entry.unmutedChats !== undefined
          ? { unmutedChats: Math.max(0, entry.unmutedChats + delta) }
          : {})
      }
    };
  }
  return result;
};

const computeUnreadTotals = (
  chats: Record<string, ChatViewModel>,
  localFolders: RealtimeState["localFolders"]
): RealtimeState["unreadTotals"] => {
  let total = 0;
  const byList: Record<string, number> = {};
  const chatsByList: Record<string, number> = {};
  const unmutedChatsByList: Record<string, number> = {};
  for (const chat of Object.values(chats)) {
    const isUnreadChat = chat.unreadCount > 0 || chat.isMarkedAsUnread === true;
    if (isUnreadChat) {
      for (const listId of chat.listIds) {
        chatsByList[listId] = (chatsByList[listId] ?? 0) + 1;
        if (!chat.isMuted) {
          unmutedChatsByList[listId] = (unmutedChatsByList[listId] ?? 0) + 1;
        }
      }
    }
    if (chat.unreadCount <= 0) {
      continue;
    }
    // Чисто архивные чаты не попадают в бейдж вкладки.
    if (!(chat.listIds.includes("archive") && chat.listIds.length === 1)) {
      total += chat.unreadCount;
    }
    for (const listId of chat.listIds) {
      byList[listId] = (byList[listId] ?? 0) + chat.unreadCount;
    }
  }

  // Локальные папки считаем отдельным проходом по их составу. Через
  // chat.listIds это не работает и работать не может: listIds приходят от
  // воркера (dialog.lists), а про наши папки воркер не знает — они живут
  // только в БД gateway. Оптимистичная правка membership вписывала local:*
  // в listIds, но ближайший dialogUpdated её затирал, поэтому бейджи наших
  // папок были пустыми ВСЕГДА, а не «иногда неточными».
  for (const folder of localFolders) {
    const listId = `local:${folder.folderId}`;
    let unreadChats = 0;
    let unreadUnmutedChats = 0;
    let unreadMessages = 0;
    for (const chatId of folder.includedChatIds) {
      const chat = chats[chatId];
      if (!chat) {
        continue;
      }
      if (chat.unreadCount > 0 || chat.isMarkedAsUnread === true) {
        unreadChats += 1;
        if (!chat.isMuted) {
          unreadUnmutedChats += 1;
        }
      }
      if (chat.unreadCount > 0) {
        unreadMessages += chat.unreadCount;
      }
    }
    // Сумму по вкладке (total) НЕ трогаем: те же чаты уже посчитаны по своим
    // спискам Telegram, иначе бейдж вкладки задвоился бы.
    chatsByList[listId] = unreadChats;
    unmutedChatsByList[listId] = unreadUnmutedChats;
    byList[listId] = unreadMessages;
  }
  return { total, byList, chatsByList, unmutedChatsByList };
};

const unreadTotalsEqual = (
  left: RealtimeState["unreadTotals"],
  right: RealtimeState["unreadTotals"]
): boolean => {
  if (left.total !== right.total) {
    return false;
  }
  const recordsEqual = (
    a: Record<string, number>,
    b: Record<string, number>
  ): boolean => {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
  };
  return (
    recordsEqual(left.byList, right.byList) &&
    recordsEqual(left.chatsByList, right.chatsByList) &&
    recordsEqual(left.unmutedChatsByList, right.unmutedChatsByList)
  );
};

if (typeof window !== "undefined") {
  let unreadRecalcTimer: number | null = null;
  let lastCountedChats: RealtimeState["chats"] | null = null;
  // Состав локальных папок — второй вход пересчёта: добор чатов в папку
  // меняет её бейдж, а state.chats при этом не трогается.
  let lastCountedLocalFolders: RealtimeState["localFolders"] | null = null;
  useRealtimeStore.subscribe((state) => {
    if (
      (state.chats === lastCountedChats &&
        state.localFolders === lastCountedLocalFolders) ||
      unreadRecalcTimer !== null
    ) {
      return;
    }
    unreadRecalcTimer = window.setTimeout(() => {
      unreadRecalcTimer = null;
      const current = useRealtimeStore.getState();
      lastCountedChats = current.chats;
      lastCountedLocalFolders = current.localFolders;
      const next = computeUnreadTotals(current.chats, current.localFolders);
      if (!unreadTotalsEqual(current.unreadTotals, next)) {
        useRealtimeStore.setState({ unreadTotals: next });
      }
    }, 300);
  });

  // In-memory лимит кэша переводов: PERSISTED_TRANSLATIONS_LIMIT ограничивал
  // только персист, в памяти карта росла без предела. Гистерезис +500, чтобы
  // не пересобирать объект на каждом переводе.
  const TRANSLATIONS_MEMORY_LIMIT = PERSISTED_TRANSLATIONS_LIMIT + 500;
  let lastTrimmedTranslations: RealtimeState["translationsByMessage"] | null = null;
  useRealtimeStore.subscribe((state) => {
    if (state.translationsByMessage === lastTrimmedTranslations) {
      return;
    }
    lastTrimmedTranslations = state.translationsByMessage;
    if (Object.keys(state.translationsByMessage).length <= TRANSLATIONS_MEMORY_LIMIT) {
      return;
    }
    useRealtimeStore.setState({
      translationsByMessage: boundTranslations(state.translationsByMessage)
    });
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  const syncPageActive = (): void => {
    useRealtimeStore.getState().setPageActive(readPageActive());
  };
  window.addEventListener("focus", syncPageActive);
  // На blur hasFocus() в части браузеров ещё true — ставим false явно.
  window.addEventListener("blur", () => useRealtimeStore.getState().setPageActive(false));
  document.addEventListener("visibilitychange", syncPageActive);
}
